import { Application, Container, Graphics, Text } from 'pixi.js';
import {
  computeSailing,
  updateSpeed,
  moveBoat,
  angleToDir,
  dirToAngle,
  normalize,
  dot,
  type Vec2,
} from './physics.js';
import { generateIslands, buildWorldContainer, checkCollision, findNearestIsland, isInsideIsland, findLandingPosition, generateTreasures, drawTreasureChest, makeBuiltIsland, drawSingleIsland, treeRngForBuiltIsland, WORLD_W, WORLD_H, type Island } from './world.js';

// ---- Gate bypass allowlist (shared with starting-chest bonus) ----
const SPECIAL_NAMES = new Set(['16', '666', 'gurkan', 'tomaten']);
function isSpecialName(name: string): boolean {
  return SPECIAL_NAMES.has(name.trim().toLowerCase());
}

function segmentsIntersect(p1: Vec2, p2: Vec2, p3: Vec2, p4: Vec2): boolean {
  const d = (p2.x - p1.x) * (p4.y - p3.y) - (p2.y - p1.y) * (p4.x - p3.x);
  if (Math.abs(d) < 1e-9) return false;
  const t = ((p3.x - p1.x) * (p4.y - p3.y) - (p3.y - p1.y) * (p4.x - p3.x)) / d;
  const u = ((p3.x - p1.x) * (p2.y - p1.y) - (p3.y - p1.y) * (p2.x - p1.x)) / d;
  return t >= 0 && t <= 1 && u >= 0 && u <= 1;
}

function pointToSegmentDist(p: Vec2, a: Vec2, b: Vec2): number {
  const abx = b.x - a.x, aby = b.y - a.y;
  const len2 = abx * abx + aby * aby;
  if (len2 < 1e-9) {
    const dx = p.x - a.x, dy = p.y - a.y;
    return Math.sqrt(dx * dx + dy * dy);
  }
  let t = ((p.x - a.x) * abx + (p.y - a.y) * aby) / len2;
  t = Math.max(0, Math.min(1, t));
  const qx = a.x + t * abx, qy = a.y + t * aby;
  const dx = p.x - qx, dy = p.y - qy;
  return Math.sqrt(dx * dx + dy * dy);
}

function drawFence(gfx: Graphics, x1: number, y1: number, x2: number, y2: number) {
  const dx = x2 - x1, dy = y2 - y1;
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len < 1) return;
  gfx.moveTo(x1, y1);
  gfx.lineTo(x2, y2);
  gfx.stroke({ color: 0x000000, width: 2 });
  const ux = dx / len, uy = dy / len;
  const px = -uy, py = ux;
  const tickLen = 6;
  const spacing = 12;
  const count = Math.max(1, Math.floor(len / spacing));
  for (let i = 1; i < count; i++) {
    const t = i / count;
    const cxf = x1 + dx * t;
    const cyf = y1 + dy * t;
    gfx.moveTo(cxf - px * tickLen, cyf - py * tickLen);
    gfx.lineTo(cxf + px * tickLen, cyf + py * tickLen);
  }
  gfx.stroke({ color: 0x000000, width: 2 });
}

function drawBridge(gfx: Graphics, x1: number, y1: number, x2: number, y2: number) {
  const dx = x2 - x1, dy = y2 - y1;
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len < 1) return;
  const ux = dx / len, uy = dy / len;
  const px = -uy, py = ux;
  const W = 8; // half-width of plank surface
  // Plank deck fill (wood color)
  gfx.moveTo(x1 + px * W, y1 + py * W);
  gfx.lineTo(x2 + px * W, y2 + py * W);
  gfx.lineTo(x2 - px * W, y2 - py * W);
  gfx.lineTo(x1 - px * W, y1 - py * W);
  gfx.closePath();
  gfx.fill({ color: 0xb37a3f });
  // Rails
  gfx.moveTo(x1 + px * W, y1 + py * W);
  gfx.lineTo(x2 + px * W, y2 + py * W);
  gfx.moveTo(x1 - px * W, y1 - py * W);
  gfx.lineTo(x2 - px * W, y2 - py * W);
  gfx.stroke({ color: 0x6a4520, width: 2 });
  // Cross planks
  const spacing = 10;
  const count = Math.max(1, Math.floor(len / spacing));
  for (let i = 0; i <= count; i++) {
    const t = i / count;
    const cxf = x1 + dx * t;
    const cyf = y1 + dy * t;
    gfx.moveTo(cxf - px * W, cyf - py * W);
    gfx.lineTo(cxf + px * W, cyf + py * W);
  }
  gfx.stroke({ color: 0x6a4520, width: 1.2 });
}
import type { Park, Season, DroppedItem } from './net.js';
import { generateAnimals, updateAnimals, drawAnimals, findAnimalNear, findIslandForPos, drawAnimalOnBoat } from './animals.js';
import { generateVillagers, updateVillagers, drawVillagers } from './villagers.js';
import { drawPet, type PetType, type CatColor, PET_NAMES, PET_CODES } from './pets.js';
import { Boat, type BoatShape } from './boat.js';
import { drawPerson } from './person.js';
import { Joystick, drawJoysticks } from './joystick.js';
import { Hud } from './hud.js';
import { Minimap } from './minimap.js';
import { connectToServer } from './net.js';
import { RemoteBoats } from './remote-boats.js';
import { DebugOverlay } from './debug-overlay.js';
import { addToInventory, removeFromInventory, INVENTORY_SLOTS, type InventoryItem } from './inventory.js';
import { loadProfile, saveProfile } from './profile.js';

// ---- Color selection lobby ----
const BOAT_COLORS: { name: string; hex: string; value: number }[] = [
  { name: 'Red',    hex: '#e04040', value: 0xe04040 },
  { name: 'Blue',   hex: '#4080e0', value: 0x4080e0 },
  { name: 'Green',  hex: '#40b060', value: 0x40b060 },
  { name: 'Yellow', hex: '#e0d040', value: 0xe0d040 },
  { name: 'Lime',   hex: '#8bc34a', value: 0x8bc34a },
  { name: 'Cyan',   hex: '#40c0c0', value: 0x40c0c0 },
  { name: 'Orange', hex: '#e08030', value: 0xe08030 },
  { name: 'White',  hex: '#e8e8e8', value: 0xe8e8e8 },
];

const HAIR_OPTIONS: { name: string; label: string; value: number }[] = [
  { name: 'Inget hår', label: 'Inget', value: 0 },
  { name: 'Kort hår',  label: 'Kort',  value: 1 },
  { name: 'Långt hår', label: 'Långt', value: 2 },
];

const HAIR_COLOR_OPTIONS: { name: string; hex: string; value: number }[] = [
  { name: 'Brun',  hex: '#4a3520', value: 0x4a3520 },
  { name: 'Svart', hex: '#1a1a1a', value: 0x1a1a1a },
  { name: 'Blond', hex: '#e0c060', value: 0xe0c060 },
  { name: 'Röd',   hex: '#b0501a', value: 0xb0501a },
  { name: 'Grå',   hex: '#a0a0a0', value: 0xa0a0a0 },
  { name: 'Vit',   hex: '#f0f0f0', value: 0xf0f0f0 },
];

const BOAT_SHAPE_OPTIONS: { name: string; shape: BoatShape; label: string }[] = [
  { name: 'Klassisk',  shape: 'classic', label: 'Klassisk' },
  { name: 'Bred',      shape: 'wide',    label: 'Bred' },
  { name: 'Smal',      shape: 'sleek',   label: 'Smal' },
  { name: 'Fiskebåt',  shape: 'fishing', label: 'Fiskebåt' },
];

const ALL_PET_TYPES: PetType[] = ['dog', 'turtle', 'guinea_pig', 'parrot', 'goldfish', 'hamster', 'snake', 'frog', 'mouse', 'shark', 'cat', 'panda', 'fox', 'squirrel', 'dragon', 'owl', 'rabbit', 'rat'];

/**
 * Render each pet's actual in-game sprite (same drawPet() used on the boat
 * and ashore) to a small transparent PNG, so the lobby picker shows the
 * hand-drawn animal instead of a generic emoji. Needs its own throwaway
 * renderer since the real `app` isn't created until after the lobby closes.
 */
async function generatePetThumbnails(): Promise<Record<PetType, string>> {
  const thumbApp = new Application();
  await thumbApp.init({ width: 32, height: 32, backgroundAlpha: 0 });
  const out = {} as Record<PetType, string>;
  for (const pt of ALL_PET_TYPES) {
    const g = new Graphics();
    drawPet(g, pt, 0, 0, pt === 'cat' ? 'gray' : undefined);
    out[pt] = await thumbApp.renderer.extract.base64({ target: g, resolution: 6, antialias: true });
    g.destroy();
  }
  thumbApp.destroy(true, { children: true, texture: true });
  return out;
}

function waitForColorSelection(petThumbnails: Record<PetType, string>): Promise<{ hull: number; shirt: number; hair: number; hairColor: number; boatShape: BoatShape; name: string; pet: PetType | null; pet2: PetType | null; catColor: CatColor }> {
  return new Promise((resolve) => {
    const grid = document.getElementById('color-grid')!;
    const shirtGrid = document.getElementById('shirt-grid')!;
    const nameInput = document.getElementById('name-input') as HTMLInputElement;
    const hairGrid = document.getElementById('hair-grid')!;
    const hairColorGrid = document.getElementById('hair-color-grid')!;
    const boatShapeGrid = document.getElementById('boat-shape-grid')!;
    const petGrid = document.getElementById('pet-grid')!;
    const petGrid2 = document.getElementById('pet-grid-2')!;
    const petGrid2Label = document.getElementById('pet-grid-2-label')!;
    const catColorRow = document.getElementById('cat-color-row')!;
    const petCodeInput = document.getElementById('pet-code-input') as HTMLInputElement;
    const petCodeBtn = document.getElementById('pet-code-btn')!;
    const startBtn = document.getElementById('start-btn')!;
    const saved = loadProfile();
    const defaultBoat = Math.floor(Math.random() * BOAT_COLORS.length);
    const defaultShirt = Math.floor(Math.random() * BOAT_COLORS.length);
    let selectedBoat = saved?.hullColor ?? BOAT_COLORS[defaultBoat].value;
    let selectedShirt = saved?.shirtColor ?? BOAT_COLORS[defaultShirt].value;
    let selectedHair = saved?.hairStyle ?? 1;
    let selectedHairColor = saved?.hairColor ?? HAIR_COLOR_OPTIONS[0].value;
    let selectedBoatShape: BoatShape = saved?.boatShape ?? 'classic';
    let selectedPet: PetType | null = null;
    let selectedPet2: PetType | null = null;
    let canHaveTwoPets = false;
    let selectedCatColor: CatColor = 'gray';
    if (nameInput && saved?.name) nameInput.value = saved.name;

    const CAT_COLORS: Array<{ key: CatColor; label: string; swatch: string }> = [
      { key: 'orange', label: 'Orange',   swatch: 'linear-gradient(#f09840,#e08030)' },
      { key: 'gray',   label: 'Grå',      swatch: 'linear-gradient(#d0c8b8,#c0b8a8)' },
      { key: 'black',  label: 'Svart',    swatch: 'linear-gradient(#2a2a2a,#111)' },
      { key: 'tuxedo', label: 'Fläckig',  swatch: 'radial-gradient(#909090 18%, #222 20%), radial-gradient(#909090 18%, #222 20%), #222' },
    ];

    function renderCatColorRow() {
      const show = unlockedPets.has('cat');
      catColorRow.style.display = show ? 'flex' : 'none';
      if (!show) return;
      catColorRow.innerHTML = '';
      for (const cc of CAT_COLORS) {
        const btn = document.createElement('button');
        btn.className = 'cat-color-btn' + (selectedCatColor === cc.key ? ' selected' : '');
        btn.title = cc.label;
        btn.style.background = cc.swatch;
        btn.addEventListener('click', () => {
          selectedCatColor = cc.key;
          renderCatColorRow();
        });
        catColorRow.appendChild(btn);
      }
    }
    const unlockedPets = new Set<PetType>(['dog', 'turtle', 'guinea_pig', 'parrot', 'goldfish', 'hamster', 'snake', 'frog', 'mouse', 'shark']);

    for (const c of BOAT_COLORS) {
      const btn = document.createElement('button');
      btn.className = 'color-btn';
      btn.style.background = c.hex;
      btn.title = c.name;
      btn.addEventListener('click', () => {
        grid.querySelectorAll('.color-btn').forEach(b => b.classList.remove('selected'));
        btn.classList.add('selected');
        selectedBoat = c.value;
      });
      grid.appendChild(btn);
    }
    const boatIdx = BOAT_COLORS.findIndex(c => c.value === selectedBoat);
    grid.children[boatIdx >= 0 ? boatIdx : defaultBoat].classList.add('selected');

    for (const c of BOAT_COLORS) {
      const btn = document.createElement('button');
      btn.className = 'color-btn';
      btn.style.background = c.hex;
      btn.title = c.name;
      btn.addEventListener('click', () => {
        shirtGrid.querySelectorAll('.color-btn').forEach(b => b.classList.remove('selected'));
        btn.classList.add('selected');
        selectedShirt = c.value;
      });
      shirtGrid.appendChild(btn);
    }
    const shirtIdx = BOAT_COLORS.findIndex(c => c.value === selectedShirt);
    shirtGrid.children[shirtIdx >= 0 ? shirtIdx : defaultShirt].classList.add('selected');

    for (const h of HAIR_OPTIONS) {
      const btn = document.createElement('button');
      btn.className = 'hair-btn';
      btn.textContent = h.label;
      btn.title = h.name;
      btn.addEventListener('click', () => {
        hairGrid.querySelectorAll('.hair-btn').forEach(b => b.classList.remove('selected'));
        btn.classList.add('selected');
        selectedHair = h.value;
      });
      hairGrid.appendChild(btn);
    }
    hairGrid.children[selectedHair].classList.add('selected');

    for (const hc of HAIR_COLOR_OPTIONS) {
      const btn = document.createElement('button');
      btn.className = 'color-btn';
      btn.style.background = hc.hex;
      btn.title = hc.name;
      btn.addEventListener('click', () => {
        hairColorGrid.querySelectorAll('.color-btn').forEach(b => b.classList.remove('selected'));
        btn.classList.add('selected');
        selectedHairColor = hc.value;
      });
      hairColorGrid.appendChild(btn);
    }
    const hairColorIdx = HAIR_COLOR_OPTIONS.findIndex(c => c.value === selectedHairColor);
    hairColorGrid.children[hairColorIdx >= 0 ? hairColorIdx : 0].classList.add('selected');

    for (const bs of BOAT_SHAPE_OPTIONS) {
      const btn = document.createElement('button');
      btn.className = 'hair-btn';
      btn.textContent = bs.label;
      btn.title = bs.name;
      btn.addEventListener('click', () => {
        boatShapeGrid.querySelectorAll('.hair-btn').forEach(b => b.classList.remove('selected'));
        btn.classList.add('selected');
        selectedBoatShape = bs.shape;
      });
      boatShapeGrid.appendChild(btn);
    }
    const boatShapeIdx = BOAT_SHAPE_OPTIONS.findIndex(bs => bs.shape === selectedBoatShape);
    boatShapeGrid.children[boatShapeIdx >= 0 ? boatShapeIdx : 0].classList.add('selected');

    const petBtnMap = new Map<PetType, HTMLButtonElement>();

    function buildPetGridInto(container: HTMLElement, getSelected: () => PetType | null, setSelected: (p: PetType | null) => void) {
      container.innerHTML = '';
      const current = getSelected();
      const noneBtn = document.createElement('button');
      noneBtn.className = 'pet-btn' + (current === null ? ' selected' : '');
      noneBtn.innerHTML = `<span class="pet-emoji">🚫</span><span class="pet-label">Inget</span>`;
      noneBtn.addEventListener('click', () => { setSelected(null); renderPetGrid(); });
      container.appendChild(noneBtn);
      for (const pt of ALL_PET_TYPES) {
        if (!unlockedPets.has(pt)) continue;
        const btn = document.createElement('button');
        btn.className = 'pet-btn' + (current === pt ? ' selected' : '');
        btn.innerHTML = `<img class="pet-thumb" src="${petThumbnails[pt]}" alt="${PET_NAMES[pt]}"><span class="pet-label">${PET_NAMES[pt]}</span>`;
        btn.addEventListener('click', () => { setSelected(pt); renderPetGrid(); });
        petBtnMap.set(pt, btn);
        container.appendChild(btn);
      }
    }

    function renderPetGrid() {
      buildPetGridInto(petGrid, () => selectedPet, p => { selectedPet = p; });
      if (canHaveTwoPets) {
        petGrid2Label.style.display = 'block';
        petGrid2.style.display = 'flex';
        buildPetGridInto(petGrid2, () => selectedPet2, p => { selectedPet2 = p; });
      } else {
        petGrid2Label.style.display = 'none';
        petGrid2.style.display = 'none';
      }
      renderCatColorRow();
    }
    renderPetGrid();

    petCodeBtn.addEventListener('click', () => {
      const code = petCodeInput.value.trim().toLowerCase();
      // Special code: "13" unlocks ability to pick a second pet
      if (code === '13') {
        canHaveTwoPets = true;
        petCodeInput.value = '';
        renderPetGrid();
        return;
      }
      for (const [pt, c] of Object.entries(PET_CODES) as [PetType, string][]) {
        if (c.toLowerCase() === code) {
          unlockedPets.add(pt);
          selectedPet = pt;
          petCodeInput.value = '';
          renderPetGrid();
          return;
        }
      }
      petCodeInput.style.borderColor = '#ff6060';
      setTimeout(() => { petCodeInput.style.borderColor = ''; }, 1000);
    });
    petCodeInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') petCodeBtn.click();
    });

    startBtn.addEventListener('click', () => {
      document.getElementById('lobby')!.style.display = 'none';
      const finalName = nameInput.value.trim() || 'Anonym';
      saveProfile({
        hullColor: selectedBoat, boatShape: selectedBoatShape, shirtColor: selectedShirt,
        hairStyle: selectedHair, hairColor: selectedHairColor, name: finalName,
        pet: selectedPet, pet2: canHaveTwoPets ? selectedPet2 : null, catColor: selectedCatColor,
      });
      resolve({
        hull: selectedBoat, shirt: selectedShirt, hair: selectedHair, hairColor: selectedHairColor,
        boatShape: selectedBoatShape, name: finalName, pet: selectedPet,
        pet2: canHaveTwoPets ? selectedPet2 : null, catColor: selectedCatColor,
      });
    });
  });
}

(async () => {

const petThumbnails = await generatePetThumbnails();
const { hull: hullColor, shirt: shirtColor, hair: hairStyle, hairColor, boatShape, name: playerName, pet: playerPet, pet2: playerPet2, catColor: playerCatColor } = await waitForColorSelection(petThumbnails);

// ---- Pixi init ----
const app = new Application();
await app.init({
  preference: 'webgl',
  background: '#1a6b8a',
  resizeTo: window,
  antialias: true,
  resolution: window.devicePixelRatio || 1,
  autoDensity: true,
});

const canvas = app.canvas as HTMLCanvasElement;
canvas.style.display     = 'block';
canvas.style.position    = 'absolute';
canvas.style.top         = '0';
canvas.style.left        = '0';
canvas.style.width       = '100%';
canvas.style.height      = '100%';
canvas.style.touchAction = 'none';
document.getElementById('app')!.appendChild(canvas);

// ---- World (3x3 tiled for seamless wrapping) ----
const islands    = generateIslands();
const worldTiles: Container[] = [];
for (let gy = -1; gy <= 1; gy++) {
  for (let gx = -1; gx <= 1; gx++) {
    const tile = buildWorldContainer(islands);
    tile.x = gx * WORLD_W;
    tile.y = gy * WORLD_H;
    worldTiles.push(tile);
  }
}
const worldContainer = new Container();
worldTiles.forEach(t => worldContainer.addChild(t));
app.stage.addChild(worldContainer);

// ---- Treasures ----
const treasures = generateTreasures(islands);
const treasureGfxList: Graphics[] = [];
for (const tr of treasures) {
  const g = new Graphics();
  drawTreasureChest(g, tr.x, tr.y);
  worldContainer.addChild(g);
  treasureGfxList.push(g);
}

// ---- Player-built islands (terrain, so drawn below houses/parks/animals) ----
const builtIslandsGfx = new Graphics();
worldContainer.addChild(builtIslandsGfx);
// id → generated shape, kept in sync with net.builtIslands in redrawStaticWorld.
const builtIslandShapes = new Map<string, Island>();
/** Real islands plus anything the special-name players have built. */
function getAllIslands(): Island[] {
  return builtIslandShapes.size ? islands.concat([...builtIslandShapes.values()]) : islands;
}

// ---- Seasonal island tint (a translucent wash over every island's shape —
// cheap because it reuses the existing polygons instead of rebuilding the
// baked-in static tile geometry, which stays untouched year-round) ----
const seasonTintGfx = new Graphics();
worldContainer.addChild(seasonTintGfx);
const SEASON_ISLAND_TINT: Record<Season, { color: number; alpha: number } | null> = {
  vår:     { color: 0xe8ffd0, alpha: 0.16 }, // fresh spring-green/yellow wash
  sommar:  null,                              // baseline look already reads as summer
  höst:    { color: 0xd07020, alpha: 0.30 },  // autumn orange/brown
  vinter:  { color: 0xffffff, alpha: 0.55 },  // snow cover
};

// ---- Buried treasure X marks (drawn in world space) ----
const buriedGfx = new Graphics();
worldContainer.addChild(buriedGfx);

// ---- Dropped inventory items (drawn as emoji glyphs in world space) ----
const droppedItemsContainer = new Container();
worldContainer.addChild(droppedItemsContainer);

// ---- Houses (drawn in world space) ----
const housesGfx = new Graphics();
worldContainer.addChild(housesGfx);

// ---- Parks (drawn in world space) ----
const parksGfx = new Graphics();
worldContainer.addChild(parksGfx);

// ---- Animals ----
const animals = generateAnimals(islands);
const animalsGfx = new Graphics();
worldContainer.addChild(animalsGfx);

// ---- Villagers (ambient decoration — same seed on every client, see villagers.ts) ----
const villagers = generateVillagers(islands);
const villagersGfx = new Graphics();
worldContainer.addChild(villagersGfx);

// ---- Bridges (drawn in world space, below gates so fences appear on top) ----
const bridgesGfx = new Graphics();
worldContainer.addChild(bridgesGfx);

// ---- Gates (drawn in world space) ----
const gatesGfx = new Graphics();
worldContainer.addChild(gatesGfx);
const gatePreviewGfx = new Graphics();
worldContainer.addChild(gatePreviewGfx);

// ---- Personal chests (currency) ----
let myChests = playerName.trim().toLowerCase() === '300'
  ? 9999
  : isSpecialName(playerName) ? 200 : 0;

// ---- Item inventory (10 slots: bought/caught food, eaten from here) ----
let myInventory: InventoryItem[] = [];

// ---- Buttons ----
const topActions      = document.getElementById('top-actions')!;
const bottomActions   = document.getElementById('bottom-actions')!;
const buryBtn         = document.getElementById('bury-btn')!;
const digBtn          = document.getElementById('dig-btn')!;
const buildToggleBtn  = document.getElementById('build-toggle-btn')!;
const buildMenu       = document.getElementById('build-menu')!;
const buildHouseBtn      = document.getElementById('build-house-btn')!;
const buildParkBtn       = document.getElementById('build-park-btn')!;
const buildPreschoolBtn  = document.getElementById('build-preschool-btn')!;
const buildSchoolBtn     = document.getElementById('build-school-btn')!;
const buildStudentenBtn  = document.getElementById('build-studenten-btn')!;
const buildToiletBtn     = document.getElementById('build-toilet-btn')!;
const buildHotelBtn      = document.getElementById('build-hotel-btn')!;
const buildIkeaBtn       = document.getElementById('build-ikea-btn')!;
const buildMatbutikBtn   = document.getElementById('build-matbutik-btn')!;
const buildCinemaBtn     = document.getElementById('build-cinema-btn')!;
const buildDjuraffarBtn        = document.getElementById('build-djuraffar-btn')!;
const buildRestaurangBtn       = document.getElementById('build-restaurang-btn')!;
const buildBlomsteraffarBtn    = document.getElementById('build-blomsteraffar-btn')!;
const buildElektronikbutikBtn  = document.getElementById('build-elektronikbutik-btn')!;
const buildBibliotekBtn        = document.getElementById('build-bibliotek-btn')!;
const buildGymBtn              = document.getElementById('build-gym-btn')!;
const demolishPanel      = document.getElementById('demolish-panel')!;
const demolishToggleBtn  = document.getElementById('demolish-toggle-btn')!;
const demolishMenu       = document.getElementById('demolish-menu')!;
const demolishHouseBtn   = document.getElementById('demolish-house-btn')!;
const demolishParkBtn    = document.getElementById('demolish-park-btn')!;

// Costs for the new buildings. They share the House pipeline (server stores
// them as Houses with a `type` field).
const BUILDING_COSTS: Record<string, number> = {
  house: 50,
  preschool: 30,
  school: 60,
  studenten: 100,
  toilet: 5,
  hotel: 25,
  ikea: 40,
  matbutik: 25,
  cinema: 60,
  djuraffar: 35,
  restaurang: 45,
  blomsteraffar: 20,
  elektronikbutik: 45,
  bibliotek: 40,
  gym: 35,
};

let buildMenuOpen    = false;
let demolishMenuOpen = false;

const killAnimalBtn  = document.getElementById('kill-animal-btn')!;
const moveAnimalBtn  = document.getElementById('move-animal-btn')!;
const dropAnimalBtn  = document.getElementById('drop-animal-btn')!;

const buildGateBtn    = document.getElementById('build-gate-btn')!;
const destroyGateBtn  = document.getElementById('destroy-gate-btn')!;
const buildBridgeBtn  = document.getElementById('build-bridge-btn')!;
const buildIslandBtn  = document.getElementById('build-island-btn')!;
destroyGateBtn.style.display = 'none';

const BRIDGE_COST = 10;
// Building islands (and demolishing them for a partial refund) is restricted
// to the same special names as gates.
const ISLAND_COST   = 100;
const ISLAND_REFUND = 90;

// ---- Build mode (shared by gate + bridge + island) ----
// Gate/bridge need two taps (start + end point); island only needs one, so it
// fires immediately on 'placing_a' instead of advancing to 'placing_b'.
type BuildMode = 'idle' | 'placing_a' | 'placing_b';
type BuildTarget = 'gate' | 'bridge' | 'island';
let buildMode: BuildMode = 'idle';
let buildTarget: BuildTarget = 'gate';
let pendingPointA: Vec2 | null = null;

function buttonForTarget(t: BuildTarget): HTMLElement {
  return t === 'gate' ? buildGateBtn : t === 'bridge' ? buildBridgeBtn : buildIslandBtn;
}
function labelForTarget(t: BuildTarget): string {
  if (t === 'gate') return 'Bygg galler';
  if (t === 'bridge') return `Bygg bro (${BRIDGE_COST} 💰)`;
  return `Bygg ö (${ISLAND_COST} 💰)`;
}

function setBuildMode(m: BuildMode, target: BuildTarget = buildTarget) {
  buildMode = m;
  buildTarget = target;
  // Reset every build button's label
  buildGateBtn.textContent = 'Bygg galler';
  buildBridgeBtn.textContent = labelForTarget('bridge');
  buildIslandBtn.textContent = labelForTarget('island');
  if (m === 'idle') {
    pendingPointA = null;
    return;
  }
  const btn = buttonForTarget(target);
  btn.textContent = m === 'placing_a' ? 'Välj startpunkt' : 'Välj slutpunkt';
}

// Only allowed specials can build / destroy gates or build / demolish
// islands. Hide the build buttons entirely for everyone else; gate-destroy
// is hidden in the game loop, island-demolish is tap-to-confirm (see below).
if (!isSpecialName(playerName)) {
  buildGateBtn.style.display = 'none';
  buildIslandBtn.style.display = 'none';
}

buildGateBtn.addEventListener('click', (e) => {
  e.stopPropagation();
  if (!isSpecialName(playerName)) return;
  if (buildMode === 'idle') setBuildMode('placing_a', 'gate');
  else setBuildMode('idle');
});

buildBridgeBtn.addEventListener('click', (e) => {
  e.stopPropagation();
  // Cancel any in-progress build
  if (buildMode !== 'idle') { setBuildMode('idle'); return; }
  if (myChests < BRIDGE_COST) return;
  setBuildMode('placing_a', 'bridge');
});

buildIslandBtn.addEventListener('click', (e) => {
  e.stopPropagation();
  if (!isSpecialName(playerName)) return;
  // Cancel any in-progress build
  if (buildMode !== 'idle') { setBuildMode('idle'); return; }
  if (myChests < ISLAND_COST) return;
  setBuildMode('placing_a', 'island');
});

// Tap-to-destroy: when a special-name player taps on a gate we show a
// confirm dialog. Track pointer-down so we can distinguish a tap from a
// joystick drag in pointer-up.
let tapStart: { x: number; y: number; time: number; id: number } | null = null;

let heldAnimalIdx: number | null = null;
let boatAnimalIdx: number | null = null;
const lastMoveSig = new Map<number, string>(); // track applied server moves

// ---- Pet walk state (local, chicken-style) ----
let petAngle = Math.random() * Math.PI * 2;
let petWalkTimer = 0;
const petOff = { x: 10, y: 8 };
let pet2Angle = Math.random() * Math.PI * 2;
let pet2WalkTimer = 0;
// (pet2Off is declared elsewhere in this file)


buryBtn.addEventListener('click', () => {
  if (myChests > 0 && gameMode === 'ashore') {
    myChests--;
    net.buryTreasure(personPos.x, personPos.y);
  }
});

digBtn.addEventListener('click', () => {
  if (gameMode !== 'ashore') return;
  for (const bt of net.buriedTreasures) {
    const dx = personPos.x - bt.x;
    const dy = personPos.y - bt.y;
    if (dx * dx + dy * dy < 30 * 30) {
      net.digTreasure(bt.id);
      myChests++;
      break;
    }
  }
});

buildToggleBtn.addEventListener('click', () => {
  buildMenuOpen = !buildMenuOpen;
  buildMenu.style.display = buildMenuOpen ? 'flex' : 'none';
  if (buildMenuOpen) { demolishMenuOpen = false; demolishMenu.style.display = 'none'; }
});

function tryBuildTypedHouse(type: string) {
  if (gameMode !== 'ashore') return;
  const cost = BUILDING_COSTS[type] ?? 50;
  if (myChests < cost) return;
  myChests -= cost;
  net.buildHouse(personPos.x, personPos.y, type);
  buildMenuOpen = false;
  buildMenu.style.display = 'none';
}

buildHouseBtn.addEventListener('click', () => tryBuildTypedHouse('house'));
buildPreschoolBtn.addEventListener('click', () => tryBuildTypedHouse('preschool'));
buildSchoolBtn.addEventListener('click', () => tryBuildTypedHouse('school'));
buildStudentenBtn.addEventListener('click', () => tryBuildTypedHouse('studenten'));
buildToiletBtn.addEventListener('click', () => tryBuildTypedHouse('toilet'));
buildHotelBtn.addEventListener('click', () => tryBuildTypedHouse('hotel'));
buildIkeaBtn.addEventListener('click', () => tryBuildTypedHouse('ikea'));
buildMatbutikBtn.addEventListener('click', () => tryBuildTypedHouse('matbutik'));
buildCinemaBtn.addEventListener('click', () => tryBuildTypedHouse('cinema'));
buildDjuraffarBtn.addEventListener('click', () => tryBuildTypedHouse('djuraffar'));
buildRestaurangBtn.addEventListener('click', () => tryBuildTypedHouse('restaurang'));
buildBlomsteraffarBtn.addEventListener('click', () => tryBuildTypedHouse('blomsteraffar'));
buildElektronikbutikBtn.addEventListener('click', () => tryBuildTypedHouse('elektronikbutik'));
buildBibliotekBtn.addEventListener('click', () => tryBuildTypedHouse('bibliotek'));
buildGymBtn.addEventListener('click', () => tryBuildTypedHouse('gym'));

buildParkBtn.addEventListener('click', () => {
  if (gameMode !== 'ashore' || myChests < 30) return;
  myChests -= 30;
  net.buildPark(personPos.x, personPos.y);
  buildMenuOpen = false;
  buildMenu.style.display = 'none';
});

demolishToggleBtn.addEventListener('click', () => {
  demolishMenuOpen = !demolishMenuOpen;
  demolishMenu.style.display = demolishMenuOpen ? 'flex' : 'none';
  if (demolishMenuOpen) { buildMenuOpen = false; buildMenu.style.display = 'none'; }
});

demolishHouseBtn.addEventListener('click', () => {
  if (gameMode !== 'ashore') return;
  for (const h of net.houses) {
    const dx = personPos.x - h.x;
    const dy = personPos.y - h.y;
    if (dx * dx + dy * dy < 40 * 40) {
      const refund = BUILDING_COSTS[h.type ?? 'house'] ?? 50;
      net.demolishHouse(h.id);
      myChests += refund;
      demolishMenuOpen = false;
      demolishMenu.style.display = 'none';
      break;
    }
  }
});

demolishParkBtn.addEventListener('click', () => {
  if (gameMode !== 'ashore') return;
  for (const p of net.parks) {
    const dx = personPos.x - p.x;
    const dy = personPos.y - p.y;
    if (dx * dx + dy * dy < 70 * 70) {
      net.demolishPark(p.id);
      myChests += 30;
      demolishMenuOpen = false;
      demolishMenu.style.display = 'none';
      break;
    }
  }
});

killAnimalBtn.addEventListener('click', () => {
  if (gameMode !== 'ashore') return;
  const idx = findAnimalNear(animals, net.killedAnimals, personPos.x, personPos.y, 22);
  if (idx !== -1) net.killAnimal(idx);
});

moveAnimalBtn.addEventListener('click', () => {
  if (gameMode !== 'ashore') return;
  const idx = findAnimalNear(animals, net.killedAnimals, personPos.x, personPos.y, 22);
  if (idx !== -1) heldAnimalIdx = idx;
});

dropAnimalBtn.addEventListener('click', () => {
  if (heldAnimalIdx === null) return;
  net.moveAnimal(heldAnimalIdx, personPos.x, personPos.y);
  // Update island index for wandering
  animals[heldAnimalIdx].islandIdx = findIslandForPos(personPos, getAllIslands());
  heldAnimalIdx = null;
});

// ---- Boat (screen-space, always at screen center) ----
const boat          = new Boat(hullColor, shirtColor, hairStyle, playerName, boatShape, hairColor);
const boatContainer = new Container();
boatContainer.addChild(boat.container);
app.stage.addChild(boatContainer);

// ---- HUD ----
const hud = new Hud();
app.stage.addChild(hud.container);

// ---- Minimap ----
const minimap = new Minimap(islands);
app.stage.addChild(minimap.container);

// ---- Minimap toggle ----
const minimapCover   = document.getElementById('minimap-cover')!;
const minimapShowBtn = document.getElementById('minimap-show-btn')!;
minimapCover.addEventListener('click', () => {
  minimap.container.visible = false;
  minimapCover.style.display = 'none';
  minimapShowBtn.style.display = 'flex';
});
minimapShowBtn.addEventListener('click', () => {
  minimap.container.visible = true;
  minimapCover.style.display = 'block';
  minimapShowBtn.style.display = 'none';
});

// ---- Joystick overlay ----
const joystickGraphics = new Graphics();
app.stage.addChild(joystickGraphics);

// ---- Local boat animal overlay (screen-space) ----
const localBoatAnimalGfx = new Graphics();
app.stage.addChild(localBoatAnimalGfx);

// ---- Local pet (world-space, follows player) ----
const localPetGfx = new Graphics();
worldContainer.addChild(localPetGfx);
const localPet2Gfx = new Graphics();
worldContainer.addChild(localPet2Gfx);
const pet2Off = { x: -10, y: 8 };

// ---- Multiplayer ----
const net = connectToServer();
net.sendAppearance(hullColor, shirtColor, hairStyle, playerName, playerPet, playerPet2, playerCatColor, hairColor, boatShape);
const remoteBoats = new RemoteBoats(worldContainer, app.stage);
remoteBoats.animalsRef = animals;

// Walkability used both in gameplay and to keep remote pets on land/bridges.
const isWalkable = (pos: Vec2): boolean => {
  for (const isl of getAllIslands()) if (isInsideIsland(pos, isl)) return true;
  for (const b of net.bridges) {
    if (pointToSegmentDist(pos, { x: b.x1, y: b.y1 }, { x: b.x2, y: b.y2 }) < 10) return true;
  }
  return false;
};
remoteBoats.isWalkableFn = isWalkable;

// ---- Debug overlay (activated via ?debug in URL) ----
const debugOverlay = new URLSearchParams(location.search).has('debug') ? new DebugOverlay() : null;

// ---- Joystick instance (single joystick for steering, anywhere on screen) ----
const steerJoy = new Joystick('full', { x: 0, y: -1 });

function screenToWorld(sx: number, sy: number): Vec2 {
  const anchor = gameMode === 'ashore' ? personPos : boatPos;
  let wx = anchor.x + (sx - app.screen.width / 2);
  let wy = anchor.y + (sy - app.screen.height / 2);
  wx = ((wx % WORLD_W) + WORLD_W) % WORLD_W;
  wy = ((wy % WORLD_H) + WORLD_H) % WORLD_H;
  return { x: wx, y: wy };
}

canvas.addEventListener('pointerdown', (e: PointerEvent) => {
  if (gameMode === 'inside' && placingFurniture && currentHouseId) {
    const rect = canvas.getBoundingClientRect();
    const rx = e.clientX - rect.left - app.screen.width / 2;
    const ry = e.clientY - rect.top - app.screen.height / 2;
    if (Math.abs(rx) < ROOM_W / 2 - 12 && Math.abs(ry) < ROOM_H / 2 - 12) {
      net.placeFurniture(currentHouseId, {
        type: placingFurniture.type, color: placingFurniture.color,
        x: rx, y: ry, subroom: currentSubroom(),
      });
      myFurniture = myFurniture.filter(f => f.id !== placingFurniture!.id);
      placingFurniture = null;
    }
    e.preventDefault();
    return;
  }
  if (buildMode !== 'idle' && gameMode === 'inside') {
    // Defensive: gate/bridge/island building must never fire indoors — the
    // buttons themselves are hidden on entry (see enterHouseBtn), but a
    // stale in-progress gesture could otherwise still complete and build at
    // a stale boatPos anchor.
    setBuildMode('idle');
    e.preventDefault();
    return;
  }
  if (buildMode !== 'idle') {
    const rect = canvas.getBoundingClientRect();
    const p = screenToWorld(e.clientX - rect.left, e.clientY - rect.top);
    if (buildMode === 'placing_a' && buildTarget === 'island') {
      // Single-point placement — no second tap needed.
      if (myChests >= ISLAND_COST) {
        myChests -= ISLAND_COST;
        net.buildIsland(p.x, p.y);
      }
      setBuildMode('idle');
    } else if (buildMode === 'placing_a') {
      pendingPointA = p;
      setBuildMode('placing_b');
    } else if (pendingPointA) {
      if (buildTarget === 'gate') {
        net.buildGate(pendingPointA.x, pendingPointA.y, p.x, p.y);
      } else if (myChests >= BRIDGE_COST) {
        myChests -= BRIDGE_COST;
        net.buildBridge(pendingPointA.x, pendingPointA.y, p.x, p.y);
      }
      setBuildMode('idle');
    }
    e.preventDefault();
    return;
  }
  tapStart = { x: e.clientX, y: e.clientY, time: Date.now(), id: e.pointerId };
  steerJoy.handlePointerDown(e, app.screen.width, canvas);
}, { passive: false });

canvas.addEventListener('pointermove', (e: PointerEvent) => {
  steerJoy.handlePointerMove(e);
  e.preventDefault();
}, { passive: false });

canvas.addEventListener('pointerup', (e: PointerEvent) => {
  steerJoy.handlePointerUp(e);
  // Indoors, a tap is for removing a placed furniture item (refunds its
  // price). Placement itself already consumed its own gesture on pointerdown
  // and returned early there, so tapStart is never set for that gesture —
  // the same tap can't immediately un-place what it just placed.
  if (gameMode === 'inside') {
    if (tapStart && tapStart.id === e.pointerId && currentHouseId) {
      const dx = e.clientX - tapStart.x;
      const dy = e.clientY - tapStart.y;
      const elapsed = Date.now() - tapStart.time;
      if (dx * dx + dy * dy < 625 && elapsed < 600) {
        const rect = canvas.getBoundingClientRect();
        const rx = e.clientX - rect.left - app.screen.width / 2;
        const ry = e.clientY - rect.top - app.screen.height / 2;
        const house = net.houses.find(h => h.id === currentHouseId);
        const subroom = currentSubroom();
        let best: { id: string; type: string } | null = null;
        let bestDist = 26 * 26;
        for (const item of house?.furniture ?? []) {
          if ((item.subroom ?? 'main') !== subroom) continue;
          const ddx = rx - item.x, ddy = ry - item.y;
          const d2 = ddx * ddx + ddy * ddy;
          if (d2 < bestDist) { bestDist = d2; best = { id: item.id, type: item.type }; }
        }
        if (best) {
          const refund = FURNITURE_PRICES[best.type] ?? 0;
          if (confirm(`Ta bort ${FURNITURE_LABELS[best.type] ?? best.type}? (+${refund} 💰)`)) {
            net.removeFurniture(currentHouseId, best.id);
            myChests += refund;
          }
        }
      }
    }
    tapStart = null;
    return;
  }
  if (tapStart && tapStart.id === e.pointerId) {
    const dx = e.clientX - tapStart.x;
    const dy = e.clientY - tapStart.y;
    const elapsed = Date.now() - tapStart.time;
    if (dx * dx + dy * dy < 625 && elapsed < 600) {
      const rect = canvas.getBoundingClientRect();
      const p = screenToWorld(e.clientX - rect.left, e.clientY - rect.top);

      // Gate (specials only)
      if (isSpecialName(playerName)) {
        let bestGate: string | null = null;
        let bestGateDist = 25;
        for (const g of net.gates) {
          const d = pointToSegmentDist(p, { x: g.x1, y: g.y1 }, { x: g.x2, y: g.y2 });
          if (d < bestGateDist) { bestGateDist = d; bestGate = g.id; }
        }
        if (bestGate !== null) {
          if (confirm('Ta sönder gallret?')) net.destroyGate(bestGate);
          tapStart = null;
          return;
        }
      }

      // Built island (specials only, partial refund)
      if (isSpecialName(playerName)) {
        let bestIsland: string | null = null;
        let bestIslandDist = Infinity;
        for (const [id, isl] of builtIslandShapes) {
          const dx = p.x - isl.cx, dy = p.y - isl.cy;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < isl.radius && dist < bestIslandDist) { bestIslandDist = dist; bestIsland = id; }
        }
        if (bestIsland !== null) {
          if (confirm(`Riv ön? (+${ISLAND_REFUND} 💰)`)) {
            net.demolishIsland(bestIsland);
            myChests += ISLAND_REFUND;
          }
          tapStart = null;
          return;
        }
      }

      // Bridge (anyone)
      let bestBridge: string | null = null;
      let bestBridgeDist = 15; // match bridge half-width (8) + tolerance
      for (const b of net.bridges) {
        const d = pointToSegmentDist(p, { x: b.x1, y: b.y1 }, { x: b.x2, y: b.y2 });
        if (d < bestBridgeDist) { bestBridgeDist = d; bestBridge = b.id; }
      }
      if (bestBridge !== null) {
        if (confirm('Ta sönder bron?')) {
          net.destroyBridge(bestBridge);
          myChests += BRIDGE_COST;
        }
      }
    }
  }
  tapStart = null;
});
canvas.addEventListener('pointercancel', (e: PointerEvent) => {
  steerJoy.handlePointerUp(e);
  tapStart = null;
});

// ---- Turning helpers ----
const TURN_LERP = 0.10; // fraction of remaining angle closed per frame (~10%)

/** Lerp `current` toward `target` angle, taking the shortest arc. */
function lerpAngle(current: number, target: number, t: number): number {
  let delta = target - current;
  // Wrap to [-π, π]
  delta -= Math.round(delta / (2 * Math.PI)) * 2 * Math.PI;
  return current + delta * t;
}

// ---- Ashore person (drawn in world space when off the boat) ----
const ashoreGfx = new Graphics();
worldContainer.addChild(ashoreGfx);

let walkPhase = 0; // animation phase

// ---- Ashore button ----
const ashoreBtn = document.getElementById('ashore-btn')!;

// ---- Game state ----
let boatPos: Vec2 = { x: WORLD_W / 2, y: WORLD_H / 2 }; // start at world center
let boatAngle      = 0;            // heading angle (0 = north, clockwise positive)
let sailAngle      = Math.PI / 2;  // world-space sail angle
let targetBoatAngle = 0;
let speed          = 0;

type GameMode = 'sailing' | 'ashore' | 'inside';
let gameMode: GameMode = 'sailing';
let personPos: Vec2 = { x: 0, y: 0 };
let currentIsland: Island | null = null;
const WALK_SPEED = 1.2;

// Indoors: when the player enters a building, we switch to this mode.
// indoorPos is a screen-centred offset within the room (0,0 = middle).
let indoorPos: Vec2 = { x: 0, y: 0 };
const ROOM_W = 400;
const ROOM_H = 300;
type SchoolRoom = 'matsal' | 'klassrum' | 'hall' | 'toalett';
// 'main' is the entry/living room — kept as 'main' (not 'vardagsrum') so it
// lines up with the subroom key furniture placed before this feature existed
// was already tagged with, instead of stranding it in an unreachable room.
type HouseRoom = 'main' | 'kok' | 'sovrum' | 'badrum';
type RoomKey = SchoolRoom | HouseRoom;
let enteredBuildingType: string = 'house';
let currentHouseId: string | null = null;

// ---- Hotel: lobby (floor 0) → elevator → 10 floors × 10 furnished rooms ----
let hotelFloor = 0;
let hotelRoomIdx: number | null = null;
let hotelFloorMenuOpen = false;

/**
 * Buildings with more than one interior view (school's hall/matsal/klassrum/
 * toalett, hotel's lobby/corridor/room-per-floor) share one House record, so
 * furniture needs a sub-location key or a sofa bought for hotel room 4 would
 * bleed into every other room and floor. Single-room buildings just get one
 * shared bucket.
 */
function currentSubroom(): string {
  if (enteredBuildingType === 'school') return currentSchoolRoom;
  if (enteredBuildingType === 'hotel') {
    return hotelRoomIdx !== null ? `f${hotelFloor}r${hotelRoomIdx}` : `f${hotelFloor}`;
  }
  if (enteredBuildingType === 'house') return currentHouseRoom;
  return 'main';
}

/** Which room-navigation state (door/stair) is active right now — school's
 *  or the house's. Only meaningful while indoors in one of those two. */
function currentNavRoom(): RoomKey {
  return enteredBuildingType === 'house' ? currentHouseRoom : currentSchoolRoom;
}

// ---- Furniture (bought at IKEA, placed in your own house) ----
interface OwnedFurniture { id: string; type: string; color?: string }
let myFurniture: OwnedFurniture[] = [];
let placingFurniture: OwnedFurniture | null = null;
const FURNITURE_LABELS: Record<string, string> = {
  bed: '🛏️ Säng', stool: '🪑 Pall', table: '🍽️ Bord', oven: '🔥 Ugn', fridge: '❄️ Kylskåp',
  sofa: '🛋️ Soffa', tv: '📺 TV', lamp: '💡 Lampa', rug: '🟫 Matta',
  bookshelf: '📚 Bokhylla', desk: '🖥️ Skrivbord', mirror: '🪞 Spegel', plant: '🪴 Krukväxt',
  chair: '🪑 Stol', wardrobe: '🚪 Garderob', painting: '🖼️ Tavla', clock: '🕐 Klocka',
  bigbookshelf: '📚 Bokhylla stor', desklamp: '💡 Skrivbordslampa', floormirror: '🪞 Golvspegel',
  barstool: '🪑 Barstol', coffeetable: '🍽️ Soffbord', dinnertable: '🍽️ Matbord runt',
  bigplant: '🪴 Grönväxt stor', cactus: '🌵 Kaktus', wallshelf: '📚 Hylla', tvstand: '📺 TV-bänk',
  armchair: '🛋️ Fåtölj', pouf: '🪑 Puff', candlestick: '🕯️ Ljusstake', vase: '🏺 Vas',
  blanket: '🧣 Filt', pillow: '🟦 Kudde', ladder: '📚 Bokstege', shoerack: '👞 Skohylla',
  bartable: '🍽️ Barbord', kitchencounter: '🚰 Diskbänk', microwave: '📦 Mikrovågsugn',
  toaster: '🍞 Brödrost', spicerack: '🧂 Kryddhylla', radiator: '🌡️ Element', floorlamp: '💡 Golvlampa',
  petbed: '🛏️ Hundkorg', birdcage: '🐦 Fågelbur', aquarium: '🐠 Akvarium', cattower: '🐈 Kattträd', hamstercage: '🐹 Hamsterbur',
  tulipvase: '🌷 Tulpanvas', roses: '🌹 Rosenbukett', orchid: '🌼 Orkidé', sunflower: '🌻 Solros', bonsai: '🌳 Bonsaiträd',
  computer: '🖥️ Dator', console: '🎮 Spelkonsol', stereo: '🎵 Stereo', projector: '📽️ Projektor', speaker: '🔊 Högtalare',
};

/** Buy prices, mirrored here (as opposed to only living on the HTML buttons)
 *  so a tap-to-remove refund can look one up without touching the DOM. */
const FURNITURE_PRICES: Record<string, number> = {
  bed: 30, stool: 10, table: 15, oven: 25, fridge: 20,
  sofa: 25, tv: 20, lamp: 8, rug: 12, bookshelf: 18, desk: 15, mirror: 10, plant: 6,
  chair: 12, wardrobe: 22, painting: 8, clock: 7,
  bigbookshelf: 24, desklamp: 9, floormirror: 14, barstool: 11, coffeetable: 13,
  dinnertable: 17, bigplant: 10, cactus: 7, wallshelf: 9, tvstand: 14,
  armchair: 20, pouf: 8, candlestick: 5, vase: 6, blanket: 8, pillow: 4,
  ladder: 16, shoerack: 9, bartable: 15, kitchencounter: 22, microwave: 14,
  toaster: 6, spicerack: 7, radiator: 10, floorlamp: 12,
  petbed: 12, birdcage: 14, aquarium: 18, cattower: 20, hamstercage: 10,
  tulipvase: 6, roses: 8, orchid: 9, sunflower: 7, bonsai: 11,
  computer: 18, console: 16, stereo: 14, projector: 20, speaker: 10,
};
let currentSchoolRoom: SchoolRoom = 'hall';
let currentHouseRoom: HouseRoom = 'main';
const enterHouseBtn = document.getElementById('enter-house-btn')!;
const stairBtn = document.getElementById('stair-btn')!;
const hotelActionBtn = document.getElementById('hotel-action-btn')!;
const hotelFloorMenu = document.getElementById('hotel-floor-menu')!;
const cinemaActionBtn = document.getElementById('cinema-action-btn')!;
const movieOverlayGfx = new Graphics();
app.stage.addChild(movieOverlayGfx);
const peeBtn = document.getElementById('pee-btn')!;
const washBtn = document.getElementById('wash-btn')!;
const eatBtn = document.getElementById('eat-btn')!;
const raiseHandBtn = document.getElementById('raise-hand-btn')!;
const fishBtn = document.getElementById('fish-btn')!;
const pickupBtn = document.getElementById('pickup-btn')!;
const sleepBtn = document.getElementById('sleep-btn')!;
const brushBtn = document.getElementById('brush-btn')!;
const cookBtn = document.getElementById('cook-btn')!;
const fridgeToggleBtn = document.getElementById('fridge-toggle-btn')!;
const fridgeMenu = document.getElementById('fridge-menu')!;
const tableToggleBtn = document.getElementById('table-toggle-btn')!;
const tableMenu = document.getElementById('table-menu')!;
const peeOverlay = document.getElementById('pee-overlay')!;
const roomGfx = new Graphics();
app.stage.addChild(roomGfx);
const indoorPersonGfx = new Graphics();
app.stage.addChild(indoorPersonGfx);
const indoorPetGfx = new Graphics();
app.stage.addChild(indoorPetGfx);
const indoorPet2Gfx = new Graphics();
app.stage.addChild(indoorPet2Gfx);
const furnitureGfx = new Graphics();
app.stage.addChild(furnitureGfx);
const indoorRemoteGfx = new Graphics();
app.stage.addChild(indoorRemoteGfx);

// Stairs: each stair is a point on the room's floor with a target room.
// Positions are in room-relative coords (centred on room centre, ±ROOM_W/2,±ROOM_H/2).
interface Stair { rx: number; ry: number; target: RoomKey; label: string }
const STAIRS_BY_ROOM: Record<RoomKey, Stair[]> = {
  hall: [
    { rx: -ROOM_W / 2 + 60, ry: -ROOM_H / 2 + 60,  target: 'matsal',   label: 'Matsal' },
    { rx: 0,                ry: -ROOM_H / 2 + 60,  target: 'klassrum', label: 'Klassrum' },
    { rx:  ROOM_W / 2 - 60, ry: -ROOM_H / 2 + 60,  target: 'toalett',  label: 'Toaletter' },
  ],
  matsal:   [{ rx: 0, ry: ROOM_H / 2 - 40, target: 'hall', label: 'Hall' }],
  klassrum: [{ rx: 0, ry: ROOM_H / 2 - 40, target: 'hall', label: 'Hall' }],
  toalett:  [{ rx: 0, ry: ROOM_H / 2 - 40, target: 'hall', label: 'Hall' }],
  // House: 'main' (the living room you land in) branches to 3 more rooms,
  // each of which leads straight back — same hub-and-spoke shape as school.
  main: [
    { rx: -ROOM_W / 2 + 60, ry: -ROOM_H / 2 + 60,  target: 'kok',     label: 'Kök' },
    { rx: 0,                ry: -ROOM_H / 2 + 60,  target: 'sovrum',  label: 'Sovrum' },
    { rx:  ROOM_W / 2 - 60, ry: -ROOM_H / 2 + 60,  target: 'badrum',  label: 'Badrum' },
  ],
  kok:    [{ rx: 0, ry: ROOM_H / 2 - 40, target: 'main', label: 'Vardagsrum' }],
  sovrum: [{ rx: 0, ry: ROOM_H / 2 - 40, target: 'main', label: 'Vardagsrum' }],
  badrum: [{ rx: 0, ry: ROOM_H / 2 - 40, target: 'main', label: 'Vardagsrum' }],
};

let nearStairTarget: RoomKey | null = null;
let nearToilet = false;
let nearSink = false;
let nearFood = false;
let nearOutdoorToilet = false;
let nearBed = false;
let nearMirror = false;
let nearFishingSpot = false;
let fishing = false; // true while a cast is out — startAction handles the timer/overlay
let nearDroppedItem: DroppedItem | null = null;
let nearStove = false;
let cooking = false; // true while a dish is on the stove — same pattern as fishing
let nearFridge = false;
let nearFridgeId: string | null = null; // which fridge (furniture id) you're standing next to
let fridgeMenuOpen = false;
let nearTable = false;
let nearTableId: string | null = null; // which table (furniture id) you're standing next to
let tableMenuOpen = false;
let nearElevator = false;
let nearHotelDoor: number | null = null;
let nearRoomExit = false;
let nearScreen = false;
let watchingMovie = false;
let currentMovieIdx = 0;
// Single action timer — while >0 the player stands still and the overlay shows.
// Used both indoors and outdoors.
let actionTimer = 0;

// Top-left meter block: a small emoji icon to the left of each bar so it's
// clear at a glance which meter is which.
const METER_X = 14, METER_Y = 14, METER_ROW_H = 18, METER_ICON_W = 20;
const bladderIcon = new Text({ text: '🚽', style: { fontSize: 13 } });
bladderIcon.x = METER_X;
bladderIcon.y = METER_Y - 1;
app.stage.addChild(bladderIcon);
const hungerIcon = new Text({ text: '🍗', style: { fontSize: 13 } });
hungerIcon.x = METER_X;
hungerIcon.y = METER_Y + METER_ROW_H - 1;
app.stage.addChild(hungerIcon);

// Bladder meter: fills over 10 minutes. When full, green smoke appears.
const PEE_FULL_FRAMES = 10 * 60 * 60; // 10 min @ ~60 fps
let bladderFill = 0;
let peeingOnSelf = false;
const bladderMeterGfx = new Graphics();
app.stage.addChild(bladderMeterGfx);
const peeSmokeGfx = new Graphics();
app.stage.addChild(peeSmokeGfx);

function relievePee() {
  bladderFill = 0;
  peeingOnSelf = false;
}

// Hunger meter: drains over 15 minutes. When empty, mild speed penalty (this
// game has no hard fail states — being hungry should nudge you to eat, not
// strand you).
const HUNGER_EMPTY_FRAMES = 15 * 60 * 60; // 15 min @ ~60 fps
let hungerFill = HUNGER_EMPTY_FRAMES; // starts full-fed, counts down
let starving = false;
const hungerMeterGfx = new Graphics();
app.stage.addChild(hungerMeterGfx);

function feedHunger() {
  hungerFill = HUNGER_EMPTY_FRAMES;
  starving = false;
}

/** Mild movement penalty while starving — a nudge to go eat, never a hard stop. */
function starvingMul(): number {
  return starving ? 0.8 : 1;
}

// ---- Seasonal weather particles (screen-space: snow / falling leaves /
// drifting petals). Summer has none — the game's baseline greens and blues
// already read as summery, so there's nothing to add. ----
interface WeatherParticle { x: number; y: number; vy: number; drift: number; phase: number; size: number; color: number; leaf: boolean; }
const weatherGfx = new Graphics();
app.stage.addChild(weatherGfx);
let weatherParticles: WeatherParticle[] = [];
// undefined = not yet built; distinct from any real Season so the first
// frame always (re)builds the right set.
let weatherParticleSeason: Season | null | undefined;

const WEATHER_PARTICLE_COUNT: Record<Season, number> = { vår: 26, sommar: 0, höst: 36, vinter: 55 };
const WEATHER_PARTICLE_COLORS: Record<Season, number[]> = {
  vår: [0xfff8d0, 0xffffff, 0xfff0b0],
  sommar: [],
  höst: [0xd06020, 0xb04010, 0xe0a020, 0x904018],
  vinter: [0xffffff, 0xe8f4ff],
};

function respawnWeatherParticle(p: WeatherParticle, season: Season, screenW: number, screenH: number, fresh: boolean): void {
  const colors = WEATHER_PARTICLE_COLORS[season];
  p.x = Math.random() * screenW;
  p.y = fresh ? Math.random() * screenH : -10 - Math.random() * 40;
  p.vy = season === 'vinter' ? 0.25 + Math.random() * 0.35 : 0.4 + Math.random() * 0.6;
  p.drift = 0.5 + Math.random() * 1.5;
  p.phase = Math.random() * Math.PI * 2;
  p.size = season === 'vinter' ? 1.5 + Math.random() * 2 : 2 + Math.random() * 2.5;
  p.color = colors[Math.floor(Math.random() * colors.length)] ?? 0xffffff;
  p.leaf = season === 'höst';
}

function ensureWeatherParticles(season: Season, screenW: number, screenH: number): void {
  if (weatherParticleSeason === season) return;
  weatherParticleSeason = season;
  const count = WEATHER_PARTICLE_COUNT[season];
  weatherParticles = [];
  for (let i = 0; i < count; i++) {
    const p: WeatherParticle = { x: 0, y: 0, vy: 0, drift: 0, phase: 0, size: 0, color: 0xffffff, leaf: false };
    respawnWeatherParticle(p, season, screenW, screenH, true);
    weatherParticles.push(p);
  }
}

function startAction(label: string, durationFrames: number = 150) {
  actionTimer = durationFrames;
  peeOverlay.textContent = label;
  peeOverlay.style.display = 'block';
}

stairBtn.addEventListener('click', () => {
  if (nearStairTarget && actionTimer <= 0) {
    if (enteredBuildingType === 'house') {
      currentHouseRoom = nearStairTarget as HouseRoom;
    } else {
      currentSchoolRoom = nearStairTarget as SchoolRoom;
    }
    indoorPos = { x: 0, y: 0 };
  }
});

peeBtn.addEventListener('click', () => {
  if (actionTimer > 0) return;
  if (nearToilet || nearOutdoorToilet) {
    startAction('Aaahhh... 💦', 150);
    relievePee();
  }
});
washBtn.addEventListener('click', () => {
  if (actionTimer <= 0 && nearSink) startAction('🧼 Tvättar händerna...', 120);
});
eatBtn.addEventListener('click', () => {
  if (actionTimer <= 0 && nearFood) startAction('🍽️ Tar mat...', 120);
});
fishBtn.addEventListener('click', () => {
  if (actionTimer > 0 || fishing || !nearFishingSpot) return;
  fishing = true;
  startAction('🎣 Fiskar...', (30 + Math.random() * 30) * 60);
});
pickupBtn.addEventListener('click', () => {
  if (!nearDroppedItem || actionTimer > 0 || myInventory.length >= INVENTORY_SLOTS) return;
  net.pickupItem(nearDroppedItem.id);
  addToInventory(myInventory, { subtype: nearDroppedItem.subtype, label: nearDroppedItem.label, emoji: nearDroppedItem.emoji });
});
sleepBtn.addEventListener('click', () => {
  if (actionTimer > 0 || !nearBed) return;
  startAction('😴 Sover...', 200);
  relievePee();
  feedHunger();
});
brushBtn.addEventListener('click', () => {
  if (actionTimer > 0 || !nearMirror) return;
  startAction('💇 Fixar håret...', 100);
});
cookBtn.addEventListener('click', () => {
  if (actionTimer > 0 || cooking || !nearStove) return;
  cooking = true;
  startAction('🍳 Lagar mat...', (15 + Math.random() * 15) * 60);
});
fridgeToggleBtn.addEventListener('click', () => {
  fridgeMenuOpen = !fridgeMenuOpen;
  if (fridgeMenuOpen) renderFridgeMenu();
  fridgeMenu.style.display = fridgeMenuOpen ? 'flex' : 'none';
});
tableToggleBtn.addEventListener('click', () => {
  tableMenuOpen = !tableMenuOpen;
  if (tableMenuOpen) renderTableMenu();
  tableMenu.style.display = tableMenuOpen ? 'flex' : 'none';
});

// Hand-raise in the classroom
let handRaisedTimer = 0;
raiseHandBtn.addEventListener('click', () => {
  if (gameMode === 'inside' && enteredBuildingType === 'school' &&
      currentSchoolRoom === 'klassrum' && handRaisedTimer <= 0) {
    handRaisedTimer = 180; // ~3 s
  }
});
const handEmoji = new Text({ text: '🙋', style: { fontSize: 18 } });
handEmoji.visible = false;
app.stage.addChild(handEmoji);

// ---- School NPCs ----
interface NPC {
  rx: number; ry: number;
  shirtColor: number; hairStyle: number;
  hairColor?: number;
  pantsColor?: number;
  scale: number;
  room: SchoolRoom;
  isTeacher?: boolean;
  isTalker?: boolean;  // Classroom: wanders + has a speech bubble
  sitStill?: boolean;  // Doesn't move — sits at a desk
  bubbleText?: Text;
  bubbleBg?: Graphics;
  // Wander state — mutable. Still-NPCs keep these but ignore them.
  angle: number;
  timer: number;
  walkPhase: number;
}
function makeNPC(partial: Omit<NPC, 'angle' | 'timer' | 'walkPhase' | 'bubbleText' | 'bubbleBg'>): NPC {
  return { ...partial, angle: Math.random() * Math.PI * 2, timer: Math.random() * 120, walkPhase: 0 };
}
const SCHOOL_NPCS: NPC[] = [
  // Hall
  makeNPC({ rx: -100, ry: 50,   shirtColor: 0xe04040, hairStyle: 1, scale: 0.75, room: 'hall' }),
  makeNPC({ rx:   40, ry: 80,   shirtColor: 0x40b060, hairStyle: 2, scale: 0.75, room: 'hall' }),
  makeNPC({ rx:  100, ry: -10,  shirtColor: 0x2a9d8f, hairStyle: 0, scale: 0.75, room: 'hall' }),
  // Matsal
  makeNPC({ rx: -100, ry: -20,  shirtColor: 0xe0d040, hairStyle: 1, scale: 0.75, room: 'matsal' }),
  makeNPC({ rx:  -30, ry: -20,  shirtColor: 0x40c0c0, hairStyle: 2, scale: 0.75, room: 'matsal' }),
  makeNPC({ rx:   40, ry: -20,  shirtColor: 0xe08030, hairStyle: 0, scale: 0.75, room: 'matsal' }),
  makeNPC({ rx:  100, ry:  70,  shirtColor: 0x4080e0, hairStyle: 1, scale: 0.75, room: 'matsal' }),
  // Klassrum - teacher + 12 students (9 sit at desks, 3 wander + talk)
  makeNPC({ rx:    0, ry: -60,  shirtColor: 0x2255aa, hairStyle: 2, scale: 0.9,  room: 'klassrum', isTeacher: true }),
  // 9 sitting students parked on their chairs (desk grid 4×3, chair centres
  // computed from drawKlassrum: cols at room-x -140/-47/47/140, rows at
  // room-y -26/44/114). Front two rows are fully seated, plus one pupil in
  // the back row.
  makeNPC({ rx: -140, ry: -26, shirtColor: 0xe04040, hairStyle: 0, scale: 0.65, room: 'klassrum', sitStill: true }),
  makeNPC({ rx:  -47, ry: -26, shirtColor: 0x40b060, hairStyle: 2, scale: 0.65, room: 'klassrum', sitStill: true }),
  makeNPC({ rx:   47, ry: -26, shirtColor: 0xe0d040, hairStyle: 1, scale: 0.65, room: 'klassrum', sitStill: true }),
  makeNPC({ rx:  140, ry: -26, shirtColor: 0x40c0c0, hairStyle: 0, scale: 0.65, room: 'klassrum', sitStill: true }),
  makeNPC({ rx: -140, ry:  44, shirtColor: 0xe08030, hairStyle: 2, scale: 0.65, room: 'klassrum', sitStill: true }),
  makeNPC({ rx:  -47, ry:  44, shirtColor: 0x4080e0, hairStyle: 0, scale: 0.65, room: 'klassrum', sitStill: true }),
  makeNPC({ rx:   47, ry:  44, shirtColor: 0xd4a017, hairStyle: 2, scale: 0.65, room: 'klassrum', sitStill: true }),
  makeNPC({ rx:  140, ry:  44, shirtColor: 0xa0b050, hairStyle: 0, scale: 0.65, room: 'klassrum', sitStill: true }),
  makeNPC({ rx:  -47, ry: 114, shirtColor: 0x50c890, hairStyle: 2, scale: 0.65, room: 'klassrum', sitStill: true }),
  // 3 talkers: short hair, wander, speech bubbles. One is fully teal.
  makeNPC({ rx:  -90, ry:  70,  shirtColor: 0x2a9d8f, hairStyle: 1, hairColor: 0x2a9d8f, pantsColor: 0x2a9d8f, scale: 0.65, room: 'klassrum', isTalker: true }),
  makeNPC({ rx:   50, ry:  70,  shirtColor: 0xe04040, hairStyle: 1, scale: 0.65, room: 'klassrum', isTalker: true }),
  makeNPC({ rx:   90, ry:  70,  shirtColor: 0x4080e0, hairStyle: 1, scale: 0.65, room: 'klassrum', isTalker: true }),
  // Toalett
  makeNPC({ rx: -120, ry:  10,  shirtColor: 0xe08030, hairStyle: 1, scale: 0.75, room: 'toalett' }),
  makeNPC({ rx:   20, ry:  70,  shirtColor: 0x4a90d9, hairStyle: 2, scale: 0.75, room: 'toalett' }),
];
const indoorNpcGfx = new Graphics();
app.stage.addChild(indoorNpcGfx);

const TEACHER_PHRASES = ['Goddag klassen!', '7 × 6 = 42', 'Läs sidan 42', 'Tyst i klassen!', 'Mycket bra!', 'Vem kan svara?'];
const STUDENT_CHATTER = ['haha!', 'ba så ja!', 'coolt!', 'nämen!', 'yo!', 'serru!', 'checka!', 'haha XD'];
const teacherBubbleBg = new Graphics();
app.stage.addChild(teacherBubbleBg);
const teacherBubble = new Text({ text: '', style: { fontSize: 12, fontFamily: 'system-ui, sans-serif', fill: 0x1a1a1a } });
teacherBubble.visible = false;
teacherBubbleBg.visible = false;
app.stage.addChild(teacherBubble);

enterHouseBtn.addEventListener('click', () => {
  if (gameMode === 'ashore') {
    let near = false;
    let buildingType: string = 'house';
    let houseId: string | null = null;
    for (const h of net.houses) {
      const dx = personPos.x - h.x;
      const dy = personPos.y - h.y;
      if (dx * dx + dy * dy < 40 * 40) { near = true; buildingType = h.type ?? 'house'; houseId = h.id; break; }
    }
    if (!near) return;
    gameMode = 'inside';
    enteredBuildingType = buildingType;
    currentHouseId = houseId;
    currentSchoolRoom = 'hall';
    currentHouseRoom = 'main';
    hotelFloor = 0;
    hotelRoomIdx = null;
    hotelFloorMenuOpen = false;
    indoorPos = { x: 0, y: 0 };
    enterHouseBtn.textContent = 'Gå ut';
    // These are free-standing buttons the ticker's indoor show/hide block
    // doesn't reach, and they were only ever hidden as a side effect of
    // redrawStaticWorld() happening to run while already indoors — so
    // entering a house could leave them visible and clickable. Hide them
    // outright and kill any in-progress build gesture.
    buildGateBtn.style.display = 'none';
    buildBridgeBtn.style.display = 'none';
    buildIslandBtn.style.display = 'none';
    setBuildMode('idle');
  } else if (gameMode === 'inside') {
    gameMode = 'ashore';
    currentHouseId = null;
    placingFurniture = null;
    hotelFloorMenuOpen = false;
    if (watchingMovie) {
      watchingMovie = false;
      movieOverlayGfx.clear();
      actionTimer = 0;
      peeOverlay.style.display = 'none';
    }
    enterHouseBtn.textContent = 'Gå in i huset';
    // Restore, respecting the same special-name gating used at load time.
    if (isSpecialName(playerName)) {
      buildGateBtn.style.display = '';
      buildIslandBtn.style.display = '';
    }
    buildBridgeBtn.style.display = '';
  }
});

// ---- IKEA & food shop ----
const ikeaPanel = document.getElementById('ikea-panel')!;
const matbutikPanel = document.getElementById('matbutik-panel')!;
const djuraffarPanel = document.getElementById('djuraffar-panel')!;
const restaurangPanel = document.getElementById('restaurang-panel')!;
const blomsteraffarPanel = document.getElementById('blomsteraffar-panel')!;
const elektronikbutikPanel = document.getElementById('elektronikbutik-panel')!;
const furnishToggleBtn = document.getElementById('furnish-toggle-btn')!;
const furnishMenu = document.getElementById('furnish-menu')!;
let furnishMenuOpen = false;

for (const btn of document.querySelectorAll<HTMLButtonElement>('.ikea-buy-btn')) {
  btn.addEventListener('click', () => {
    const price = Number(btn.dataset.price);
    if (myChests < price) return;
    myChests -= price;
    myFurniture.push({ id: crypto.randomUUID(), type: btn.dataset.type!, color: btn.dataset.color });
  });
}
for (const btn of document.querySelectorAll<HTMLButtonElement>('.food-buy-btn')) {
  btn.addEventListener('click', () => {
    const price = Number(btn.dataset.price);
    if (myChests < price) return;
    if (myInventory.length >= INVENTORY_SLOTS) {
      startAction('🎒 Väskan är full!', 60);
      return;
    }
    myChests -= price;
    addToInventory(myInventory, {
      subtype: 'food', label: btn.dataset.label ?? 'Mat', emoji: btn.dataset.emoji ?? '🍽️',
    });
  });
}

// ---- "New version available" banner: shown once the server has restarted
// (redeployed) since we first connected — see net.ts's updateAvailable. ----
const updateBanner = document.getElementById('update-banner')!;
let updateBannerShown = false;
updateBanner.addEventListener('click', () => location.reload());

// ---- Item inventory ----
const inventoryToggleBtn = document.getElementById('inventory-toggle-btn')!;
const inventoryMenu = document.getElementById('inventory-menu')!;
let inventoryMenuOpen = false;

inventoryToggleBtn.addEventListener('click', () => {
  inventoryMenuOpen = !inventoryMenuOpen;
  if (inventoryMenuOpen) renderInventoryMenu();
  inventoryMenu.style.display = inventoryMenuOpen ? 'flex' : 'none';
});

/** The fridge furniture item you're currently standing next to, if any. */
type FoodHolder = { id: string; type: string; color?: string; food?: { id: string; subtype: string; label: string; emoji: string }[] };

/** Looks up a furniture item (fridge or table) by id in the current house. */
function findFurniture(id: string | null): FoodHolder | undefined {
  if (!currentHouseId || !id) return undefined;
  const house = net.houses.find(h => h.id === currentHouseId);
  return house?.furniture?.find(f => f.id === id) as FoodHolder | undefined;
}
function currentFridge(): FoodHolder | undefined { return findFurniture(nearFridgeId); }
function currentTable(): FoodHolder | undefined { return findFurniture(nearTableId); }

function renderInventoryMenu(): void {
  inventoryMenu.innerHTML = '';
  for (const item of myInventory) {
    const row = document.createElement('div');
    row.style.cssText = 'display:flex; gap:5px;';
    const eatBtn2 = document.createElement('button');
    eatBtn2.textContent = `${item.emoji} ${item.label} — ät`;
    eatBtn2.style.cssText = `font-size:0.8rem; padding:6px 12px; white-space:nowrap;
      background:rgba(90,60,30,0.92); color:#ffe0a0; border:2px solid #c8a050;
      border-radius:8px; cursor:pointer; font-family:system-ui,sans-serif; font-weight:600;`;
    eatBtn2.addEventListener('click', () => {
      if (actionTimer > 0) return;
      removeFromInventory(myInventory, item.id);
      startAction('🍽️ Käkar gott...', 120);
      feedHunger();
      inventoryMenuOpen = false;
      inventoryMenu.style.display = 'none';
    });
    row.appendChild(eatBtn2);
    if (gameMode === 'ashore') {
      const dropBtn = document.createElement('button');
      dropBtn.textContent = '📍 Släpp';
      dropBtn.style.cssText = `font-size:0.8rem; padding:6px 10px; white-space:nowrap;
        background:rgba(90,90,90,0.92); color:#f0f0f0; border:2px solid #b0b0b0;
        border-radius:8px; cursor:pointer; font-family:system-ui,sans-serif; font-weight:600;`;
      dropBtn.addEventListener('click', () => {
        net.dropItem(personPos.x, personPos.y, { subtype: item.subtype, label: item.label, emoji: item.emoji });
        removeFromInventory(myInventory, item.id);
        inventoryMenuOpen = false;
        inventoryMenu.style.display = 'none';
      });
      row.appendChild(dropBtn);
    }
    if (nearFridge && currentHouseId && nearFridgeId) {
      const houseId = currentHouseId;
      const fridgeId = nearFridgeId;
      const storeBtn = document.createElement('button');
      storeBtn.textContent = '🧊 Lägg in';
      storeBtn.style.cssText = `font-size:0.8rem; padding:6px 10px; white-space:nowrap;
        background:rgba(30,90,110,0.92); color:#d8f8ff; border:2px solid #40b0c8;
        border-radius:8px; cursor:pointer; font-family:system-ui,sans-serif; font-weight:600;`;
      storeBtn.addEventListener('click', () => {
        if ((currentFridge()?.food?.length ?? 0) >= FRIDGE_SLOTS) {
          startAction('🧊 Kylskåpet är fullt!', 60);
          return;
        }
        net.storeFood(houseId, fridgeId, { subtype: item.subtype, label: item.label, emoji: item.emoji });
        removeFromInventory(myInventory, item.id);
        inventoryMenuOpen = false;
        inventoryMenu.style.display = 'none';
      });
      row.appendChild(storeBtn);
    }
    if (nearTable && currentHouseId && nearTableId) {
      const houseId = currentHouseId;
      const tableId = nearTableId;
      const serveBtn = document.createElement('button');
      serveBtn.textContent = '🍽️ Lägg på bordet';
      serveBtn.style.cssText = `font-size:0.8rem; padding:6px 10px; white-space:nowrap;
        background:rgba(150,90,30,0.92); color:#ffecc0; border:2px solid #e0a050;
        border-radius:8px; cursor:pointer; font-family:system-ui,sans-serif; font-weight:600;`;
      serveBtn.addEventListener('click', () => {
        if ((currentTable()?.food?.length ?? 0) >= TABLE_SLOTS) {
          startAction('🍽️ Bordet är fullt!', 60);
          return;
        }
        net.storeFood(houseId, tableId, { subtype: item.subtype, label: item.label, emoji: item.emoji });
        removeFromInventory(myInventory, item.id);
        inventoryMenuOpen = false;
        inventoryMenu.style.display = 'none';
      });
      row.appendChild(serveBtn);
    }
    inventoryMenu.appendChild(row);
  }
}

function renderFridgeMenu(): void {
  fridgeMenu.innerHTML = '';
  const food = currentFridge()?.food ?? [];
  if (food.length === 0) {
    const p = document.createElement('div');
    p.textContent = 'Tomt';
    p.style.cssText = `font-size:0.8rem; padding:6px 12px; color:#d8f8ff;
      background:rgba(30,80,100,0.85); border-radius:8px; font-family:system-ui,sans-serif;`;
    fridgeMenu.appendChild(p);
    return;
  }
  for (const item of food) {
    const b = document.createElement('button');
    b.textContent = `${item.emoji} ${item.label} — ta ut`;
    b.style.cssText = `font-size:0.8rem; padding:6px 12px; white-space:nowrap;
      background:rgba(30,90,110,0.92); color:#d8f8ff; border:2px solid #40b0c8;
      border-radius:8px; cursor:pointer; font-family:system-ui,sans-serif; font-weight:600;`;
    b.addEventListener('click', () => {
      if (!currentHouseId || !nearFridgeId) return;
      if (myInventory.length >= INVENTORY_SLOTS) {
        startAction('🎒 Väskan är full!', 60);
        return;
      }
      net.takeFood(currentHouseId, nearFridgeId, item.id);
      addToInventory(myInventory, { subtype: item.subtype, label: item.label, emoji: item.emoji });
      fridgeMenuOpen = false;
      fridgeMenu.style.display = 'none';
    });
    fridgeMenu.appendChild(b);
  }
}

/** Food someone served on a table: eaten right there by whoever clicks it —
 *  no inventory round-trip, since the whole point is other players sharing
 *  what one player cooked. */
function renderTableMenu(): void {
  tableMenu.innerHTML = '';
  const food = currentTable()?.food ?? [];
  if (food.length === 0) {
    const p = document.createElement('div');
    p.textContent = 'Tomt';
    p.style.cssText = `font-size:0.8rem; padding:6px 12px; color:#ffecc0;
      background:rgba(110,70,20,0.85); border-radius:8px; font-family:system-ui,sans-serif;`;
    tableMenu.appendChild(p);
    return;
  }
  for (const item of food) {
    const b = document.createElement('button');
    b.textContent = `${item.emoji} ${item.label} — ät`;
    b.style.cssText = `font-size:0.8rem; padding:6px 12px; white-space:nowrap;
      background:rgba(150,90,30,0.92); color:#ffecc0; border:2px solid #e0a050;
      border-radius:8px; cursor:pointer; font-family:system-ui,sans-serif; font-weight:600;`;
    b.addEventListener('click', () => {
      if (!currentHouseId || !nearTableId || actionTimer > 0) return;
      net.takeFood(currentHouseId, nearTableId, item.id);
      startAction('🍽️ Käkar gott...', 120);
      feedHunger();
      tableMenuOpen = false;
      tableMenu.style.display = 'none';
    });
    tableMenu.appendChild(b);
  }
}

furnishToggleBtn.addEventListener('click', () => {
  furnishMenuOpen = !furnishMenuOpen;
  if (furnishMenuOpen) renderFurnishMenu();
  furnishMenu.style.display = furnishMenuOpen ? 'flex' : 'none';
});

function renderFurnishMenu(): void {
  furnishMenu.innerHTML = '';
  for (const item of myFurniture) {
    const b = document.createElement('button');
    b.textContent = FURNITURE_LABELS[item.type] ?? item.type;
    b.style.cssText = `font-size:0.8rem; padding:6px 12px; white-space:nowrap;
      background:${item.color ?? 'rgba(90,60,30,0.92)'}; color:#fff; border:2px solid #fff8;
      border-radius:8px; cursor:pointer; font-family:system-ui,sans-serif; font-weight:600;`;
    b.addEventListener('click', () => {
      placingFurniture = item;
      furnishMenuOpen = false;
      furnishMenu.style.display = 'none';
    });
    furnishMenu.appendChild(b);
  }
}

// ---- Hotel elevator ----
function goToHotelFloor(floor: number): void {
  hotelFloor = floor;
  hotelRoomIdx = null;
  indoorPos = { x: 0, y: 0 };
  hotelFloorMenuOpen = false;
  hotelFloorMenu.style.display = 'none';
}

function renderHotelFloorMenu(): void {
  hotelFloorMenu.innerHTML = '';
  const addFloorBtn = (floor: number, label: string) => {
    const b = document.createElement('button');
    b.textContent = label;
    b.style.cssText = `font-size:0.8rem; padding:6px 12px; white-space:nowrap;
      background:rgba(60,60,60,0.92); color:#ffecc0; border:2px solid #c8a060;
      border-radius:8px; cursor:pointer; font-family:system-ui,sans-serif; font-weight:600;`;
    b.addEventListener('click', () => goToHotelFloor(floor));
    hotelFloorMenu.appendChild(b);
  };
  addFloorBtn(0, '🛎️ Reception');
  for (let f = 1; f <= HOTEL_FLOORS; f++) addFloorBtn(f, `Våning ${f}`);
}

hotelActionBtn.addEventListener('click', () => {
  if (actionTimer > 0) return;
  if (nearElevator) {
    hotelFloorMenuOpen = !hotelFloorMenuOpen;
    if (hotelFloorMenuOpen) renderHotelFloorMenu();
    hotelFloorMenu.style.display = hotelFloorMenuOpen ? 'flex' : 'none';
  } else if (nearHotelDoor !== null) {
    hotelRoomIdx = nearHotelDoor;
    indoorPos = { x: 0, y: 0 };
  } else if (nearRoomExit) {
    hotelRoomIdx = null;
    indoorPos = { x: 0, y: 0 };
  }
});

cinemaActionBtn.addEventListener('click', () => {
  if (actionTimer > 0 || !nearScreen || watchingMovie) return;
  currentMovieIdx = Math.floor(Math.random() * MOVIE_VIGNETTES.length);
  watchingMovie = true;
  startAction(`🎬 ${MOVIE_VIGNETTES[currentMovieIdx]}`, MOVIE_DURATION_FRAMES);
});

function isLandingBlocked(from: Vec2, island: Island): boolean {
  if (isSpecialName(playerName) || myChests >= 10000) return false;
  const landing = findLandingPosition(from, island);
  for (const g of net.gates) {
    if (segmentsIntersect(from, landing, { x: g.x1, y: g.y1 }, { x: g.x2, y: g.y2 })) return true;
  }
  return false;
}

ashoreBtn.addEventListener('click', () => {
  if (gameMode === 'sailing') {
    // Go ashore — bring boat animal with you
    const island = findNearestIsland(boatPos, getAllIslands(), 200);
    if (!island) return;
    if (isLandingBlocked(boatPos, island)) return;
    gameMode = 'ashore';
    currentIsland = island;
    personPos = findLandingPosition(boatPos, island);
    boat.setPersonVisible(false);
    speed = 0;
    if (boatAnimalIdx !== null) {
      heldAnimalIdx = boatAnimalIdx;
      boatAnimalIdx = null;
    }
  } else {
    // Board boat — only if near the boat
    const dx = personPos.x - boatPos.x;
    const dy = personPos.y - boatPos.y;
    if (dx * dx + dy * dy > 60 * 60) return; // too far
    gameMode = 'sailing';
    currentIsland = null;
    boat.setPersonVisible(true);
    ashoreGfx.clear();
    ashoreBtn.style.display = 'none';
    // Bring held animal onto boat
    if (heldAnimalIdx !== null) {
      boatAnimalIdx = heldAnimalIdx;
      heldAnimalIdx = null;
    }
  }
});

// Wind angle — authoritative value comes from the server
let windAngle = 0.15;

/** Find the sail angle that maximises thrust for the given wind/boat combo.
 *  Falls back to wind direction (sail streams like a flag) when no thrust is possible. */
function optimalSailAngle(windDir: Vec2, boatDir: Vec2): number {
  let bestThrust = 0;
  let bestAngle  = 0;
  // Sweep 36 candidate angles (every 10°)
  for (let i = 0; i < 36; i++) {
    const a   = (i / 36) * Math.PI * 2;
    const sd  = angleToDir(a);
    const { thrust } = computeSailing({ windDir, boatDir, sailDir: sd });
    if (thrust > bestThrust) {
      bestThrust = thrust;
      bestAngle  = a;
    }
  }
  // No thrust possible (no-go zone): let sail stream downwind like a flag
  if (bestThrust < 0.001) return dirToAngle(windDir);
  return bestAngle;
}

// ---- Building drawing ----
interface BuildingStyle {
  w: number; ht: number; roof: number;
  wall: number; wallStroke: number;
  roofColor: number; roofStroke: number;
  door: number;
  label?: string; labelColor?: number;
}

const BUILDING_STYLES: Record<string, BuildingStyle> = {
  house:      { w: 22, ht: 16, roof: 12, wall: 0xc8a060, wallStroke: 0x7a5020, roofColor: 0xb03020, roofStroke: 0x701810, door: 0x7a5020 },
  preschool:  { w: 26, ht: 15, roof: 10, wall: 0xf5c8a0, wallStroke: 0xc08050, roofColor: 0xf09060, roofStroke: 0xb05020, door: 0x805030, label: 'F', labelColor: 0x603010 },
  school:     { w: 38, ht: 22, roof: 14, wall: 0xb04840, wallStroke: 0x703028, roofColor: 0x703028, roofStroke: 0x401810, door: 0x401810, label: 'S', labelColor: 0xffe8c0 },
  studenten:  { w: 34, ht: 28, roof: 16, wall: 0x8a9eb0, wallStroke: 0x506070, roofColor: 0x506070, roofStroke: 0x2a3850, door: 0x2a3850, label: 'U', labelColor: 0xffffff },
  toilet:     { w: 14, ht: 14, roof: 7,  wall: 0x4a88b0, wallStroke: 0x204060, roofColor: 0x204060, roofStroke: 0x102030, door: 0x102030 },
  hotel:      { w: 40, ht: 30, roof: 16, wall: 0xd8b878, wallStroke: 0x9a7838, roofColor: 0x9a5838, roofStroke: 0x603018, door: 0x603018, label: 'H', labelColor: 0xffe8c0 },
  ikea:       { w: 30, ht: 20, roof: 6,  wall: 0x2870c0, wallStroke: 0x184880, roofColor: 0xffd800, roofStroke: 0xc0a000, door: 0x184880, label: 'IKEA', labelColor: 0xffd800 },
  matbutik:   { w: 26, ht: 18, roof: 10, wall: 0xe0e0d8, wallStroke: 0xa0a090, roofColor: 0xc84040, roofStroke: 0x902020, door: 0x902020, label: 'MAT', labelColor: 0xffffff },
  cinema:     { w: 36, ht: 24, roof: 8,  wall: 0x4a1828, wallStroke: 0x2a0c16, roofColor: 0x901830, roofStroke: 0x500a18, door: 0x2a0c16, label: 'BIO', labelColor: 0xffe060 },
  djuraffar:       { w: 26, ht: 17, roof: 9,  wall: 0x8ab060, wallStroke: 0x5a8030, roofColor: 0x5a8030, roofStroke: 0x3a5820, door: 0x3a5020, label: 'PET', labelColor: 0xffffff },
  restaurang:      { w: 30, ht: 20, roof: 10, wall: 0xf0e0c0, wallStroke: 0xc0a870, roofColor: 0xc84030, roofStroke: 0x802818, door: 0x802818, label: 'R', labelColor: 0xffffff },
  blomsteraffar:   { w: 24, ht: 16, roof: 9,  wall: 0xe8f0e0, wallStroke: 0xa0c090, roofColor: 0x60a050, roofStroke: 0x305020, door: 0x305020, label: 'FLOR', labelColor: 0xffffff },
  elektronikbutik: { w: 28, ht: 19, roof: 7,  wall: 0x304858, wallStroke: 0x182430, roofColor: 0x1c2c38, roofStroke: 0x101820, door: 0x101820, label: 'EL', labelColor: 0x60e0ff },
  bibliotek:       { w: 32, ht: 22, roof: 12, wall: 0x9a7848, wallStroke: 0x5a3820, roofColor: 0x5a3820, roofStroke: 0x3a2010, door: 0x3a2010, label: 'B', labelColor: 0xffe8c0 },
  gym:             { w: 30, ht: 20, roof: 8,  wall: 0x606870, wallStroke: 0x383e44, roofColor: 0xe07020, roofStroke: 0xa05010, door: 0x303840, label: 'GYM', labelColor: 0xffffff },
};

function drawBuilding(g: Graphics, x: number, y: number, type: string): void {
  const s = BUILDING_STYLES[type] ?? BUILDING_STYLES.house;
  const { w, ht, roof } = s;
  // Wall
  g.rect(x - w / 2, y - ht, w, ht);
  g.fill({ color: s.wall });
  g.rect(x - w / 2, y - ht, w, ht);
  g.stroke({ color: s.wallStroke, width: 1 });
  // Roof
  g.moveTo(x - w / 2 - 3, y - ht);
  g.lineTo(x, y - ht - roof);
  g.lineTo(x + w / 2 + 3, y - ht);
  g.closePath();
  g.fill({ color: s.roofColor });
  g.stroke({ color: s.roofStroke, width: 1 });
  // Door
  const doorW = Math.min(8, w * 0.25);
  g.rect(x - doorW / 2, y - ht * 0.55, doorW, ht * 0.55);
  g.fill({ color: s.door });
  // Windows — give each building type some visual variation
  if (type === 'preschool') {
    // Three small round playful windows
    const wy = y - ht * 0.7;
    for (let i = -1; i <= 1; i++) {
      g.circle(x + i * (w * 0.28), wy, 1.8);
      g.fill({ color: 0xfff8dd });
    }
  } else if (type === 'school') {
    // Two rows of rectangular windows
    for (let row = 0; row < 2; row++) {
      const wy = y - ht * (0.75 - row * 0.3);
      for (let i = -2; i <= 2; i++) {
        g.rect(x + i * (w * 0.18) - 1.5, wy - 1.5, 3, 3);
        g.fill({ color: 0xfff8dd });
      }
    }
  } else if (type === 'studenten') {
    // Large arched window near top + row of narrow windows
    g.rect(x - 4, y - ht * 0.85, 8, 6);
    g.fill({ color: 0xfff8dd });
    for (let i = -3; i <= 3; i++) {
      g.rect(x + i * (w * 0.13) - 0.8, y - ht * 0.45, 1.6, 4);
      g.fill({ color: 0xfff8dd });
    }
  } else if (type === 'toilet') {
    // "WC" style marker — a white bar on the door area
    g.rect(x - 4, y - ht * 0.6, 8, 3);
    g.fill({ color: 0xffffff });
  } else if (type === 'hotel') {
    // Multiple floors of little windows
    for (let row = 0; row < 3; row++) {
      const wy = y - ht * (0.85 - row * 0.22);
      for (let i = -2; i <= 2; i++) {
        g.rect(x + i * (w * 0.17) - 1.2, wy - 1.2, 2.4, 2.4);
        g.fill({ color: 0xfff8dd });
      }
    }
  } else if (type === 'ikea') {
    // Big yellow-blue signboard window
    g.rect(x - w * 0.3, y - ht * 0.75, w * 0.6, ht * 0.3);
    g.fill({ color: 0xfff8dd });
  } else if (type === 'matbutik') {
    // Striped awning over the door
    for (let i = -2; i <= 2; i++) {
      g.rect(x + i * (w * 0.14) - 2, y - ht * 0.62, 3, ht * 0.12);
      g.fill({ color: i % 2 === 0 ? 0xffffff : 0xc84040 });
    }
  } else if (type === 'cinema') {
    // Marquee sign with bulbs, plus a poster box on the facade
    g.rect(x - w * 0.4, y - ht * 0.95, w * 0.8, ht * 0.22);
    g.fill({ color: 0x281016 });
    g.rect(x - w * 0.4, y - ht * 0.95, w * 0.8, ht * 0.22);
    g.stroke({ color: 0xffe060, width: 1 });
    for (let i = -3; i <= 3; i++) {
      g.circle(x + i * (w * 0.11), y - ht * 0.84, 1.2);
      g.fill({ color: 0xffe060 });
    }
    g.rect(x - w * 0.22, y - ht * 0.55, w * 0.16, ht * 0.28);
    g.fill({ color: 0xfff8dd });
  } else if (type === 'djuraffar') {
    // Paw-print decal on the facade
    g.circle(x, y - ht * 0.62, 2.4);
    g.fill({ color: 0xfff8dd });
    for (const [dx, dy] of [[-2.6, -1.4], [2.6, -1.4], [-1.6, -4], [1.6, -4]] as Array<[number, number]>) {
      g.circle(x + dx, y - ht * 0.62 + dy, 1.1);
      g.fill({ color: 0xfff8dd });
    }
  } else if (type === 'restaurang') {
    // Striped awning + a round window
    for (let i = -2; i <= 2; i++) {
      g.rect(x + i * (w * 0.16) - 2, y - ht * 0.6, 3, ht * 0.12);
      g.fill({ color: i % 2 === 0 ? 0xffffff : 0xc84030 });
    }
    g.circle(x, y - ht * 0.8, 2.2);
    g.fill({ color: 0xfff8dd });
  } else if (type === 'blomsteraffar') {
    // A little box of flowers under the window
    g.rect(x - w * 0.28, y - ht * 0.5, w * 0.56, ht * 0.14);
    g.fill({ color: 0x7a5a30 });
    for (let i = -2; i <= 2; i++) {
      g.circle(x + i * (w * 0.11), y - ht * 0.56, 1.4);
      g.fill({ color: [0xf0c020, 0xffffff, 0xe86030, 0xf0c020, 0xffffff][i + 2] });
    }
  } else if (type === 'elektronikbutik') {
    // Grid of glowing screens in the window
    for (let row = 0; row < 2; row++) {
      for (let i = -1; i <= 1; i++) {
        g.rect(x + i * (w * 0.22) - 2.4, y - ht * (0.78 - row * 0.2), 4.8, 3.2);
        g.fill({ color: 0x60e0ff });
      }
    }
  } else if (type === 'bibliotek') {
    // Rows of book spines visible through a big window
    g.rect(x - w * 0.32, y - ht * 0.8, w * 0.64, ht * 0.35);
    g.fill({ color: 0xfff8dd });
    for (let i = 0; i < 9; i++) {
      g.rect(x - w * 0.3 + i * (w * 0.64 / 9), y - ht * 0.78, w * 0.64 / 9 - 0.6, ht * 0.31);
      g.fill({ color: [0x8a5a30, 0x5a7830, 0x305880, 0xa04830, 0x8a5a30][i % 5] });
    }
  } else if (type === 'gym') {
    // Dumbbell decal on the facade
    const dy = y - ht * 0.6;
    g.rect(x - 5, dy - 1, 10, 2);
    g.fill({ color: 0xffd8a0 });
    g.rect(x - 6, dy - 2.4, 2.4, 4.8);
    g.fill({ color: 0xffd8a0 });
    g.rect(x + 3.6, dy - 2.4, 2.4, 4.8);
    g.fill({ color: 0xffd8a0 });
  }
}

// ---- Indoor room drawing ----
function drawWoodFloor(g: Graphics, x0: number, y0: number, w: number, h: number) {
  g.rect(x0, y0, w, h);
  g.fill({ color: 0xd4a878 });
  const plankStep = 28;
  for (let px = x0 + plankStep; px < x0 + w; px += plankStep) {
    g.moveTo(px, y0); g.lineTo(px, y0 + h);
  }
  g.stroke({ color: 0x8a5a30, width: 1.5 });
  for (let px = x0 + plankStep / 2; px < x0 + w; px += plankStep) {
    for (let py = y0 + 40; py < y0 + h - 20; py += 80) {
      g.moveTo(px - 4, py); g.lineTo(px + 4, py);
    }
  }
  g.stroke({ color: 0x8a5a30, width: 1, alpha: 0.6 });
}

function drawRoomWalls(g: Graphics, x0: number, y0: number, w: number, h: number) {
  g.rect(x0, y0, w, h);
  g.stroke({ color: 0x5a3a18, width: 3 });
}

function drawStair(g: Graphics, sx: number, sy: number) {
  // Drawn as a short staircase viewed top-down: stacked planks.
  const w = 44, h = 34;
  g.rect(sx - w / 2, sy - h / 2, w, h);
  g.fill({ color: 0x8a6a40 });
  g.rect(sx - w / 2, sy - h / 2, w, h);
  g.stroke({ color: 0x4a3018, width: 1.5 });
  for (let i = 0; i < 5; i++) {
    const y = sy - h / 2 + 4 + i * ((h - 8) / 5);
    g.moveTo(sx - w / 2 + 4, y);
    g.lineTo(sx + w / 2 - 4, y);
  }
  g.stroke({ color: 0x4a3018, width: 1 });
  // Arrow hint
  g.moveTo(sx, sy + 3); g.lineTo(sx - 4, sy - 3); g.lineTo(sx + 4, sy - 3); g.closePath();
  g.fill({ color: 0xffe0a0 });
}

function drawStairsFor(g: Graphics, cx: number, cy: number, room: RoomKey) {
  for (const s of STAIRS_BY_ROOM[room]) drawStair(g, cx + s.rx, cy + s.ry);
}

function drawMatsal(g: Graphics, x0: number, y0: number, w: number, h: number) {
  drawWoodFloor(g, x0, y0, w, h);
  // Serving counter along the top wall
  g.rect(x0 + 30, y0 + 12, w - 60, 14);
  g.fill({ color: 0xd0d0d0 });
  g.rect(x0 + 30, y0 + 12, w - 60, 14);
  g.stroke({ color: 0x707070, width: 1.2 });
  // Food trays on counter
  for (let i = 0; i < 5; i++) {
    const tx = x0 + 50 + i * ((w - 100) / 4);
    g.rect(tx - 8, y0 + 15, 16, 8);
    g.fill({ color: i % 2 === 0 ? 0xf0a040 : 0x60a070 });
    g.rect(tx - 8, y0 + 15, 16, 8);
    g.stroke({ color: 0x504030, width: 0.6 });
  }
  // 2 long dining tables with stools along each side
  for (let row = 0; row < 2; row++) {
    const ty = y0 + 80 + row * 90;
    const tx = x0 + 50;
    const tw = w - 100, thh = 22;
    g.rect(tx, ty, tw, thh);
    g.fill({ color: 0xd0a070 });
    g.rect(tx, ty, tw, thh);
    g.stroke({ color: 0x7a5020, width: 1.5 });
    // Centerpiece (plate + flower)
    for (let cx = tx + 25; cx < tx + tw - 15; cx += 60) {
      g.circle(cx, ty + thh / 2, 3); g.fill({ color: 0xffffff });
      g.circle(cx, ty + thh / 2, 3); g.stroke({ color: 0x808080, width: 0.4 });
    }
    // Flower vase in middle
    const vx = tx + tw / 2;
    g.rect(vx - 2, ty + 6, 4, 10); g.fill({ color: 0x80c0f0 });
    g.circle(vx, ty + 5, 3); g.fill({ color: 0xf0c020 });
    // Stools above + below
    for (let sx = tx + 18; sx < tx + tw - 10; sx += 34) {
      g.circle(sx, ty - 10, 5); g.fill({ color: 0x8a5020 });
      g.circle(sx, ty - 10, 5); g.stroke({ color: 0x4a3018, width: 0.8 });
      g.circle(sx, ty + thh + 10, 5); g.fill({ color: 0x8a5020 });
      g.circle(sx, ty + thh + 10, 5); g.stroke({ color: 0x4a3018, width: 0.8 });
    }
  }
  drawStairsFor(g, x0 + w / 2, y0 + h / 2, 'matsal');
  drawRoomWalls(g, x0, y0, w, h);
}

function drawKlassrum(g: Graphics, x0: number, y0: number, w: number, h: number) {
  // Pale grey linoleum floor with subtle tile pattern
  g.rect(x0, y0, w, h);
  g.fill({ color: 0xe0ddd6 });
  const tile = 40;
  for (let px = x0 + tile; px < x0 + w; px += tile) {
    g.moveTo(px, y0); g.lineTo(px, y0 + h);
  }
  for (let py = y0 + tile; py < y0 + h; py += tile) {
    g.moveTo(x0, py); g.lineTo(x0 + w, py);
  }
  g.stroke({ color: 0xc8c5be, width: 0.6 });
  // Blackboard at the top with frame
  g.rect(x0 + 40, y0 + 8, w - 80, 32);
  g.fill({ color: 0x5a3a18 });
  g.rect(x0 + 46, y0 + 12, w - 92, 24);
  g.fill({ color: 0x1a3020 });
  g.rect(x0 + 46, y0 + 12, w - 92, 24);
  g.stroke({ color: 0x0a1810, width: 1 });
  // Chalk writing
  g.moveTo(x0 + 60, y0 + 20); g.lineTo(x0 + w - 100, y0 + 20);
  g.moveTo(x0 + 60, y0 + 26); g.lineTo(x0 + w - 130, y0 + 26);
  g.moveTo(x0 + 60, y0 + 32); g.lineTo(x0 + w - 110, y0 + 32);
  g.stroke({ color: 0xffffff, width: 1.2, alpha: 0.75 });
  // World map posters on side walls
  g.rect(x0 + 8, y0 + 55, 14, 30);
  g.fill({ color: 0xb8d8f0 });
  g.rect(x0 + 8, y0 + 55, 14, 30);
  g.stroke({ color: 0x406080, width: 0.8 });
  // Tiny continents on map
  g.ellipse(x0 + 13, y0 + 65, 3, 2);
  g.ellipse(x0 + 17, y0 + 75, 2, 3);
  g.fill({ color: 0x70a060 });
  g.rect(x0 + w - 22, y0 + 55, 14, 30);
  g.fill({ color: 0xf0e8c0 });
  g.rect(x0 + w - 22, y0 + 55, 14, 30);
  g.stroke({ color: 0x806040, width: 0.8 });
  // Teacher's desk
  g.rect(x0 + w / 2 - 22, y0 + 54, 44, 18);
  g.fill({ color: 0xb08060 });
  g.rect(x0 + w / 2 - 22, y0 + 54, 44, 18);
  g.stroke({ color: 0x704020, width: 1 });
  // Apple on teacher's desk
  g.circle(x0 + w / 2 + 14, y0 + 63, 2);
  g.fill({ color: 0xe03030 });
  // Rows of student desks with chairs (4 rows of 4)
  const cols = 4, rows = 3;
  const startX = x0 + 60;
  const startY = y0 + 110;
  const gapX = (w - 120) / (cols - 1);
  const gapY = (h - 160) / (rows - 1);
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const dx = startX + c * gapX - 12;
      const dy = startY + r * gapY - 8;
      // Desk
      g.rect(dx, dy, 24, 16);
      g.fill({ color: 0xc89868 });
      g.rect(dx, dy, 24, 16);
      g.stroke({ color: 0x7a5020, width: 1 });
      // Book on desk
      g.rect(dx + 4, dy + 3, 10, 6);
      g.fill({ color: (r + c) % 2 === 0 ? 0xc03040 : 0x3060b0 });
      // Pencil on desk
      g.rect(dx + 16, dy + 4, 6, 1.5);
      g.fill({ color: 0xffcc00 });
      // Chair behind (below)
      g.rect(dx + 6, dy + 18, 12, 8);
      g.fill({ color: 0x6a4020 });
      g.rect(dx + 6, dy + 18, 12, 8);
      g.stroke({ color: 0x4a2818, width: 0.6 });
    }
  }
  drawStairsFor(g, x0 + w / 2, y0 + h / 2, 'klassrum');
  drawRoomWalls(g, x0, y0, w, h);
}

function drawDoor(g: Graphics, x: number, y: number) {
  // Door on the left wall — oriented so the hinge sits on the wall.
  g.rect(x - 5, y - 18, 10, 36);
  g.fill({ color: 0x8a5a20 });
  g.rect(x - 5, y - 18, 10, 36);
  g.stroke({ color: 0x4a3010, width: 1.2 });
  // Door panels
  g.rect(x - 3, y - 15, 6, 12);
  g.rect(x - 3, y + 3, 6, 12);
  g.stroke({ color: 0x4a3010, width: 0.6 });
  // Handle
  g.circle(x + 3, y, 1.5);
  g.fill({ color: 0xffd040 });
  // "Ut"-arrow hint
  g.moveTo(x - 10, y); g.lineTo(x - 6, y - 3); g.lineTo(x - 6, y + 3); g.closePath();
  g.fill({ color: 0xffe0a0 });
}

function drawHall(g: Graphics, x0: number, y0: number, w: number, h: number) {
  drawWoodFloor(g, x0, y0, w, h);
  // Exit door on the left wall (middle)
  drawDoor(g, x0, y0 + h / 2);
  // Long bench along the bottom wall
  g.rect(x0 + 20, y0 + h - 28, w - 40, 12);
  g.fill({ color: 0x7a5020 });
  g.rect(x0 + 20, y0 + h - 28, w - 40, 12);
  g.stroke({ color: 0x4a3010, width: 1 });
  // Potted plants in corners
  for (const [px, py] of [[x0 + 20, y0 + h - 60], [x0 + w - 20, y0 + h - 60]]) {
    g.rect(px - 6, py, 12, 12); g.fill({ color: 0x8a6030 });
    g.circle(px, py - 2, 8); g.fill({ color: 0x40a040 });
    g.circle(px - 3, py - 5, 4); g.fill({ color: 0x50b050 });
    g.circle(px + 3, py - 6, 4); g.fill({ color: 0x50b050 });
  }
  // Cork notice board on a side wall
  g.rect(x0 + 12, y0 + 100, 24, 36);
  g.fill({ color: 0xc89868 });
  g.rect(x0 + 12, y0 + 100, 24, 36);
  g.stroke({ color: 0x6a4020, width: 1.2 });
  // Paper notes on board
  for (const [nx, ny, c] of [[18, 108, 0xffff90], [26, 112, 0xe0f0ff], [16, 122, 0xc0ffe0], [28, 128, 0xfff0a0]] as Array<[number, number, number]>) {
    g.rect(x0 + nx, y0 + ny, 6, 5);
    g.fill({ color: c });
    g.rect(x0 + nx, y0 + ny, 6, 5);
    g.stroke({ color: 0x806040, width: 0.4 });
  }
  drawStairsFor(g, x0 + w / 2, y0 + h / 2, 'hall');
  drawRoomWalls(g, x0, y0, w, h);
}

function drawToalett(g: Graphics, x0: number, y0: number, w: number, h: number) {
  // White tile floor
  g.rect(x0, y0, w, h);
  g.fill({ color: 0xf0f4f8 });
  // Tile grid
  const tile = 30;
  for (let px = x0 + tile; px < x0 + w; px += tile) {
    g.moveTo(px, y0); g.lineTo(px, y0 + h);
  }
  for (let py = y0 + tile; py < y0 + h; py += tile) {
    g.moveTo(x0, py); g.lineTo(x0 + w, py);
  }
  g.stroke({ color: 0xc0c8d0, width: 1 });
  drawRoomWalls(g, x0, y0, w, h);
  // Row of sinks + mirrors along the left wall
  for (let i = 0; i < 3; i++) {
    const sx = x0 + 6;
    const sy = y0 + 30 + i * 60;
    // Mirror
    g.rect(sx, sy, 10, 14);
    g.fill({ color: 0xccddee });
    g.rect(sx, sy, 10, 14);
    g.stroke({ color: 0x607080, width: 0.8 });
    // Sink
    g.ellipse(sx + 5, sy + 24, 8, 5);
    g.fill({ color: 0xffffff });
    g.ellipse(sx + 5, sy + 24, 8, 5);
    g.stroke({ color: 0x607080, width: 0.8 });
    // Tap
    g.rect(sx + 4, sy + 18, 2, 4);
    g.fill({ color: 0x9090a0 });
  }
  // Ten toilet stalls arranged in two rows of five on the right side
  const cols = 5, rows = 2;
  const padL = 40;
  const padR = 16;
  const padT = 20;
  const padB = 20;
  const availW = w - padL - padR;
  const availH = h - padT - padB;
  const stallW = availW / cols - 4;
  const stallH = availH / rows - 10;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const sx = x0 + padL + c * (stallW + 4);
      const sy = y0 + padT + r * (stallH + 10);
      // Stall walls
      g.rect(sx, sy, stallW, stallH);
      g.fill({ color: 0xe6ecf0 });
      g.rect(sx, sy, stallW, stallH);
      g.stroke({ color: 0x8090a0, width: 1.2 });
      // Door frame gap (hint at doors)
      g.moveTo(sx + 2, sy + stallH); g.lineTo(sx + stallW - 2, sy + stallH);
      g.stroke({ color: 0xa0b0c0, width: 1.5 });
      // Toilet tank
      const cx = sx + stallW / 2;
      const cy = sy + stallH / 2;
      g.rect(cx - 7, cy - 11, 14, 7);
      g.fill({ color: 0xffffff });
      g.rect(cx - 7, cy - 11, 14, 7);
      g.stroke({ color: 0x607080, width: 1 });
      // Flush button
      g.rect(cx - 2, cy - 9, 4, 2);
      g.fill({ color: 0xb0b0b0 });
      // Toilet seat (oval)
      g.ellipse(cx, cy + 2, 7, 5);
      g.fill({ color: 0xffffff });
      g.ellipse(cx, cy + 2, 7, 5);
      g.stroke({ color: 0x607080, width: 1 });
      // Bowl water
      g.ellipse(cx, cy + 2, 4, 2.5);
      g.fill({ color: 0xb0c8d8 });
      // Toilet paper roll on stall wall
      g.circle(sx + stallW - 4, cy + 2, 1.6);
      g.fill({ color: 0xffffff });
      g.circle(sx + stallW - 4, cy + 2, 1.6);
      g.stroke({ color: 0xa0a0a0, width: 0.5 });
    }
  }
  drawStairsFor(g, x0 + w / 2, y0 + h / 2, 'toalett');
}

function drawPreschoolRoom(g: Graphics, x0: number, y0: number, w: number, h: number) {
  // Soft pastel foam-mat floor in four coloured squares
  const halfW = w / 2, halfH = h / 2;
  const mats: [number, number, number][] = [
    [x0, y0, 0xffd6e4],
    [x0 + halfW, y0, 0xffe8b0],
    [x0, y0 + halfH, 0xc8e0ff],
    [x0 + halfW, y0 + halfH, 0xc8f0c8],
  ];
  for (const [mx, my, color] of mats) {
    g.rect(mx, my, halfW, halfH);
    g.fill({ color });
  }
  // Mat borders
  g.moveTo(x0 + halfW, y0); g.lineTo(x0 + halfW, y0 + h);
  g.moveTo(x0, y0 + halfH); g.lineTo(x0 + w, y0 + halfH);
  g.stroke({ color: 0x888888, width: 1.2, alpha: 0.5 });
  // Building blocks pile in top-left
  const bx = x0 + 30, by = y0 + 30;
  for (const [dx, dy, c] of [[0, 10, 0xe04040], [10, 10, 0x4080e0], [20, 10, 0xe0d040], [5, 0, 0x40b060], [15, 0, 0xe08030]] as Array<[number, number, number]>) {
    g.rect(bx + dx, by + dy, 9, 9); g.fill({ color: c });
    g.rect(bx + dx, by + dy, 9, 9); g.stroke({ color: 0x303030, width: 0.5 });
  }
  // Stuffed teddy in bottom-right
  const tx = x0 + w - 40, ty = y0 + h - 40;
  g.circle(tx, ty + 5, 8); g.fill({ color: 0xa87040 });   // body
  g.circle(tx, ty - 6, 6); g.fill({ color: 0xa87040 });   // head
  g.circle(tx - 5, ty - 11, 2.5); g.fill({ color: 0xa87040 });
  g.circle(tx + 5, ty - 11, 2.5); g.fill({ color: 0xa87040 });
  g.circle(tx - 2, ty - 6, 0.8); g.circle(tx + 2, ty - 6, 0.8); g.fill({ color: 0x111111 });
  // Round play rug in centre
  g.circle(x0 + w / 2, y0 + h / 2, 38);
  g.fill({ color: 0xffffff, alpha: 0.7 });
  g.circle(x0 + w / 2, y0 + h / 2, 38);
  g.stroke({ color: 0x80c0a0, width: 1.5 });
  g.circle(x0 + w / 2, y0 + h / 2, 26);
  g.stroke({ color: 0x80b0e0, width: 1.5 });
  g.circle(x0 + w / 2, y0 + h / 2, 14);
  g.stroke({ color: 0xe0c040, width: 1.5 });
  // Small table with crayons in top-right
  const dx = x0 + w - 70, dy = y0 + 30;
  g.rect(dx, dy, 40, 24); g.fill({ color: 0xf0b860 });
  g.rect(dx, dy, 40, 24); g.stroke({ color: 0x8a5020, width: 1 });
  for (let i = 0; i < 4; i++) {
    g.rect(dx + 4 + i * 8, dy + 6, 2, 10);
    g.fill({ color: [0xe04040, 0x40b060, 0x4080e0, 0xe0d040][i] });
  }
  // Low bookshelf left wall
  g.rect(x0 + 4, y0 + h - 70, 18, 50);
  g.fill({ color: 0xc88860 });
  g.rect(x0 + 4, y0 + h - 70, 18, 50);
  g.stroke({ color: 0x7a5020, width: 1 });
  for (let i = 0; i < 3; i++) {
    g.moveTo(x0 + 4, y0 + h - 70 + (i + 1) * 16);
    g.lineTo(x0 + 22, y0 + h - 70 + (i + 1) * 16);
  }
  g.stroke({ color: 0x7a5020, width: 0.8 });
  // Tiny books on the shelves
  for (let s = 0; s < 3; s++) {
    for (let b = 0; b < 3; b++) {
      const sx = x0 + 6 + b * 6;
      const sy = y0 + h - 70 + s * 16 + 4;
      g.rect(sx, sy, 4, 10);
      g.fill({ color: [0xe04040, 0x40b060, 0x4a90d9, 0xe0d040][(s + b) % 4] });
    }
  }
  drawRoomWalls(g, x0, y0, w, h);
}

function drawStudentenRoom(g: Graphics, x0: number, y0: number, w: number, h: number) {
  // Darker wood floor for a student union / pub feel
  g.rect(x0, y0, w, h);
  g.fill({ color: 0x6a4a28 });
  const plankStep = 32;
  for (let px = x0 + plankStep; px < x0 + w; px += plankStep) {
    g.moveTo(px, y0); g.lineTo(px, y0 + h);
  }
  g.stroke({ color: 0x3a2410, width: 1.2 });
  // Bar counter along the top wall
  const barY = y0 + 12, barH = 22;
  g.rect(x0 + 20, barY, w - 40, barH);
  g.fill({ color: 0x4a2a10 });
  g.rect(x0 + 20, barY, w - 40, barH);
  g.stroke({ color: 0x1a0a00, width: 1.2 });
  // Beer taps on the bar
  for (let i = 0; i < 4; i++) {
    const tx = x0 + 60 + i * 60;
    g.rect(tx - 2, barY + 4, 4, 12);
    g.fill({ color: 0xa0a0b0 });
    g.circle(tx, barY + 4, 3);
    g.fill({ color: 0xa0a0b0 });
  }
  // Bottles on a shelf behind the bar
  for (let i = 0; i < 8; i++) {
    const bx = x0 + 30 + i * ((w - 60) / 8);
    g.rect(bx, barY - 14, 6, 10);
    g.fill({ color: [0x40a040, 0x804020, 0xc8c8c8, 0xe0a040][i % 4] });
  }
  // Barstools in front of the bar
  for (let i = 0; i < 5; i++) {
    const sx = x0 + 40 + i * ((w - 80) / 4);
    g.circle(sx, barY + barH + 14, 6);
    g.fill({ color: 0x1a0a00 });
    g.circle(sx, barY + barH + 14, 6);
    g.stroke({ color: 0x503010, width: 0.8 });
    g.circle(sx, barY + barH + 14, 3);
    g.fill({ color: 0x804020 });
  }
  // Round tables with stools in the lower half
  for (const [tx, ty] of [[x0 + 80, y0 + h - 50], [x0 + w - 80, y0 + h - 50]] as [number, number][]) {
    g.circle(tx, ty, 18);
    g.fill({ color: 0x804020 });
    g.circle(tx, ty, 18);
    g.stroke({ color: 0x401800, width: 1.2 });
    // Beer glasses on table
    for (const ang of [0, 1.5, 3.1, 4.7]) {
      const gx = tx + Math.cos(ang) * 9;
      const gy = ty + Math.sin(ang) * 9;
      g.rect(gx - 2, gy - 3, 4, 6);
      g.fill({ color: 0xffd080 });
    }
    // Stools
    for (const ang of [0.8, 2.3, 3.9, 5.4]) {
      const sx = tx + Math.cos(ang) * 28;
      const sy = ty + Math.sin(ang) * 28;
      g.circle(sx, sy, 5);
      g.fill({ color: 0x603010 });
    }
  }
  // Dartboard on the left wall
  const dbx = x0 + 14, dby = y0 + h / 2 - 12;
  g.circle(dbx, dby, 10); g.fill({ color: 0xf5eedd });
  g.circle(dbx, dby, 10); g.stroke({ color: 0x302010, width: 1.2 });
  g.circle(dbx, dby, 6); g.stroke({ color: 0x302010, width: 0.8 });
  g.circle(dbx, dby, 3); g.fill({ color: 0xe02030 });
  // Neon-ish poster on the right wall
  g.rect(x0 + w - 22, y0 + 80, 14, 28);
  g.fill({ color: 0x4a90d9 });
  g.rect(x0 + w - 22, y0 + 80, 14, 28);
  g.stroke({ color: 0x203a70, width: 1 });
  drawRoomWalls(g, x0, y0, w, h);
}

function drawBibliotekRoom(g: Graphics, x0: number, y0: number, w: number, h: number): void {
  // Warm reading-room floor
  g.rect(x0, y0, w, h);
  g.fill({ color: 0xc8a878 });
  const plankStep = 28;
  for (let px = x0 + plankStep; px < x0 + w; px += plankStep) {
    g.moveTo(px, y0); g.lineTo(px, y0 + h);
  }
  g.stroke({ color: 0x8a6838, width: 1.2 });
  // A wall of bookshelves along the top
  for (let i = 0; i < 8; i++) {
    const bx = x0 + 30 + i * ((w - 60) / 7);
    drawFurnitureItem(g, bx, y0 + 34, 'bookshelf');
  }
  // Two more along the left wall
  drawFurnitureItem(g, x0 + 20, y0 + h / 2, 'bookshelf');
  drawFurnitureItem(g, x0 + 20, y0 + h - 60, 'bookshelf');
  // Reading tables with chairs, lower half of the room
  for (const [tx, ty] of [[x0 + w * 0.4, y0 + h - 70], [x0 + w * 0.72, y0 + h - 70]] as [number, number][]) {
    drawFurnitureItem(g, tx, ty, 'table');
    for (const dx of [-26, 26]) drawFurnitureItem(g, tx + dx, ty, 'chair');
    drawFurnitureItem(g, tx, ty - 10, 'desklamp');
  }
  drawRoomWalls(g, x0, y0, w, h);
}

function drawGymRoom(g: Graphics, x0: number, y0: number, w: number, h: number): void {
  // Rubber floor mats, laid out in a grid
  g.rect(x0, y0, w, h);
  g.fill({ color: 0x505860 });
  const tile = 34;
  for (let px = x0 + tile; px < x0 + w; px += tile) { g.moveTo(px, y0); g.lineTo(px, y0 + h); }
  for (let py = y0 + tile; py < y0 + h; py += tile) { g.moveTo(x0, py); g.lineTo(x0 + w, py); }
  g.stroke({ color: 0x383e44, width: 1 });
  // Mirror wall along the top
  g.rect(x0 + 20, y0 + 8, w - 40, 14);
  g.fill({ color: 0xbcdcec, alpha: 0.5 });
  g.rect(x0 + 20, y0 + 8, w - 40, 14);
  g.stroke({ color: 0x788890, width: 1.2 });
  // A rack of dumbbells along the left wall
  for (let i = 0; i < 5; i++) {
    const dy = y0 + 50 + i * 26;
    g.rect(x0 + 14, dy - 3, 24, 6);
    g.fill({ color: 0x282828 });
    g.rect(x0 + 12, dy - 6, 5, 12);
    g.fill({ color: 0x606060 });
    g.rect(x0 + 33, dy - 6, 5, 12);
    g.fill({ color: 0x606060 });
  }
  // Exercise mats scattered in the middle
  for (const [mx, my, rot] of [[w * 0.45, h * 0.4, 0], [w * 0.65, h * 0.6, 0.3], [w * 0.4, h * 0.72, -0.2]] as Array<[number, number, number]>) {
    g.rect(x0 + mx - 18, y0 + my - 10, 36, 20);
    g.fill({ color: [0xe07020, 0x4090c0, 0x50a050][Math.abs(Math.round(rot * 10)) % 3] });
    g.rect(x0 + mx - 18, y0 + my - 10, 36, 20);
    g.stroke({ color: 0x282828, width: 1 });
  }
  // A treadmill against the right wall
  const tx = x0 + w - 40, ty = y0 + h * 0.5;
  g.roundRect(tx - 14, ty - 26, 28, 52, 3);
  g.fill({ color: 0x303840 });
  g.roundRect(tx - 11, ty - 20, 22, 40, 2);
  g.fill({ color: 0x181c20 });
  g.rect(tx - 10, ty - 30, 20, 6);
  g.fill({ color: 0xe07020 });
  drawRoomWalls(g, x0, y0, w, h);
}

function drawRoom(g: Graphics, x0: number, y0: number, w: number, h: number, buildingType: string, room: RoomKey) {
  if (buildingType === 'school') {
    switch (room as SchoolRoom) {
      case 'matsal':   drawMatsal(g, x0, y0, w, h); return;
      case 'klassrum': drawKlassrum(g, x0, y0, w, h); return;
      case 'hall':     drawHall(g, x0, y0, w, h); return;
      case 'toalett':  drawToalett(g, x0, y0, w, h); return;
    }
  }
  if (buildingType === 'preschool') { drawPreschoolRoom(g, x0, y0, w, h); return; }
  if (buildingType === 'studenten') { drawStudentenRoom(g, x0, y0, w, h); return; }
  if (buildingType === 'bibliotek') { drawBibliotekRoom(g, x0, y0, w, h); return; }
  if (buildingType === 'gym') { drawGymRoom(g, x0, y0, w, h); return; }
  // Plain house: 4 rooms (living room/kitchen/bedroom/bathroom) reached via
  // the same hub-and-spoke door pattern as the school. Hotel has its own
  // multi-floor interior, drawn by drawHotelInterior() instead of this
  // function entirely.
  if (buildingType === 'house') {
    switch (room as HouseRoom) {
      case 'kok':    drawKok(g, x0, y0, w, h); return;
      case 'sovrum': drawSovrum(g, x0, y0, w, h); return;
      case 'badrum': drawBadrum(g, x0, y0, w, h); return;
      default:       drawVardagsrum(g, x0, y0, w, h); return; // 'main'
    }
  }
  // Shops (ikea/matbutik/djuraffar/restaurang/blomsteraffar/elektronikbutik):
  // single plain room, no toilet — you're there to shop, not to live.
  drawWoodFloor(g, x0, y0, w, h);
  drawRoomWalls(g, x0, y0, w, h);
}

/** Living room — the room every house is entered into. Subroom key 'main',
 *  not 'vardagsrum', so furniture placed before this multi-room feature
 *  existed (all tagged 'main') still shows up here instead of vanishing. */
function drawVardagsrum(g: Graphics, x0: number, y0: number, w: number, h: number): void {
  drawWoodFloor(g, x0, y0, w, h);
  // Window on the back wall, purely for atmosphere.
  g.roundRect(x0 + w / 2 - 30, y0 + 6, 60, 24, 3);
  g.fill({ color: 0xbce0f0 });
  g.roundRect(x0 + w / 2 - 30, y0 + 6, 60, 24, 3);
  g.stroke({ color: 0x6a4a20, width: 2 });
  g.moveTo(x0 + w / 2, y0 + 6); g.lineTo(x0 + w / 2, y0 + 30);
  g.stroke({ color: 0x6a4a20, width: 1.5 });
  drawStairsFor(g, x0 + w / 2, y0 + h / 2, 'main');
  drawRoomWalls(g, x0, y0, w, h);
}

/** Kitchen — built-in counter with stovetop + sink, so it reads as a kitchen
 *  even before any furniture is placed. */
function drawKok(g: Graphics, x0: number, y0: number, w: number, h: number): void {
  g.rect(x0, y0, w, h);
  g.fill({ color: 0xe8e4d8 });
  const tile = 32;
  for (let px = x0 + tile; px < x0 + w; px += tile) { g.moveTo(px, y0); g.lineTo(px, y0 + h); }
  for (let py = y0 + tile; py < y0 + h; py += tile) { g.moveTo(x0, py); g.lineTo(x0 + w, py); }
  g.stroke({ color: 0xc8c0a8, width: 0.8 });
  const cw = w - 80;
  g.roundRect(x0 + 40, y0 + 14, cw, 30, 3);
  g.fill({ color: 0xd8d8d8 });
  g.roundRect(x0 + 40, y0 + 14, cw, 30, 3);
  g.stroke({ color: 0x909090, width: 1.5 });
  for (const frac of [0.28, 0.42]) {
    g.circle(x0 + 40 + cw * frac, y0 + 29, 5);
    g.fill({ color: 0x404040 });
  }
  g.ellipse(x0 + 40 + cw * 0.72, y0 + 29, 12, 8);
  g.fill({ color: 0xffffff });
  g.ellipse(x0 + 40 + cw * 0.72, y0 + 29, 12, 8);
  g.stroke({ color: 0x8090a0, width: 1 });
  drawStairsFor(g, x0 + w / 2, y0 + h / 2, 'kok');
  drawRoomWalls(g, x0, y0, w, h);
}

/** Bedroom — warm floor tint distinct from the living room's, plus a window.
 *  The bed itself is player-placed furniture, like everywhere else. */
function drawSovrum(g: Graphics, x0: number, y0: number, w: number, h: number): void {
  g.rect(x0, y0, w, h);
  g.fill({ color: 0xe0c8a0 });
  const plankStep = 28;
  for (let px = x0 + plankStep; px < x0 + w; px += plankStep) { g.moveTo(px, y0); g.lineTo(px, y0 + h); }
  g.stroke({ color: 0xa08050, width: 1.2 });
  g.roundRect(x0 + w / 2 - 30, y0 + 6, 60, 24, 3);
  g.fill({ color: 0xbce0f0 });
  g.roundRect(x0 + w / 2 - 30, y0 + 6, 60, 24, 3);
  g.stroke({ color: 0x6a4a20, width: 2 });
  drawStairsFor(g, x0 + w / 2, y0 + h / 2, 'sovrum');
  drawRoomWalls(g, x0, y0, w, h);
}

/** Bathroom — white tile floor, the house's one toilet, and a small sink. */
function drawBadrum(g: Graphics, x0: number, y0: number, w: number, h: number): void {
  g.rect(x0, y0, w, h);
  g.fill({ color: 0xf0f4f8 });
  const tile = 28;
  for (let px = x0 + tile; px < x0 + w; px += tile) { g.moveTo(px, y0); g.lineTo(px, y0 + h); }
  for (let py = y0 + tile; py < y0 + h; py += tile) { g.moveTo(x0, py); g.lineTo(x0 + w, py); }
  g.stroke({ color: 0xc0c8d0, width: 1 });
  drawHouseToilet(g, x0 + w - 30, y0 + 26);
  g.rect(x0 + 16, y0 + 14, 10, 14);
  g.fill({ color: 0xccddee });
  g.rect(x0 + 16, y0 + 14, 10, 14);
  g.stroke({ color: 0x607080, width: 0.8 });
  g.ellipse(x0 + 21, y0 + 38, 8, 5);
  g.fill({ color: 0xffffff });
  g.ellipse(x0 + 21, y0 + 38, 8, 5);
  g.stroke({ color: 0x607080, width: 0.8 });
  drawStairsFor(g, x0 + w / 2, y0 + h / 2, 'badrum');
  drawRoomWalls(g, x0, y0, w, h);
}

/** A single toilet fixture, drawn in room-space (used by every non-school
 *  interior — house, hotel rooms, the shops). */
function drawHouseToilet(g: Graphics, cx: number, cy: number): void {
  g.rect(cx - 8, cy - 12, 16, 8);
  g.fill({ color: 0xffffff });
  g.rect(cx - 8, cy - 12, 16, 8);
  g.stroke({ color: 0x8090a0, width: 1 });
  g.ellipse(cx, cy - 2, 8, 6);
  g.fill({ color: 0xffffff });
  g.ellipse(cx, cy - 2, 8, 6);
  g.stroke({ color: 0x8090a0, width: 1 });
  g.ellipse(cx, cy - 2, 4.5, 3);
  g.fill({ color: 0xb0c8d8 });
}

/** Room-relative position of the toilet, so hit-testing matches drawHouseToilet(). */
const HOUSE_TOILET_ROFFSET = { rx: ROOM_W / 2 - 30, ry: -ROOM_H / 2 + 26 };

/** Building types that skip the generic fixed-position toilet entirely —
 *  shops (you're there to buy, not to live) plus hotel/cinema/house/school,
 *  which all handle the toilet themselves in their own branch above. */
const NO_GENERIC_TOILET_TYPES = new Set([
  'ikea', 'matbutik', 'hotel', 'cinema',
  'djuraffar', 'restaurang', 'blomsteraffar', 'elektronikbutik', 'bibliotek', 'gym',
]);

/** Room-relative position of the kitchen's built-in stovetop, matching the
 *  burner row drawn by drawKok(). */
const KOK_STOVE_ROFFSET = { rx: 0, ry: -ROOM_H / 2 + 29 };

const COOKED_DISHES = [
  { label: 'Köttbullar', emoji: '🍲' },
  { label: 'Pannkakor', emoji: '🥞' },
  { label: 'Soppa', emoji: '🍜' },
  { label: 'Stekt fisk', emoji: '🍽️' },
  { label: 'Gryta', emoji: '🍛' },
];

const FRIDGE_SLOTS = 8;
const TABLE_SLOTS = 8;

// ---- Hotel: lobby → elevator → 10 floors × 10 identical furnished rooms ----
const HOTEL_FLOORS = 10;
const HOTEL_ROOMS_PER_FLOOR = 10;
const HOTEL_DOOR_COLS = 5;
const HOTEL_DOOR_ROWS = 2;

const HOTEL_ELEVATOR_ROFFSET = { rx: 0, ry: -ROOM_H / 2 + 40 };

function hotelDoorOffset(idx: number): { rx: number; ry: number } {
  const col = idx % HOTEL_DOOR_COLS;
  const row = Math.floor(idx / HOTEL_DOOR_COLS);
  const padX = 50, padY = 110;
  const stepX = (ROOM_W - 2 * padX) / (HOTEL_DOOR_COLS - 1);
  const stepY = 90;
  return {
    rx: -ROOM_W / 2 + padX + col * stepX,
    ry: -ROOM_H / 2 + padY + row * stepY,
  };
}

const HOTEL_ROOM_BED_ROFFSET     = { rx: -ROOM_W / 2 + 60,  ry: -ROOM_H / 2 + 50 };
const HOTEL_ROOM_TOILET_ROFFSET  = { rx:  ROOM_W / 2 - 40,  ry: -ROOM_H / 2 + 40 };
const HOTEL_ROOM_KITCHEN_ROFFSET = { rx: -ROOM_W / 2 + 50,  ry:  ROOM_H / 2 - 40 };
const HOTEL_ROOM_FRIDGE_ROFFSET  = { rx:  ROOM_W / 2 - 40,  ry:  ROOM_H / 2 - 46 };
const HOTEL_ROOM_EXIT_ROFFSET    = { rx: 0,                 ry:  ROOM_H / 2 - 30 };

function drawElevator(g: Graphics, cx: number, cy: number): void {
  g.rect(cx - 26, cy - 20, 52, 40);
  g.fill({ color: 0x707880 });
  g.rect(cx - 26, cy - 20, 52, 40);
  g.stroke({ color: 0x404850, width: 2 });
  // Doors
  g.rect(cx - 23, cy - 17, 21, 34);
  g.fill({ color: 0xb0b8c0 });
  g.rect(cx + 2, cy - 17, 21, 34);
  g.fill({ color: 0xb0b8c0 });
  g.moveTo(cx, cy - 17); g.lineTo(cx, cy + 17);
  g.stroke({ color: 0x404850, width: 1.5 });
  // Up/down arrows
  g.moveTo(cx, cy - 30); g.lineTo(cx - 5, cy - 23); g.lineTo(cx + 5, cy - 23); g.closePath();
  g.fill({ color: 0xffe060 });
  g.moveTo(cx, cy + 30); g.lineTo(cx - 5, cy + 23); g.lineTo(cx + 5, cy + 23); g.closePath();
  g.fill({ color: 0xffe060 });
}

function drawHotelLobby(g: Graphics, x0: number, y0: number, w: number, h: number): void {
  drawWoodFloor(g, x0, y0, w, h);
  drawRoomWalls(g, x0, y0, w, h);
  const cx = x0 + w / 2, cy = y0 + h / 2;
  // Reception desk
  g.roundRect(x0 + 30, y0 + h - 70, 90, 34, 4);
  g.fill({ color: 0x8a5a30 });
  g.roundRect(x0 + 30, y0 + h - 70, 90, 34, 4);
  g.stroke({ color: 0x5a3a18, width: 1.5 });
  drawElevator(g, cx + HOTEL_ELEVATOR_ROFFSET.rx, cy + HOTEL_ELEVATOR_ROFFSET.ry);
}

function drawHotelCorridor(g: Graphics, x0: number, y0: number, w: number, h: number, floor: number): void {
  drawWoodFloor(g, x0, y0, w, h);
  drawRoomWalls(g, x0, y0, w, h);
  const cx = x0 + w / 2, cy = y0 + h / 2;
  drawElevator(g, cx + HOTEL_ELEVATOR_ROFFSET.rx, cy + HOTEL_ELEVATOR_ROFFSET.ry);
  for (let i = 0; i < HOTEL_ROOMS_PER_FLOOR; i++) {
    const { rx, ry } = hotelDoorOffset(i);
    const dx = cx + rx, dy = cy + ry;
    g.rect(dx - 16, dy - 20, 32, 40);
    g.fill({ color: 0xa87840 });
    g.rect(dx - 16, dy - 20, 32, 40);
    g.stroke({ color: 0x5a3a18, width: 1.5 });
    g.circle(dx + 10, dy, 1.6);
    g.fill({ color: 0xffe060 });
  }
}

function drawHotelRoom(g: Graphics, x0: number, y0: number, w: number, h: number): void {
  drawWoodFloor(g, x0, y0, w, h);
  drawRoomWalls(g, x0, y0, w, h);
  const cx = x0 + w / 2, cy = y0 + h / 2;

  // White bed
  const bx = cx + HOTEL_ROOM_BED_ROFFSET.rx, by = cy + HOTEL_ROOM_BED_ROFFSET.ry;
  drawFurnitureItem(g, bx, by, 'bed', '#ffffff');

  // Toilet
  const tx = cx + HOTEL_ROOM_TOILET_ROFFSET.rx, ty = cy + HOTEL_ROOM_TOILET_ROFFSET.ry;
  drawHouseToilet(g, tx, ty);

  // Kitchen — small counter with a stove top
  const kx = cx + HOTEL_ROOM_KITCHEN_ROFFSET.rx, ky = cy + HOTEL_ROOM_KITCHEN_ROFFSET.ry;
  g.roundRect(kx - 24, ky - 12, 48, 24, 3);
  g.fill({ color: 0xd8d8d8 });
  g.roundRect(kx - 24, ky - 12, 48, 24, 3);
  g.stroke({ color: 0x909090, width: 1.5 });
  for (const ox of [-12, 12]) {
    g.circle(kx + ox, ky - 2, 4);
    g.fill({ color: 0x404040 });
  }

  // Fridge — with food inside, ready to eat
  const fx = cx + HOTEL_ROOM_FRIDGE_ROFFSET.rx, fy = cy + HOTEL_ROOM_FRIDGE_ROFFSET.ry;
  drawFurnitureItem(g, fx, fy, 'fridge');

  // Door back to the corridor
  const ex = cx + HOTEL_ROOM_EXIT_ROFFSET.rx, ey = cy + HOTEL_ROOM_EXIT_ROFFSET.ry;
  g.rect(ex - 16, ey - 4, 32, 8);
  g.fill({ color: 0x8a6a40 });
  g.rect(ex - 16, ey - 4, 32, 8);
  g.stroke({ color: 0x4a3018, width: 1 });
}

function drawHotelInterior(
  g: Graphics, x0: number, y0: number, w: number, h: number,
  floor: number, roomIdx: number | null,
): void {
  if (roomIdx !== null) { drawHotelRoom(g, x0, y0, w, h); return; }
  if (floor === 0) { drawHotelLobby(g, x0, y0, w, h); return; }
  drawHotelCorridor(g, x0, y0, w, h, floor);
}

// ---- Cinema: one screen room, watchable from anywhere among the seats ----
const CINEMA_SCREEN_ROFFSET = { rx: 0, ry: -ROOM_H / 2 + 34 };

function drawCinemaInterior(g: Graphics, x0: number, y0: number, w: number, h: number): void {
  // Dark maroon carpeted floor, distinct from the wood-plank default.
  g.rect(x0, y0, w, h);
  g.fill({ color: 0x2a1216 });
  const plankStep = 26;
  for (let px = x0 + plankStep; px < x0 + w; px += plankStep) {
    g.moveTo(px, y0); g.lineTo(px, y0 + h);
  }
  g.stroke({ color: 0x180a0c, width: 1 });
  drawRoomWalls(g, x0, y0, w, h);

  const cx = x0 + w / 2, cy = y0 + h / 2;
  // Screen
  const sx = cx + CINEMA_SCREEN_ROFFSET.rx, sy = cy + CINEMA_SCREEN_ROFFSET.ry;
  g.rect(sx - (w - 90) / 2, sy - 26, w - 90, 40);
  g.fill({ color: 0x0a0a10 });
  g.rect(sx - (w - 90) / 2, sy - 26, w - 90, 40);
  g.stroke({ color: 0x606070, width: 2 });
  g.rect(sx - (w - 100) / 2, sy - 22, w - 100, 32);
  g.fill({ color: 0x1a2a4a, alpha: 0.7 });

  // Rows of seats
  const rows = 4, cols = 8;
  const padX = 40, startY = sy + 46, stepY = 30, stepX = (w - 2 * padX) / (cols - 1);
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const seatX = x0 + padX + c * stepX, seatY = startY + r * stepY;
      g.roundRect(seatX - 8, seatY - 7, 16, 14, 3);
      g.fill({ color: 0x8a3a3a });
      g.roundRect(seatX - 8, seatY - 7, 16, 14, 3);
      g.stroke({ color: 0x5a2020, width: 1 });
    }
  }
}

// ---- Cinema movies: short procedural vignettes, no video assets — each is
// just a looping animation keyed off remaining playback time. Capped at 10s
// by the caller (startAction's duration). ----
const MOVIE_VIGNETTES = ['Sjöodjuret', 'Rymdresan', 'Skattjakten', 'Stormen', 'Fiskarens dröm'];
const MOVIE_DURATION_FRAMES = 10 * 60;

function drawMovieFrame(g: Graphics, screenW: number, screenH: number, idx: number, elapsed: number): void {
  g.clear();
  const cx = screenW / 2, cy = screenH / 2;
  const w = Math.min(screenW - 80, 520), h = Math.min(screenH - 160, 320);
  const x0 = cx - w / 2, y0 = cy - h / 2;
  const t = elapsed / 60; // seconds

  // Cinema screen frame
  g.rect(x0 - 10, y0 - 10, w + 20, h + 20);
  g.fill({ color: 0x000000, alpha: 0.85 });
  g.rect(x0, y0, w, h);
  g.fill({ color: 0x0a1428 });

  switch (MOVIE_VIGNETTES[idx]) {
    case 'Sjöodjuret': {
      // Waterline + sine-wave sea-monster silhouette rising and sinking
      const waterY = y0 + h * 0.65;
      g.rect(x0, waterY, w, h - (waterY - y0));
      g.fill({ color: 0x0a3a50 });
      const bob = Math.sin(t * 1.3) * h * 0.12;
      const humps = 4;
      g.moveTo(x0 + w * 0.15, waterY + bob);
      for (let i = 0; i <= humps; i++) {
        const hx = x0 + w * (0.15 + (i / humps) * 0.6);
        const hy = waterY + bob - Math.abs(Math.sin(t * 2 + i)) * 22;
        g.quadraticCurveTo(hx - w * 0.04, hy - 14, hx, hy);
      }
      g.stroke({ color: 0x2a6a40, width: 6, cap: 'round' });
      break;
    }
    case 'Rymdresan': {
      g.rect(x0, y0, w, h);
      g.fill({ color: 0x05050f });
      for (let i = 0; i < 40; i++) {
        const sx = (x0 + (i * 53 + t * 40) % w);
        const sy = y0 + (i * 31) % h;
        g.circle(sx, sy, 1);
        g.fill({ color: 0xffffff, alpha: 0.7 });
      }
      const rocketT = (t * 0.3) % 1.4 - 0.2;
      const rx = x0 + w * rocketT, ry = y0 + h * (0.7 - rocketT * 0.4);
      g.moveTo(rx, ry); g.lineTo(rx - 10, ry + 16); g.lineTo(rx + 10, ry + 16); g.closePath();
      g.fill({ color: 0xe0e0e0 });
      g.moveTo(rx - 8, ry + 16); g.lineTo(rx - 16, ry + 24); g.lineTo(rx - 4, ry + 20); g.closePath();
      g.fill({ color: 0xe08030, alpha: 0.85 });
      break;
    }
    case 'Skattjakten': {
      g.rect(x0, y0, w, h);
      g.fill({ color: 0xd8c090 });
      const drawFrac = Math.min(1, t / 6);
      const path = [
        { x: x0 + w * 0.1, y: y0 + h * 0.8 }, { x: x0 + w * 0.35, y: y0 + h * 0.5 },
        { x: x0 + w * 0.55, y: y0 + h * 0.65 }, { x: x0 + w * 0.85, y: y0 + h * 0.2 },
      ];
      const totalLen = path.length - 1;
      const shown = Math.floor(drawFrac * totalLen);
      g.moveTo(path[0].x, path[0].y);
      for (let i = 1; i <= shown; i++) g.lineTo(path[i].x, path[i].y);
      if (shown < totalLen) {
        const p0 = path[shown], p1 = path[shown + 1], frac = drawFrac * totalLen - shown;
        g.lineTo(p0.x + (p1.x - p0.x) * frac, p0.y + (p1.y - p0.y) * frac);
      }
      g.stroke({ color: 0x6a3a18, width: 3, cap: 'round' });
      if (drawFrac >= 0.99) {
        const pct = (Math.sin(t * 4) + 1) / 2;
        const ex = path[3].x, ey = path[3].y, s = 8 + pct * 3;
        g.moveTo(ex - s, ey - s); g.lineTo(ex + s, ey + s);
        g.moveTo(ex + s, ey - s); g.lineTo(ex - s, ey + s);
        g.stroke({ color: 0xd02020, width: 4, cap: 'round' });
      }
      break;
    }
    case 'Stormen': {
      g.rect(x0, y0, w, h);
      g.fill({ color: 0x283040 });
      for (let i = 0; i < 5; i++) {
        const cxi = x0 + ((i * 97 + t * 25) % (w + 100)) - 50;
        g.ellipse(cxi, y0 + 30 + (i % 2) * 20, 40, 16);
        g.fill({ color: 0x101820, alpha: 0.7 });
      }
      const boatY = y0 + h * 0.6 + Math.sin(t * 3) * 10;
      const tilt = Math.sin(t * 3) * 0.2;
      g.moveTo(x0 + w * 0.5 - 30, boatY);
      g.lineTo(x0 + w * 0.5 - 18, boatY + 14 + tilt * 10);
      g.lineTo(x0 + w * 0.5 + 18, boatY + 14 - tilt * 10);
      g.lineTo(x0 + w * 0.5 + 30, boatY);
      g.closePath();
      g.fill({ color: 0x4a3018 });
      if (Math.sin(t * 6) > 0.85) {
        g.moveTo(x0 + w * 0.3, y0); g.lineTo(x0 + w * 0.34, y0 + h * 0.3); g.lineTo(x0 + w * 0.28, y0 + h * 0.3);
        g.lineTo(x0 + w * 0.32, y0 + h * 0.55);
        g.stroke({ color: 0xffe060, width: 2 });
      }
      break;
    }
    case 'Fiskarens dröm': {
      g.rect(x0, y0, w, h);
      g.fill({ color: 0x0a4058 });
      for (let i = 0; i < 8; i++) {
        const phase = t * 1.2 + i * 0.6;
        const fx = x0 + ((i * 61 + t * 30) % (w + 40)) - 20;
        const fy = y0 + h * 0.5 + Math.sin(phase) * h * 0.3;
        g.moveTo(fx, fy);
        g.lineTo(fx - 10, fy - 4);
        g.lineTo(fx - 10, fy + 4);
        g.closePath();
        g.fill({ color: 0x6ab0d0, alpha: 0.9 });
      }
      break;
    }
  }
}

/** IKEA furniture, placed by a player inside their own house. */
function drawFurnitureItem(g: Graphics, x: number, y: number, type: string, color?: string): void {
  const fill = color ? parseInt(color.replace('#', ''), 16) : 0x8a5a30;
  switch (type) {
    case 'bed':
      g.roundRect(x - 16, y - 22, 32, 44, 4);
      g.fill({ color: 0xf0f0f0 });
      g.roundRect(x - 16, y - 22, 32, 12, 4);
      g.fill({ color: 0xffffff });
      g.roundRect(x - 14, y - 8, 28, 26, 3);
      g.fill({ color: fill });
      g.roundRect(x - 16, y - 22, 32, 44, 4);
      g.stroke({ color: 0x6a4a20, width: 1.5 });
      break;
    case 'stool':
      g.circle(x, y, 9);
      g.fill({ color: 0xc8a060 });
      g.circle(x, y, 9);
      g.stroke({ color: 0x7a5020, width: 1.5 });
      break;
    case 'table':
      g.roundRect(x - 16, y - 10, 32, 20, 3);
      g.fill({ color: 0xa87840 });
      g.roundRect(x - 16, y - 10, 32, 20, 3);
      g.stroke({ color: 0x6a4a20, width: 1.5 });
      break;
    case 'oven':
      g.roundRect(x - 11, y - 13, 22, 26, 3);
      g.fill({ color: 0x606060 });
      g.rect(x - 7, y - 6, 14, 12);
      g.fill({ color: 0x202020 });
      g.circle(x, y - 9, 1.5);
      g.fill({ color: 0xe0e0e0 });
      break;
    case 'fridge':
      g.roundRect(x - 10, y - 16, 20, 32, 3);
      g.fill({ color: 0xe8ecef });
      g.roundRect(x - 10, y - 16, 20, 32, 3);
      g.stroke({ color: 0xa0a8b0, width: 1.5 });
      g.rect(x + 5, y - 12, 2, 6);
      g.fill({ color: 0x8a92a0 });
      break;
    case 'sofa': {
      // Sofas are always black, regardless of any stored per-item color
      // (e.g. from before the "never purple or pink" pass) — never let a
      // legacy-colored instance slip through.
      const sofaColor = 0x111111;
      // Legs
      for (const lx of [x - 20, x + 20]) {
        g.moveTo(lx, y + 14); g.lineTo(lx, y + 18);
        g.stroke({ color: 0x4a3018, width: 2 });
      }
      // Seat base
      g.roundRect(x - 24, y, 48, 14, 4);
      g.fill({ color: sofaColor });
      g.roundRect(x - 24, y, 48, 14, 4);
      g.stroke({ color: 0x000000, alpha: 0.28, width: 1.5 });
      // Backrest — taller band behind the seat, is what makes it read as a
      // sofa instead of just another rectangular table/box.
      g.roundRect(x - 24, y - 16, 48, 16, 6);
      g.fill({ color: sofaColor });
      g.roundRect(x - 24, y - 16, 48, 16, 6);
      g.stroke({ color: 0x000000, alpha: 0.28, width: 1.5 });
      // Seat-cushion divider lines
      g.moveTo(x - 8, y); g.lineTo(x - 8, y + 14);
      g.moveTo(x + 8, y); g.lineTo(x + 8, y + 14);
      g.stroke({ color: 0x000000, alpha: 0.2, width: 1.2 });
      // Rolled armrests, taller than the seat so the silhouette isn't a flat box
      g.roundRect(x - 30, y - 12, 9, 26, 4); g.fill({ color: sofaColor });
      g.roundRect(x - 30, y - 12, 9, 26, 4); g.stroke({ color: 0x000000, alpha: 0.28, width: 1.5 });
      g.roundRect(x + 21, y - 12, 9, 26, 4); g.fill({ color: sofaColor });
      g.roundRect(x + 21, y - 12, 9, 26, 4); g.stroke({ color: 0x000000, alpha: 0.28, width: 1.5 });
      break;
    }
    case 'tv':
      g.rect(x - 16, y - 11, 32, 18);
      g.fill({ color: 0x101418 });
      g.rect(x - 16, y - 11, 32, 18);
      g.stroke({ color: 0x404850, width: 1.5 });
      g.rect(x - 13, y - 8, 26, 12);
      g.fill({ color: 0x3a5a80, alpha: 0.85 });
      g.rect(x - 3, y + 7, 6, 5);
      g.fill({ color: 0x202020 });
      break;
    case 'lamp':
      g.moveTo(x, y + 12); g.lineTo(x, y - 6);
      g.stroke({ color: 0x707070, width: 2 });
      g.moveTo(x - 8, y + 12); g.lineTo(x + 8, y + 12);
      g.stroke({ color: 0x505050, width: 3, cap: 'round' });
      g.moveTo(x - 9, y - 6); g.lineTo(x + 9, y - 6); g.lineTo(x + 5, y - 16); g.lineTo(x - 5, y - 16); g.closePath();
      g.fill({ color: fill });
      g.circle(x, y - 8, 2);
      g.fill({ color: 0xffe89a, alpha: 0.9 });
      break;
    case 'rug':
      g.ellipse(x, y, 28, 18);
      g.fill({ color: fill });
      g.ellipse(x, y, 28, 18);
      g.stroke({ color: 0x000000, alpha: 0.2, width: 1.5 });
      g.ellipse(x, y, 19, 11);
      g.stroke({ color: 0xffffff, alpha: 0.35, width: 1.5 });
      break;
    case 'bookshelf':
      g.rect(x - 16, y - 22, 32, 44);
      g.fill({ color: 0x8a5a30 });
      g.rect(x - 16, y - 22, 32, 44);
      g.stroke({ color: 0x5a3a18, width: 1.5 });
      for (let s = 0; s < 3; s++) {
        const sy = y - 15 + s * 14;
        g.moveTo(x - 14, sy); g.lineTo(x + 14, sy);
        g.stroke({ color: 0x5a3a18, width: 1 });
        for (let b = 0; b < 4; b++) {
          g.rect(x - 13 + b * 7, sy - 10, 5, 10);
          g.fill({ color: [0xe04040, 0x40b060, 0x4a90d9, 0xe0d040][(s + b) % 4] });
        }
      }
      break;
    case 'desk':
      g.roundRect(x - 20, y - 9, 40, 18, 3);
      g.fill({ color: 0xa87840 });
      g.roundRect(x - 20, y - 9, 40, 18, 3);
      g.stroke({ color: 0x6a4a20, width: 1.5 });
      g.rect(x - 8, y - 15, 12, 8);
      g.fill({ color: 0x202020 });
      g.rect(x - 7, y - 14, 10, 6);
      g.fill({ color: 0x3a5a80 });
      break;
    case 'mirror':
      g.roundRect(x - 10, y - 18, 20, 36, 8);
      g.fill({ color: 0xd0e8f0, alpha: 0.9 });
      g.roundRect(x - 10, y - 18, 20, 36, 8);
      g.stroke({ color: 0xa0a8b0, width: 2 });
      g.moveTo(x - 4, y - 12); g.lineTo(x - 4, y + 10);
      g.stroke({ color: 0xffffff, alpha: 0.6, width: 1.5 });
      break;
    case 'plant':
      g.roundRect(x - 8, y, 16, 12, 2);
      g.fill({ color: 0xa87840 });
      g.roundRect(x - 8, y, 16, 12, 2);
      g.stroke({ color: 0x6a4a20, width: 1 });
      g.circle(x, y - 6, 9);
      g.circle(x - 6, y - 2, 7);
      g.circle(x + 6, y - 2, 7);
      g.fill({ color: 0x347a3a });
      break;
    case 'chair':
      // Backrest (behind the seat, taller) — distinguishes it from the stool
      g.roundRect(x - 8, y - 18, 16, 12, 3);
      g.fill({ color: fill });
      g.roundRect(x - 8, y - 18, 16, 12, 3);
      g.stroke({ color: 0x6a4a20, width: 1.2 });
      g.roundRect(x - 9, y - 7, 18, 10, 3);
      g.fill({ color: fill });
      g.roundRect(x - 9, y - 7, 18, 10, 3);
      g.stroke({ color: 0x6a4a20, width: 1.2 });
      for (const lx of [x - 7, x + 7]) {
        g.moveTo(lx, y + 3); g.lineTo(lx, y + 10);
        g.stroke({ color: 0x6a4a20, width: 1.5 });
      }
      break;
    case 'wardrobe':
      g.rect(x - 15, y - 26, 30, 52);
      g.fill({ color: 0x6a4a28 });
      g.rect(x - 15, y - 26, 30, 52);
      g.stroke({ color: 0x3a2a10, width: 1.5 });
      g.moveTo(x, y - 26); g.lineTo(x, y + 26);
      g.stroke({ color: 0x3a2a10, width: 1 });
      g.circle(x - 3, y, 1.2); g.circle(x + 3, y, 1.2);
      g.fill({ color: 0xe0c060 });
      break;
    case 'painting':
      g.rect(x - 11, y - 14, 22, 28);
      g.fill({ color: 0x8a5a30 });
      g.rect(x - 11, y - 14, 22, 28);
      g.stroke({ color: 0x4a3018, width: 1.5 });
      g.rect(x - 8, y - 11, 16, 22);
      g.fill({ color: fill });
      g.moveTo(x - 6, y + 5); g.lineTo(x, y - 5); g.lineTo(x + 6, y + 5); g.closePath();
      g.fill({ color: 0x347a3a, alpha: 0.8 });
      break;
    case 'clock':
      g.circle(x, y, 11);
      g.fill({ color: 0xf0ead8 });
      g.circle(x, y, 11);
      g.stroke({ color: 0x5a3a18, width: 2 });
      g.moveTo(x, y); g.lineTo(x, y - 6);
      g.moveTo(x, y); g.lineTo(x + 4, y + 2);
      g.stroke({ color: 0x201810, width: 1.3, cap: 'round' });
      g.circle(x, y, 1.2);
      g.fill({ color: 0x201810 });
      break;
    case 'bigbookshelf':
      g.rect(x - 22, y - 28, 44, 56);
      g.fill({ color: 0x8a5a30 });
      g.rect(x - 22, y - 28, 44, 56);
      g.stroke({ color: 0x5a3a18, width: 1.5 });
      for (let s = 0; s < 4; s++) {
        const sy = y - 20 + s * 13;
        g.moveTo(x - 20, sy); g.lineTo(x + 20, sy);
        g.stroke({ color: 0x5a3a18, width: 1 });
        for (let b = 0; b < 6; b++) {
          g.rect(x - 19 + b * 6.5, sy - 9, 5, 9);
          g.fill({ color: [0xe04040, 0x40b060, 0x4a90d9, 0xe0d040][(s + b) % 4] });
        }
      }
      break;
    case 'desklamp':
      g.rect(x - 6, y + 8, 12, 3);
      g.fill({ color: 0x606060 });
      g.moveTo(x, y + 8); g.lineTo(x - 2, y - 6);
      g.stroke({ color: 0x707070, width: 2 });
      g.moveTo(x - 7, y - 10); g.lineTo(x + 3, y - 10); g.lineTo(x, y - 2); g.lineTo(x - 10, y - 2); g.closePath();
      g.fill({ color: fill });
      g.circle(x - 5, y - 6, 1.5);
      g.fill({ color: 0xffe89a, alpha: 0.9 });
      break;
    case 'floormirror':
      g.roundRect(x - 12, y - 24, 24, 44, 10);
      g.fill({ color: 0xd0e8f0, alpha: 0.9 });
      g.roundRect(x - 12, y - 24, 24, 44, 10);
      g.stroke({ color: 0x8a5a30, width: 2.5 });
      g.moveTo(x - 4, y - 16); g.lineTo(x - 4, y + 12);
      g.stroke({ color: 0xffffff, alpha: 0.6, width: 1.5 });
      g.rect(x - 5, y + 20, 10, 3);
      g.fill({ color: 0x8a5a30 });
      break;
    case 'barstool':
      g.circle(x, y, 8);
      g.fill({ color: fill });
      g.circle(x, y, 8);
      g.stroke({ color: 0x4a3018, width: 1.5 });
      for (const lx of [x - 5, x + 5]) {
        g.moveTo(lx, y + 7); g.lineTo(lx, y + 18);
        g.stroke({ color: 0x606060, width: 2 });
      }
      g.moveTo(x - 5, y + 13); g.lineTo(x + 5, y + 13);
      g.stroke({ color: 0x606060, width: 1.5 });
      break;
    case 'coffeetable':
      g.roundRect(x - 20, y - 8, 40, 16, 4);
      g.fill({ color: 0xa87840 });
      g.roundRect(x - 20, y - 8, 40, 16, 4);
      g.stroke({ color: 0x6a4a20, width: 1.5 });
      g.roundRect(x - 14, y - 5, 28, 10, 2);
      g.fill({ color: 0xd0e8f0, alpha: 0.6 });
      break;
    case 'dinnertable':
      g.circle(x, y, 22);
      g.fill({ color: 0xa87840 });
      g.circle(x, y, 22);
      g.stroke({ color: 0x6a4a20, width: 1.5 });
      g.circle(x, y, 15);
      g.stroke({ color: 0x8a6030, width: 1, alpha: 0.6 });
      break;
    case 'bigplant':
      g.roundRect(x - 11, y, 22, 16, 3);
      g.fill({ color: 0xa87840 });
      g.roundRect(x - 11, y, 22, 16, 3);
      g.stroke({ color: 0x6a4a20, width: 1 });
      g.circle(x, y - 10, 13);
      g.circle(x - 9, y - 2, 10);
      g.circle(x + 9, y - 2, 10);
      g.fill({ color: 0x2a6e30 });
      break;
    case 'cactus':
      g.roundRect(x - 7, y + 4, 14, 10, 2);
      g.fill({ color: 0xc8926a });
      g.roundRect(x - 7, y + 4, 14, 10, 2);
      g.stroke({ color: 0x8a5a30, width: 1 });
      g.roundRect(x - 3, y - 14, 6, 20, 3);
      g.fill({ color: 0x347a3a });
      g.roundRect(x - 10, y - 6, 5, 10, 2.5);
      g.fill({ color: 0x347a3a });
      g.roundRect(x + 5, y - 9, 5, 12, 2.5);
      g.fill({ color: 0x347a3a });
      break;
    case 'wallshelf':
      g.rect(x - 18, y - 2, 36, 4);
      g.fill({ color: 0x8a5a30 });
      g.rect(x - 18, y - 2, 36, 4);
      g.stroke({ color: 0x5a3a18, width: 1 });
      for (let b = 0; b < 4; b++) {
        g.rect(x - 15 + b * 8, y - 12, 6, 10);
        g.fill({ color: [0xe04040, 0x40b060, 0x4a90d9, 0xe0d040][b] });
      }
      break;
    case 'tvstand':
      g.roundRect(x - 22, y - 4, 44, 14, 3);
      g.fill({ color: 0xa87840 });
      g.roundRect(x - 22, y - 4, 44, 14, 3);
      g.stroke({ color: 0x6a4a20, width: 1.5 });
      g.rect(x - 16, y - 20, 32, 15);
      g.fill({ color: 0x101418 });
      g.rect(x - 13, y - 17, 26, 9);
      g.fill({ color: 0x3a5a80, alpha: 0.85 });
      break;
    case 'armchair':
      for (const lx of [x - 16, x + 16]) {
        g.moveTo(lx, y + 10); g.lineTo(lx, y + 15);
        g.stroke({ color: 0x4a3018, width: 2 });
      }
      g.roundRect(x - 18, y - 2, 36, 12, 4);
      g.fill({ color: fill });
      g.roundRect(x - 18, y - 16, 36, 16, 6);
      g.fill({ color: fill });
      g.roundRect(x - 22, y - 12, 8, 22, 4);
      g.fill({ color: fill });
      g.roundRect(x + 14, y - 12, 8, 22, 4);
      g.fill({ color: fill });
      g.roundRect(x - 18, y - 2, 36, 12, 4);
      g.stroke({ color: 0x000000, alpha: 0.25, width: 1.2 });
      break;
    case 'pouf':
      g.ellipse(x, y, 12, 8);
      g.fill({ color: fill });
      g.ellipse(x, y, 12, 8);
      g.stroke({ color: 0x000000, alpha: 0.25, width: 1.2 });
      g.moveTo(x - 6, y); g.lineTo(x + 6, y);
      g.stroke({ color: 0x000000, alpha: 0.15, width: 1 });
      break;
    case 'candlestick':
      g.rect(x - 4, y + 4, 8, 4);
      g.fill({ color: 0xd0d0d0 });
      g.moveTo(x, y + 4); g.lineTo(x, y - 6);
      g.stroke({ color: 0xd0d0d0, width: 2 });
      g.rect(x - 1.5, y - 12, 3, 7);
      g.fill({ color: 0xfff8e0 });
      g.moveTo(x, y - 12); g.lineTo(x - 1, y - 16); g.lineTo(x + 1.5, y - 13); g.closePath();
      g.fill({ color: 0xe08030 });
      break;
    case 'vase':
      g.moveTo(x - 5, y + 8); g.lineTo(x - 6, y - 4); g.quadraticCurveTo(x, y - 10, x + 6, y - 4); g.lineTo(x + 5, y + 8); g.closePath();
      g.fill({ color: 0x4a90d9 });
      g.stroke({ color: 0x2a5a9a, width: 1 });
      g.moveTo(x, y - 8); g.lineTo(x - 3, y - 16);
      g.moveTo(x, y - 8); g.lineTo(x + 3, y - 17);
      g.moveTo(x, y - 8); g.lineTo(x, y - 18);
      g.stroke({ color: 0x347a3a, width: 1.5, cap: 'round' });
      break;
    case 'blanket':
      g.roundRect(x - 18, y - 10, 36, 20, 4);
      g.fill({ color: fill });
      for (let i = -2; i <= 2; i++) {
        g.moveTo(x + i * 6, y - 10); g.lineTo(x + i * 6, y + 10);
      }
      g.stroke({ color: 0x000000, alpha: 0.12, width: 1 });
      g.roundRect(x - 18, y - 10, 36, 20, 4);
      g.stroke({ color: 0x000000, alpha: 0.2, width: 1.2 });
      break;
    case 'pillow':
      g.roundRect(x - 10, y - 10, 20, 20, 6);
      g.fill({ color: fill });
      g.roundRect(x - 10, y - 10, 20, 20, 6);
      g.stroke({ color: 0x000000, alpha: 0.2, width: 1.2 });
      g.moveTo(x - 6, y); g.lineTo(x + 6, y);
      g.stroke({ color: 0x000000, alpha: 0.15, width: 1 });
      break;
    case 'ladder':
      g.moveTo(x - 8, y - 22); g.lineTo(x - 10, y + 22);
      g.moveTo(x + 8, y - 22); g.lineTo(x + 10, y + 22);
      g.stroke({ color: 0x8a5a30, width: 2.5, cap: 'round' });
      for (let s = 0; s < 4; s++) {
        const sy = y - 14 + s * 12;
        g.moveTo(x - 9, sy); g.lineTo(x + 9, sy);
        g.stroke({ color: 0x6a4a20, width: 2 });
        g.rect(x - 7, sy - 6, 5, 6);
        g.fill({ color: [0xe04040, 0x40b060, 0x4a90d9][(s) % 3] });
      }
      break;
    case 'shoerack':
      g.rect(x - 20, y - 10, 40, 20);
      g.fill({ color: 0x8a5a30 });
      g.rect(x - 20, y - 10, 40, 20);
      g.stroke({ color: 0x5a3a18, width: 1.5 });
      g.moveTo(x - 20, y); g.lineTo(x + 20, y);
      g.stroke({ color: 0x5a3a18, width: 1 });
      for (let i = -2; i <= 2; i += 2) {
        g.ellipse(x + i * 7, y - 5, 4, 2);
        g.ellipse(x + i * 7, y + 5, 4, 2);
        g.fill({ color: 0x4a4a4a });
      }
      break;
    case 'bartable':
      g.circle(x, y - 8, 12);
      g.fill({ color: 0xa87840 });
      g.circle(x, y - 8, 12);
      g.stroke({ color: 0x6a4a20, width: 1.5 });
      g.rect(x - 2.5, y + 4, 5, 16);
      g.fill({ color: 0x707070 });
      g.ellipse(x, y + 20, 8, 3);
      g.fill({ color: 0x606060 });
      break;
    case 'kitchencounter':
      g.roundRect(x - 24, y - 6, 48, 18, 3);
      g.fill({ color: 0xd8d8d8 });
      g.roundRect(x - 24, y - 6, 48, 18, 3);
      g.stroke({ color: 0x909090, width: 1.5 });
      g.ellipse(x, y, 10, 5);
      g.fill({ color: 0xa0b0c0 });
      g.rect(x + 8, y - 10, 2, 6);
      g.fill({ color: 0x707070 });
      break;
    case 'microwave':
      g.roundRect(x - 15, y - 10, 30, 20, 3);
      g.fill({ color: 0xe8e8e8 });
      g.roundRect(x - 15, y - 10, 30, 20, 3);
      g.stroke({ color: 0x909090, width: 1.5 });
      g.rect(x - 11, y - 6, 18, 12);
      g.fill({ color: 0x303030 });
      g.rect(x + 9, y - 6, 3, 12);
      g.fill({ color: 0xb0b0b0 });
      break;
    case 'toaster':
      g.roundRect(x - 10, y - 8, 20, 14, 4);
      g.fill({ color: 0xc0c0c8 });
      g.roundRect(x - 10, y - 8, 20, 14, 4);
      g.stroke({ color: 0x808088, width: 1.2 });
      g.rect(x - 6, y - 12, 4, 4);
      g.fill({ color: 0x303030 });
      g.rect(x + 2, y - 12, 4, 4);
      g.fill({ color: 0x303030 });
      break;
    case 'spicerack':
      g.rect(x - 14, y - 10, 28, 20);
      g.fill({ color: 0x8a5a30 });
      g.rect(x - 14, y - 10, 28, 20);
      g.stroke({ color: 0x5a3a18, width: 1.2 });
      for (let i = 0; i < 5; i++) {
        g.rect(x - 11 + i * 5, y - 6, 3, 12);
        g.fill({ color: [0xe0a030, 0x8a5a30, 0x40b060, 0xe04040, 0xd0d0d0][i] });
      }
      break;
    case 'radiator':
      g.roundRect(x - 22, y - 12, 44, 20, 2);
      g.fill({ color: 0xe8e8e8 });
      g.roundRect(x - 22, y - 12, 44, 20, 2);
      g.stroke({ color: 0xa0a0a0, width: 1.2 });
      for (let i = -3; i <= 3; i++) {
        g.moveTo(x + i * 6, y - 10); g.lineTo(x + i * 6, y + 6);
      }
      g.stroke({ color: 0xb8b8b8, width: 1.5 });
      break;
    case 'floorlamp':
      g.moveTo(x, y + 16); g.lineTo(x, y - 14);
      g.stroke({ color: 0x606060, width: 2.5 });
      g.ellipse(x, y + 17, 9, 3);
      g.fill({ color: 0x505050 });
      g.moveTo(x - 10, y - 14); g.lineTo(x + 10, y - 14); g.lineTo(x + 6, y - 26); g.lineTo(x - 6, y - 26); g.closePath();
      g.fill({ color: fill });
      g.circle(x, y - 16, 2);
      g.fill({ color: 0xffe89a, alpha: 0.9 });
      break;
    case 'petbed':
      g.ellipse(x, y + 2, 20, 12);
      g.fill({ color: fill });
      g.ellipse(x, y + 2, 20, 12);
      g.stroke({ color: 0x5a3a18, width: 1.5 });
      g.ellipse(x, y, 14, 8);
      g.fill({ color: 0xf0e8d8 });
      break;
    case 'birdcage':
      g.moveTo(x, y - 22); g.lineTo(x, y - 16);
      g.stroke({ color: 0x808890, width: 1.5 });
      g.ellipse(x, y - 4, 14, 18);
      g.stroke({ color: 0x808890, width: 1.2 });
      for (let i = -3; i <= 3; i++) {
        g.moveTo(x + i * 4, y - 16); g.lineTo(x + i * 4, y + 10);
      }
      g.stroke({ color: 0xa0a8b0, width: 1 });
      g.ellipse(x, y + 12, 15, 4);
      g.fill({ color: 0x808890 });
      g.moveTo(x - 8, y - 2); g.lineTo(x + 8, y - 2);
      g.stroke({ color: 0xd0a040, width: 1.5 });
      g.circle(x, y - 6, 2.5);
      g.fill({ color: 0xe0d040 });
      break;
    case 'aquarium':
      g.roundRect(x - 22, y - 14, 44, 24, 2);
      g.fill({ color: 0xbfe4f5, alpha: 0.55 });
      g.roundRect(x - 22, y - 14, 44, 24, 2);
      g.stroke({ color: 0x506878, width: 1.5 });
      g.rect(x - 22, y - 16, 44, 4);
      g.fill({ color: 0x506878 });
      for (const [fx, fy, fc] of [[-8, 0, 0xff9020], [6, 4, 0xffc040]] as Array<[number, number, number]>) {
        g.moveTo(x + fx, y + fy); g.lineTo(x + fx - 5, y + fy - 3); g.lineTo(x + fx - 5, y + fy + 3); g.closePath();
        g.fill({ color: fc });
      }
      break;
    case 'cattower':
      g.rect(x - 4, y - 30, 8, 40);
      g.fill({ color: fill });
      g.rect(x - 4, y - 30, 8, 40);
      g.stroke({ color: 0x8a6838, width: 1 });
      g.ellipse(x, y - 30, 14, 6);
      g.fill({ color: fill });
      g.ellipse(x, y - 30, 14, 6);
      g.stroke({ color: 0x8a6838, width: 1 });
      g.ellipse(x, y + 2, 18, 7);
      g.fill({ color: fill });
      g.ellipse(x, y + 2, 18, 7);
      g.stroke({ color: 0x8a6838, width: 1 });
      break;
    case 'hamstercage':
      g.roundRect(x - 16, y - 12, 32, 22, 3);
      g.fill({ color: 0xf0f0f0, alpha: 0.4 });
      g.roundRect(x - 16, y - 12, 32, 22, 3);
      g.stroke({ color: 0x909090, width: 1.2 });
      for (let i = -2; i <= 2; i++) {
        g.moveTo(x + i * 6, y - 12); g.lineTo(x + i * 6, y + 10);
      }
      g.stroke({ color: 0xb0b0b0, width: 0.8 });
      g.circle(x, y - 1, 6);
      g.stroke({ color: 0xc09040, width: 1.5 });
      break;
    case 'tulipvase':
      g.moveTo(x - 5, y + 12); g.lineTo(x - 3, y - 2); g.lineTo(x + 3, y - 2); g.lineTo(x + 5, y + 12); g.closePath();
      g.fill({ color: fill });
      for (const [tx, tc] of [[-6, 0xe04040], [0, 0xf0d020], [6, 0xe04040]] as Array<[number, number]>) {
        g.moveTo(x + tx, y - 4); g.lineTo(x + tx, y - 16);
        g.stroke({ color: 0x40a050, width: 1.2 });
        g.ellipse(x + tx, y - 18, 4, 6);
        g.fill({ color: tc });
      }
      break;
    case 'roses':
      g.moveTo(x - 4, y + 12); g.lineTo(x - 2, y - 2); g.lineTo(x + 2, y - 2); g.lineTo(x + 4, y + 12); g.closePath();
      g.fill({ color: fill });
      for (const [rx, ry] of [[-5, -14], [0, -18], [5, -14]] as Array<[number, number]>) {
        g.moveTo(x + rx, y - 2); g.lineTo(x + rx, y + ry + 4);
        g.stroke({ color: 0x40a050, width: 1.2 });
        g.circle(x + rx, y + ry, 4);
        g.fill({ color: 0xd02818 });
      }
      break;
    case 'orchid':
      g.roundRect(x - 6, y + 2, 12, 10, 2);
      g.fill({ color: 0xd8d0c0 });
      g.moveTo(x, y + 2); g.quadraticCurveTo(x + 10, y - 10, x + 6, y - 22);
      g.stroke({ color: 0x40a050, width: 1.5 });
      for (const t of [0.4, 0.7, 1]) {
        const px = x + 10 * t, py = y + 2 - 12 * t - 10 * t * t;
        g.ellipse(px, py, 4, 3);
        g.fill({ color: 0xfff8e0 });
        g.ellipse(px, py, 1.5, 1);
        g.fill({ color: 0xf0c020 });
      }
      break;
    case 'sunflower':
      g.roundRect(x - 6, y + 4, 12, 10, 2);
      g.fill({ color: 0x8a5a30 });
      g.moveTo(x, y + 4); g.lineTo(x, y - 14);
      g.stroke({ color: 0x40a050, width: 2 });
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2;
        g.ellipse(x + Math.cos(a) * 8, y - 14 + Math.sin(a) * 8, 4, 2.4);
        g.fill({ color: fill });
      }
      g.circle(x, y - 14, 5);
      g.fill({ color: 0x6a4020 });
      break;
    case 'bonsai':
      g.roundRect(x - 10, y + 4, 20, 10, 2);
      g.fill({ color: fill });
      g.roundRect(x - 10, y + 4, 20, 10, 2);
      g.stroke({ color: 0x5a3a18, width: 1.2 });
      g.moveTo(x, y + 4); g.quadraticCurveTo(x - 6, y - 6, x - 2, y - 14);
      g.stroke({ color: 0x6a4a28, width: 2.5 });
      for (const [bx, by] of [[-4, -16], [4, -14], [0, -20]] as Array<[number, number]>) {
        g.circle(x + bx, y + by, 6);
        g.fill({ color: 0x4a8a40 });
      }
      break;
    case 'computer':
      g.roundRect(x - 14, y - 16, 28, 18, 2);
      g.fill({ color: fill });
      g.roundRect(x - 12, y - 14, 24, 14, 1);
      g.fill({ color: 0x60a8d8 });
      g.rect(x - 3, y + 2, 6, 6);
      g.fill({ color: 0x505050 });
      g.roundRect(x - 12, y + 8, 24, 3, 1);
      g.fill({ color: 0x404040 });
      break;
    case 'console':
      g.roundRect(x - 12, y - 6, 24, 12, 3);
      g.fill({ color: fill });
      g.roundRect(x - 12, y - 6, 24, 12, 3);
      g.stroke({ color: 0x505050, width: 1 });
      g.circle(x - 6, y, 2);
      g.fill({ color: 0x40c0f0 });
      g.circle(x + 6, y, 2);
      g.fill({ color: 0xe04040 });
      break;
    case 'stereo':
      g.roundRect(x - 16, y - 10, 32, 20, 3);
      g.fill({ color: fill });
      g.roundRect(x - 16, y - 10, 32, 20, 3);
      g.stroke({ color: 0x202020, width: 1.2 });
      g.circle(x - 8, y, 5);
      g.stroke({ color: 0xc0c0c0, width: 1.5 });
      g.circle(x + 8, y, 5);
      g.stroke({ color: 0xc0c0c0, width: 1.5 });
      break;
    case 'projector':
      g.roundRect(x - 14, y - 8, 28, 14, 3);
      g.fill({ color: fill });
      g.circle(x + 12, y - 1, 4);
      g.fill({ color: 0x303030 });
      g.circle(x + 12, y - 1, 2);
      g.fill({ color: 0x80c8e0 });
      g.moveTo(x - 14, y - 1); g.lineTo(x - 28, y - 9); g.lineTo(x - 28, y + 7); g.closePath();
      g.fill({ color: 0xfff8dd, alpha: 0.35 });
      break;
    case 'speaker':
      g.roundRect(x - 8, y - 20, 16, 36, 2);
      g.fill({ color: fill });
      g.roundRect(x - 8, y - 20, 16, 36, 2);
      g.stroke({ color: 0x181818, width: 1 });
      g.circle(x, y - 10, 5);
      g.stroke({ color: 0x606060, width: 1.2 });
      g.circle(x, y + 6, 3.5);
      g.stroke({ color: 0x606060, width: 1.2 });
      break;
  }
}

// ---- Park drawing ----
function drawPark(g: Graphics, x: number, y: number): void {
  const w = 64, h = 50;
  const top = y - h;

  // Ground — sandy/rubber surface
  g.roundRect(x - w / 2, top, w, h, 8);
  g.fill({ color: 0xe8c87a });
  g.roundRect(x - w / 2, top, w, h, 8);
  g.stroke({ color: 0xc8902a, width: 2 });

  // --- Sandlåda (sandbox) — top-left quadrant ---
  const sbx = x - w / 2 + 6, sby = top + 4;
  g.roundRect(sbx, sby, 20, 16, 3);
  g.fill({ color: 0xf5dfa0 });
  g.roundRect(sbx, sby, 20, 16, 3);
  g.stroke({ color: 0xb07820, width: 1.5 });
  // bucket in sandbox
  g.circle(sbx + 10, sby + 8, 3.5);
  g.fill({ color: 0xe04040 });

  // --- Rutschkana (slide) — top-right quadrant ---
  // Platform
  const slx = x + 4, sly = top + 4;
  g.rect(slx, sly, 14, 10);
  g.fill({ color: 0x4488cc });
  g.rect(slx, sly, 14, 10);
  g.stroke({ color: 0x2255aa, width: 1.5 });
  // Ramp going down-left
  g.moveTo(slx, sly + 10);
  g.lineTo(slx - 10, top + 26);
  g.lineTo(slx - 6, top + 26);
  g.lineTo(slx + 4, sly + 10);
  g.closePath();
  g.fill({ color: 0x55aaee });
  g.stroke({ color: 0x2255aa, width: 1 });

  // --- Gungor (swings) — bottom half ---
  // Frame: two vertical posts + top bar (top-down = horizontal rectangle)
  const swx = x - 14, swy = top + 28;
  // Top bar (horizontal beam seen from above)
  g.rect(swx - 2, swy, 32, 4);
  g.fill({ color: 0x885520 });
  // Left post shadow
  g.rect(swx - 2, swy, 4, 14);
  g.fill({ color: 0x664010 });
  // Right post shadow
  g.rect(swx + 26, swy, 4, 14);
  g.fill({ color: 0x664010 });
  // Two swing seats (circles hanging below bar)
  g.circle(swx + 6, swy + 10, 4);
  g.fill({ color: 0xdd3333 });
  g.circle(swx + 20, swy + 10, 4);
  g.fill({ color: 0x3388dd });
  // Rope lines
  g.moveTo(swx + 4, swy + 4);  g.lineTo(swx + 6, swy + 6);
  g.moveTo(swx + 8, swy + 4);  g.lineTo(swx + 6, swy + 6);
  g.moveTo(swx + 18, swy + 4); g.lineTo(swx + 20, swy + 6);
  g.moveTo(swx + 22, swy + 4); g.lineTo(swx + 20, swy + 6);
  g.stroke({ color: 0x554010, width: 1 });

  // --- Karusell (roundabout) — bottom-right ---
  const rx = x + w / 2 - 14, ry = top + h - 12;
  g.circle(rx, ry, 9);
  g.fill({ color: 0x44bb44 });
  g.circle(rx, ry, 9);
  g.stroke({ color: 0x226622, width: 1.5 });
  // spokes
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2;
    g.moveTo(rx, ry);
    g.lineTo(rx + Math.cos(a) * 8, ry + Math.sin(a) * 8);
  }
  g.stroke({ color: 0x226622, width: 1.5 });
  g.circle(rx, ry, 2);
  g.fill({ color: 0x114411 });
}

// ---- Static world geometry ----------------------------------------------
// Parks, houses, buried-treasure marks, bridges and gates are pure functions of
// server state — they only change when a world delta arrives (typically minutes
// apart). Rebuilding them every frame re-tessellated ~700 paths per frame for
// nothing, so they are redrawn only when net.worldVersion changes.
//
// Note these are NOT viewport-culled on purpose: culling would mean redrawing on
// every camera move, which defeats the gate. Once the geometry is built, Pixi
// re-renders it from retained buffers cheaply.
let lastWorldVersion = -1;

function redrawStaticWorld() {
  // Player-built islands — rebuild the shapes map so getAllIslands() (used
  // for collision/landing) and the tap-to-demolish check stay in sync too.
  builtIslandShapes.clear();
  for (const b of net.builtIslands) {
    builtIslandShapes.set(b.id, makeBuiltIsland(b.id, b.x, b.y));
  }
  builtIslandsGfx.clear();
  for (const [id, isl] of builtIslandShapes) {
    drawSingleIsland(builtIslandsGfx, isl, treeRngForBuiltIsland(id));
  }
  minimap.setBuiltIslands([...builtIslandShapes.values()]);

  seasonTintGfx.clear();
  const tint = net.season ? SEASON_ISLAND_TINT[net.season] : null;
  if (tint) {
    for (const isl of getAllIslands()) {
      seasonTintGfx.moveTo(isl.points[0].x, isl.points[0].y);
      for (let i = 1; i < isl.points.length; i++) seasonTintGfx.lineTo(isl.points[i].x, isl.points[i].y);
      seasonTintGfx.closePath();
      seasonTintGfx.fill({ color: tint.color, alpha: tint.alpha });
    }
  }

  parksGfx.clear();
  for (const p of net.parks) {
    drawPark(parksGfx, p.x, p.y);
  }

  housesGfx.clear();
  for (const h of net.houses) {
    drawBuilding(housesGfx, h.x, h.y, h.type ?? 'house');
  }

  // Red X marks for buried treasures
  buriedGfx.clear();
  for (const bt of net.buriedTreasures) {
    const sz = 8;
    buriedGfx.moveTo(bt.x - sz, bt.y - sz);
    buriedGfx.lineTo(bt.x + sz, bt.y + sz);
    buriedGfx.moveTo(bt.x + sz, bt.y - sz);
    buriedGfx.lineTo(bt.x - sz, bt.y + sz);
    buriedGfx.stroke({ color: 0xff2020, width: 3, cap: 'round' });
  }

  // Dropped items — an emoji glyph sitting on the ground where a player
  // dropped it, pickable by anyone.
  droppedItemsContainer.removeChildren();
  for (const d of net.droppedItems) {
    const t = new Text({ text: d.emoji, style: { fontSize: 18 } });
    t.x = d.x - t.width / 2;
    t.y = d.y - t.height / 2;
    droppedItemsContainer.addChild(t);
  }

  bridgesGfx.clear();
  for (const b of net.bridges) {
    drawBridge(bridgesGfx, b.x1, b.y1, b.x2, b.y2);
  }

  // Gates (orienteering-map fence style)
  gatesGfx.clear();
  for (const g of net.gates) {
    drawFence(gatesGfx, g.x1, g.y1, g.x2, g.y2);
  }
}

// ---- Game loop ----
app.ticker.add(() => {
  const dt = app.ticker.deltaTime; // ~1 at 60fps

  if (net.updateAvailable && !updateBannerShown) {
    updateBannerShown = true;
    updateBanner.style.display = 'block';
  }

  if (debugOverlay) {
    const bw = net.bandwidth;
    debugOverlay.update(app.ticker.FPS, bw.sentPerSec, bw.recvPerSec);
  }

  // Use server-authoritative wind angle
  if (net.windAngle != null) windAngle = net.windAngle;
  const windDir = angleToDir(windAngle);

  const screenW = app.screen.width;
  const screenH = app.screen.height;
  const cx = screenW / 2;
  const cy = screenH / 2;

  const bypassGate = isSpecialName(playerName) || myChests >= 10000;

  // Action timer runs regardless of mode (indoor pee/wash/eat or outdoor pee).
  if (actionTimer > 0) {
    actionTimer -= dt;
    if (actionTimer <= 0) {
      peeOverlay.style.display = 'none';
      if (fishing) {
        fishing = false;
        if (!addToInventory(myInventory, { subtype: 'fish', label: 'Fisk', emoji: '🐟' })) {
          startAction('🎒 Väskan är full — fisken simmade iväg!', 90);
        }
      }
      if (cooking) {
        cooking = false;
        const dish = COOKED_DISHES[Math.floor(Math.random() * COOKED_DISHES.length)];
        if (!addToInventory(myInventory, { subtype: 'food', label: dish.label, emoji: dish.emoji })) {
          startAction('🎒 Väskan är full — maten kallnade!', 90);
        }
      }
      if (watchingMovie) {
        watchingMovie = false;
        movieOverlayGfx.clear();
      }
    }
  }
  // While a movie is playing, redraw its current frame every tick.
  if (watchingMovie) {
    drawMovieFrame(movieOverlayGfx, app.screen.width, app.screen.height, currentMovieIdx, MOVIE_DURATION_FRAMES - actionTimer);
  }
  // Bladder fills whenever you're not mid-action.
  if (actionTimer <= 0) {
    bladderFill += dt;
    if (bladderFill >= PEE_FULL_FRAMES) peeingOnSelf = true;
  }
  // Hunger drains the same way, just in the opposite direction (starts full).
  if (actionTimer <= 0) {
    hungerFill -= dt;
    if (hungerFill <= 0) { hungerFill = 0; starving = true; }
  }

  const isOnAnyIsland = (pos: Vec2): Island | null => {
    for (const isl of getAllIslands()) if (isInsideIsland(pos, isl)) return isl;
    return null;
  };
  const isOnBridge = (pos: Vec2): boolean => {
    for (const b of net.bridges) {
      if (pointToSegmentDist(pos, { x: b.x1, y: b.y1 }, { x: b.x2, y: b.y2 }) < 10) return true;
    }
    return false;
  };

  if (gameMode === 'inside') {
    // ---- Inside a house: render a wooden-plank floor and walk around ----
    worldContainer.visible = false;
    boatContainer.visible = false;
    // Hide every outdoor action button — you can't demolish or build anything
    // while indoors.
    topActions.style.display = 'none';
    bottomActions.style.display = 'none';
    buryBtn.style.display = 'none';
    digBtn.style.display = 'none';
    buildMenu.style.display = 'none';
    demolishMenu.style.display = 'none';
    demolishPanel.style.display = 'none';
    demolishHouseBtn.style.display = 'none';
    demolishParkBtn.style.display = 'none';
    destroyGateBtn.style.display = 'none';
    buildGateBtn.style.display = 'none';
    buildBridgeBtn.style.display = 'none';
    buildIslandBtn.style.display = 'none';
    killAnimalBtn.style.display = 'none';
    moveAnimalBtn.style.display = 'none';
    dropAnimalBtn.style.display = 'none';
    ashoreBtn.style.display = 'none';
    fishBtn.style.display = 'none';
    pickupBtn.style.display = 'none';
    buildMenuOpen = false;
    demolishMenuOpen = false;

    // Shop panels — only inside the matching store — and the furnish menu,
    // which now works in every building including the shops themselves (it
    // lives in a different screen corner from the item panels, so no clash).
    ikeaPanel.style.display = enteredBuildingType === 'ikea' ? 'flex' : 'none';
    matbutikPanel.style.display = enteredBuildingType === 'matbutik' ? 'flex' : 'none';
    djuraffarPanel.style.display = enteredBuildingType === 'djuraffar' ? 'flex' : 'none';
    restaurangPanel.style.display = enteredBuildingType === 'restaurang' ? 'flex' : 'none';
    blomsteraffarPanel.style.display = enteredBuildingType === 'blomsteraffar' ? 'flex' : 'none';
    elektronikbutikPanel.style.display = enteredBuildingType === 'elektronikbutik' ? 'flex' : 'none';
    const canFurnish = myFurniture.length > 0;
    furnishToggleBtn.style.display = canFurnish ? 'block' : 'none';
    if (!canFurnish) {
      furnishMenuOpen = false;
      furnishMenu.style.display = 'none';
      placingFurniture = null;
    }

    // Move inside the room (frozen while an action is in progress)
    let isWalking = false;
    if (actionTimer <= 0 && steerJoy.active) {
      const d = steerJoy.direction;
      if (Math.abs(d.x) > 0.05 || Math.abs(d.y) > 0.05) {
        const nx = indoorPos.x + d.x * WALK_SPEED * starvingMul() * dt;
        const ny = indoorPos.y + d.y * WALK_SPEED * starvingMul() * dt;
        if (Math.abs(nx) < ROOM_W / 2 - 15 && Math.abs(ny) < ROOM_H / 2 - 15) {
          indoorPos = { x: nx, y: ny };
          isWalking = true;
        }
      }
    }
    if (isWalking) walkPhase += 0.25 * dt;

    roomGfx.clear();
    const rx0 = screenW / 2 - ROOM_W / 2;
    const ry0 = screenH / 2 - ROOM_H / 2;
    // Paint background green to replace the ocean blue around the room.
    roomGfx.rect(0, 0, screenW, screenH);
    roomGfx.fill({ color: 0x3a8a5a });
    if (enteredBuildingType === 'hotel') {
      drawHotelInterior(roomGfx, rx0, ry0, ROOM_W, ROOM_H, hotelFloor, hotelRoomIdx);
    } else if (enteredBuildingType === 'cinema') {
      drawCinemaInterior(roomGfx, rx0, ry0, ROOM_W, ROOM_H);
    } else {
      drawRoom(roomGfx, rx0, ry0, ROOM_W, ROOM_H, enteredBuildingType, currentNavRoom());
    }

    // Furniture placed in this house — visible to every player who walks in.
    // Filtered to the sub-room you're actually standing in, so a lamp bought
    // for hotel room 4 doesn't show up in every other room on every floor.
    furnitureGfx.clear();
    const insideHouse = currentHouseId ? net.houses.find(h => h.id === currentHouseId) : undefined;
    if (insideHouse?.furniture) {
      const subroom = currentSubroom();
      for (const item of insideHouse.furniture) {
        if ((item.subroom ?? 'main') !== subroom) continue;
        drawFurnitureItem(furnitureGfx, screenW / 2 + item.x, screenH / 2 + item.y, item.type, item.color);
      }
    }
    // Placement preview, following the cursor's last-known room position isn't
    // tracked — just hint that a placement is pending via the furnish button.
    furnishToggleBtn.textContent = placingFurniture
      ? `Placera: ${FURNITURE_LABELS[placingFurniture.type] ?? placingFurniture.type} (tryck i rummet)`
      : '🛋️ Möblera';

    // Other players standing in this same house/shop.
    indoorRemoteGfx.clear();
    if (currentHouseId) {
      for (const p of net.players.values()) {
        if (p.inside && p.insideHouseId === currentHouseId && p.indoorX != null && p.indoorY != null) {
          drawPerson(indoorRemoteGfx, screenW / 2 + p.indoorX, screenH / 2 + p.indoorY, p.shirtColor, p.hairStyle, 0, 1, { hairColor: p.hairColor });
        }
      }
    }

    indoorPersonGfx.clear();
    drawPerson(indoorPersonGfx,
      screenW / 2 + indoorPos.x,
      screenH / 2 + indoorPos.y,
      shirtColor, hairStyle, isWalking ? walkPhase : 0, 1, { hairColor });

    // Pets follow indoors — they wander within the room bounds
    function stepIndoorPet(off: { x: number; y: number }, state: { angle: number; timer: number }) {
      state.timer -= dt;
      if (state.timer <= 0) {
        state.angle = Math.random() * Math.PI * 2;
        state.timer = 80 + Math.random() * 200;
      }
      const SPEED = 0.5;
      const nx = off.x + Math.cos(state.angle) * SPEED * dt;
      const ny = off.y + Math.sin(state.angle) * SPEED * dt;
      const absX = screenW / 2 + indoorPos.x + nx;
      const absY = screenH / 2 + indoorPos.y + ny;
      const inRoom = absX > rx0 + 12 && absX < rx0 + ROOM_W - 12 &&
                     absY > ry0 + 12 && absY < ry0 + ROOM_H - 12;
      const leashOk = nx * nx + ny * ny < 50 * 50;
      if (!inRoom || !leashOk) {
        state.angle += Math.PI + (Math.random() - 0.5) * 1.2;
        state.timer = 40 + Math.random() * 80;
      } else {
        off.x = nx; off.y = ny;
      }
    }

    indoorPetGfx.clear();
    if (playerPet) {
      const s = { angle: petAngle, timer: petWalkTimer };
      stepIndoorPet(petOff, s);
      petAngle = s.angle; petWalkTimer = s.timer;
      drawPet(indoorPetGfx, playerPet,
        screenW / 2 + indoorPos.x + petOff.x,
        screenH / 2 + indoorPos.y + petOff.y,
        playerCatColor);
    }
    indoorPet2Gfx.clear();
    if (playerPet2) {
      const s = { angle: pet2Angle, timer: pet2WalkTimer };
      stepIndoorPet(pet2Off, s);
      pet2Angle = s.angle; pet2WalkTimer = s.timer;
      drawPet(indoorPet2Gfx, playerPet2,
        screenW / 2 + indoorPos.x + pet2Off.x,
        screenH / 2 + indoorPos.y + pet2Off.y,
        playerCatColor);
    }

    // Interaction detection: stairs + toilets + sinks + food counter
    const cxScr = screenW / 2, cyScr = screenH / 2;
    const personAbsX = cxScr + indoorPos.x;
    const personAbsY = cyScr + indoorPos.y;
    nearStairTarget = null;
    nearToilet = false;
    nearSink = false;
    nearFood = false;
    if (enteredBuildingType === 'school') {
      for (const s of STAIRS_BY_ROOM[currentSchoolRoom]) {
        const dx = personAbsX - (cxScr + s.rx);
        const dy = personAbsY - (cyScr + s.ry);
        if (dx * dx + dy * dy < 26 * 26) { nearStairTarget = s.target; break; }
      }
      if (currentSchoolRoom === 'toalett') {
        // Toilets on right
        const padL = 40, padT = 20, padR = 16, padB = 20;
        const cols = 5, rows = 2;
        const stallW = (ROOM_W - padL - padR) / cols - 4;
        const stallH = (ROOM_H - padT - padB) / rows - 10;
        for (let r = 0; r < rows && !nearToilet; r++) {
          for (let c = 0; c < cols; c++) {
            const tcx = rx0 + padL + c * (stallW + 4) + stallW / 2;
            const tcy = ry0 + padT + r * (stallH + 10) + stallH / 2;
            const dx = personAbsX - tcx;
            const dy = personAbsY - tcy;
            if (dx * dx + dy * dy < 18 * 18) { nearToilet = true; break; }
          }
        }
        // Sinks along left wall (3 sinks)
        for (let i = 0; i < 3 && !nearSink; i++) {
          const scx = rx0 + 11;
          const scy = ry0 + 30 + i * 60 + 24;
          const dx = personAbsX - scx;
          const dy = personAbsY - scy;
          if (dx * dx + dy * dy < 22 * 22) nearSink = true;
        }
      }
      if (currentSchoolRoom === 'matsal') {
        // Serving counter runs along the top wall
        if (personAbsY < ry0 + 42 && personAbsX > rx0 + 28 && personAbsX < rx0 + ROOM_W - 28) {
          nearFood = true;
        }
      }
    } else if (enteredBuildingType === 'house') {
      for (const s of STAIRS_BY_ROOM[currentHouseRoom]) {
        const dx = personAbsX - (cxScr + s.rx);
        const dy = personAbsY - (cyScr + s.ry);
        if (dx * dx + dy * dy < 26 * 26) { nearStairTarget = s.target; break; }
      }
      // The toilet only lives in the bathroom now, not every room.
      if (currentHouseRoom === 'badrum') {
        const tx = cxScr + HOUSE_TOILET_ROFFSET.rx;
        const ty = cyScr + HOUSE_TOILET_ROFFSET.ry;
        const dx = personAbsX - tx, dy = personAbsY - ty;
        if (dx * dx + dy * dy < 22 * 22) nearToilet = true;
      }
    } else if (!NO_GENERIC_TOILET_TYPES.has(enteredBuildingType)) {
      // Every other plain building (preschool, studenten) has a fixed toilet.
      const tx = cxScr + HOUSE_TOILET_ROFFSET.rx;
      const ty = cyScr + HOUSE_TOILET_ROFFSET.ry;
      const dx = personAbsX - tx, dy = personAbsY - ty;
      if (dx * dx + dy * dy < 22 * 22) nearToilet = true;
    }

    // ---- Bed (sleep resets hunger + bladder): the hotel's fixed bed, or any
    // placed IKEA bed in the current room/subroom.
    nearBed = false;
    if (enteredBuildingType === 'hotel' && hotelRoomIdx !== null) {
      const bx = cxScr + HOTEL_ROOM_BED_ROFFSET.rx, by = cyScr + HOTEL_ROOM_BED_ROFFSET.ry;
      const dbx = personAbsX - bx, dby = personAbsY - by;
      if (dbx * dbx + dby * dby < 26 * 26) nearBed = true;
    } else if (insideHouse?.furniture) {
      const subroom = currentSubroom();
      for (const item of insideHouse.furniture) {
        if (item.type !== 'bed' || (item.subroom ?? 'main') !== subroom) continue;
        const dbx = personAbsX - (cxScr + item.x), dby = personAbsY - (cyScr + item.y);
        if (dbx * dbx + dby * dby < 26 * 26) { nearBed = true; break; }
      }
    }
    sleepBtn.style.display = (nearBed && actionTimer <= 0) ? 'block' : 'none';

    // ---- Mirror (purely cosmetic hair-brushing action) — any placed IKEA
    // mirror in the current room/subroom. No fixed-furniture equivalent.
    nearMirror = false;
    if (insideHouse?.furniture) {
      const subroom = currentSubroom();
      for (const item of insideHouse.furniture) {
        if (item.type !== 'mirror' || (item.subroom ?? 'main') !== subroom) continue;
        const dmx = personAbsX - (cxScr + item.x), dmy = personAbsY - (cyScr + item.y);
        if (dmx * dmx + dmy * dmy < 26 * 26) { nearMirror = true; break; }
      }
    }
    brushBtn.style.display = (nearMirror && actionTimer <= 0) ? 'block' : 'none';

    // ---- Stove (cook a dish into your inventory): the kitchen's built-in
    // stovetop, or any placed IKEA oven in the current room/subroom.
    nearStove = false;
    if (enteredBuildingType === 'house' && currentHouseRoom === 'kok') {
      const sx = cxScr + KOK_STOVE_ROFFSET.rx, sy = cyScr + KOK_STOVE_ROFFSET.ry;
      const dsx = personAbsX - sx, dsy = personAbsY - sy;
      if (dsx * dsx + dsy * dsy < 30 * 30) nearStove = true;
    }
    if (!nearStove && insideHouse?.furniture) {
      const subroom = currentSubroom();
      for (const item of insideHouse.furniture) {
        if (item.type !== 'oven' || (item.subroom ?? 'main') !== subroom) continue;
        const dox = personAbsX - (cxScr + item.x), doy = personAbsY - (cyScr + item.y);
        if (dox * dox + doy * doy < 26 * 26) { nearStove = true; break; }
      }
    }
    cookBtn.style.display = (nearStove && actionTimer <= 0 && !cooking) ? 'block' : 'none';

    // ---- Fridge (store/take food, shared with every player who visits this
    // house): any placed IKEA fridge in the current room/subroom.
    nearFridge = false;
    nearFridgeId = null;
    if (insideHouse?.furniture) {
      const subroom = currentSubroom();
      for (const item of insideHouse.furniture) {
        if (item.type !== 'fridge' || (item.subroom ?? 'main') !== subroom) continue;
        const dfx2 = personAbsX - (cxScr + item.x), dfy2 = personAbsY - (cyScr + item.y);
        if (dfx2 * dfx2 + dfy2 * dfy2 < 26 * 26) { nearFridge = true; nearFridgeId = item.id; break; }
      }
    }
    if (nearFridge) {
      const count = currentFridge()?.food?.length ?? 0;
      fridgeToggleBtn.style.display = 'block';
      fridgeToggleBtn.textContent = `🧊 Kylskåp (${count}/${FRIDGE_SLOTS})`;
    } else {
      fridgeToggleBtn.style.display = 'none';
      fridgeMenuOpen = false;
      fridgeMenu.style.display = 'none';
    }

    // ---- Table (serve cooked food so other players can eat it): any placed
    // IKEA table in the current room/subroom.
    nearTable = false;
    nearTableId = null;
    if (insideHouse?.furniture) {
      const subroom = currentSubroom();
      for (const item of insideHouse.furniture) {
        if (item.type !== 'table' || (item.subroom ?? 'main') !== subroom) continue;
        const dtx2 = personAbsX - (cxScr + item.x), dty2 = personAbsY - (cyScr + item.y);
        if (dtx2 * dtx2 + dty2 * dty2 < 26 * 26) { nearTable = true; nearTableId = item.id; break; }
      }
    }
    if (nearTable) {
      const count = currentTable()?.food?.length ?? 0;
      tableToggleBtn.style.display = 'block';
      tableToggleBtn.textContent = `🍽️ Bord (${count}/${TABLE_SLOTS})`;
    } else {
      tableToggleBtn.style.display = 'none';
      tableMenuOpen = false;
      tableMenu.style.display = 'none';
    }

    // ---- Hotel: elevator, room doors, and each room's own toilet/fridge ----
    nearElevator = false;
    nearHotelDoor = null;
    nearRoomExit = false;
    if (enteredBuildingType === 'hotel') {
      if (hotelRoomIdx !== null) {
        const bx = cxScr + HOTEL_ROOM_TOILET_ROFFSET.rx, by = cyScr + HOTEL_ROOM_TOILET_ROFFSET.ry;
        const dtx = personAbsX - bx, dty = personAbsY - by;
        if (dtx * dtx + dty * dty < 22 * 22) nearToilet = true;
        const fx = cxScr + HOTEL_ROOM_FRIDGE_ROFFSET.rx, fy = cyScr + HOTEL_ROOM_FRIDGE_ROFFSET.ry;
        const dfx = personAbsX - fx, dfy = personAbsY - fy;
        if (dfx * dfx + dfy * dfy < 22 * 22) nearFood = true;
        const ex = cxScr + HOTEL_ROOM_EXIT_ROFFSET.rx, ey = cyScr + HOTEL_ROOM_EXIT_ROFFSET.ry;
        const dex = personAbsX - ex, dey = personAbsY - ey;
        if (dex * dex + dey * dey < 26 * 26) nearRoomExit = true;
      } else {
        const elx = cxScr + HOTEL_ELEVATOR_ROFFSET.rx, ely = cyScr + HOTEL_ELEVATOR_ROFFSET.ry;
        const delx = personAbsX - elx, dely = personAbsY - ely;
        if (delx * delx + dely * dely < 30 * 30) nearElevator = true;
        if (hotelFloor > 0) {
          for (let i = 0; i < HOTEL_ROOMS_PER_FLOOR; i++) {
            const { rx, ry } = hotelDoorOffset(i);
            const ddx = personAbsX - (cxScr + rx), ddy = personAbsY - (cyScr + ry);
            if (ddx * ddx + ddy * ddy < 24 * 24) { nearHotelDoor = i; break; }
          }
        }
      }
    }

    // ---- Cinema: any seat counts as "near the screen" ----
    nearScreen = false;
    if (enteredBuildingType === 'cinema') {
      const scx = cxScr + CINEMA_SCREEN_ROFFSET.rx, scy = cyScr + CINEMA_SCREEN_ROFFSET.ry;
      const dscx = personAbsX - scx, dscy = personAbsY - scy;
      if (dscx * dscx + dscy * dscy < (ROOM_H * 0.7) ** 2) nearScreen = true;
    }
    cinemaActionBtn.style.display = (nearScreen && actionTimer <= 0 && !watchingMovie) ? 'block' : 'none';

    if (nearStairTarget && actionTimer <= 0) {
      const st = STAIRS_BY_ROOM[currentNavRoom()].find(s => s.target === nearStairTarget)!;
      stairBtn.style.display = 'block';
      stairBtn.textContent = `Gå till ${st.label}`;
    } else {
      stairBtn.style.display = 'none';
    }
    peeBtn.style.display  = (nearToilet && actionTimer <= 0) ? 'block' : 'none';
    washBtn.style.display = (nearSink    && actionTimer <= 0) ? 'block' : 'none';
    eatBtn.style.display  = (nearFood    && actionTimer <= 0) ? 'block' : 'none';

    if (actionTimer <= 0 && nearElevator) {
      hotelActionBtn.style.display = 'block';
      hotelActionBtn.textContent = '🛗 Hiss';
    } else if (actionTimer <= 0 && nearHotelDoor !== null) {
      hotelActionBtn.style.display = 'block';
      hotelActionBtn.textContent = `Gå in i rum ${nearHotelDoor + 1}`;
    } else if (actionTimer <= 0 && nearRoomExit) {
      hotelActionBtn.style.display = 'block';
      hotelActionBtn.textContent = 'Gå ut i korridoren';
    } else {
      hotelActionBtn.style.display = 'none';
      if (hotelFloorMenuOpen) { hotelFloorMenuOpen = false; hotelFloorMenu.style.display = 'none'; }
    }

    // Hand-raise button appears in the classroom when hand isn't already up
    if (enteredBuildingType === 'school' && currentSchoolRoom === 'klassrum' &&
        actionTimer <= 0 && handRaisedTimer <= 0) {
      raiseHandBtn.style.display = 'block';
    } else {
      raiseHandBtn.style.display = 'none';
    }
    if (handRaisedTimer > 0) {
      handRaisedTimer -= dt;
      handEmoji.x = personAbsX - handEmoji.width / 2;
      handEmoji.y = personAbsY - 36;
      handEmoji.visible = true;
    } else {
      handEmoji.visible = false;
    }

    // Draw NPCs for the current room (scaled smaller than the player)
    indoorNpcGfx.clear();
    const teacherPos: { x: number; y: number } | null = (() => {
      if (enteredBuildingType !== 'school') return null;
      for (const npc of SCHOOL_NPCS) {
        if (npc.room === currentSchoolRoom && npc.isTeacher) {
          return { x: cxScr + npc.rx, y: cyScr + npc.ry };
        }
      }
      return null;
    })();
    if (enteredBuildingType === 'school') {
      const NPC_SPEED = 0.3;
      const MARGIN = 22;
      for (const npc of SCHOOL_NPCS) {
        if (npc.room !== currentSchoolRoom) continue;
        const moves = !npc.isTeacher && !npc.sitStill;
        if (moves) {
          npc.timer -= dt;
          if (npc.timer <= 0) {
            npc.angle = Math.random() * Math.PI * 2;
            npc.timer = 60 + Math.random() * 180;
          }
          const nx = npc.rx + Math.cos(npc.angle) * NPC_SPEED * dt;
          const ny = npc.ry + Math.sin(npc.angle) * NPC_SPEED * dt;
          if (Math.abs(nx) < ROOM_W / 2 - MARGIN && Math.abs(ny) < ROOM_H / 2 - MARGIN) {
            npc.rx = nx; npc.ry = ny;
            npc.walkPhase += 0.22 * dt;
          } else {
            npc.angle += Math.PI + (Math.random() - 0.5) * 1.2;
            npc.timer = 40 + Math.random() * 60;
          }
        }
        drawPerson(indoorNpcGfx, cxScr + npc.rx, cyScr + npc.ry,
          npc.shirtColor, npc.hairStyle,
          moves ? npc.walkPhase : 0,
          npc.scale,
          (npc.hairColor !== undefined || npc.pantsColor !== undefined)
            ? { hairColor: npc.hairColor, pantsColor: npc.pantsColor }
            : undefined);
      }
    }
    // Talker speech bubbles — each classroom talker gets their own bubble that
    // cycles phrases over time (staggered so they don't all say the same thing).
    if (enteredBuildingType === 'school' && currentSchoolRoom === 'klassrum') {
      let talkerIdx = 0;
      for (const npc of SCHOOL_NPCS) {
        if (npc.room !== currentSchoolRoom || !npc.isTalker) continue;
        if (!npc.bubbleText) {
          npc.bubbleBg = new Graphics();
          app.stage.addChild(npc.bubbleBg);
          npc.bubbleText = new Text({ text: '', style: { fontSize: 10, fontFamily: 'system-ui, sans-serif', fill: 0x202020 } });
          app.stage.addChild(npc.bubbleText);
        }
        const phrase = STUDENT_CHATTER[(Math.floor(Date.now() / 3000) + talkerIdx * 2) % STUDENT_CHATTER.length];
        npc.bubbleText.text = phrase;
        const tx = cxScr + npc.rx;
        const ty = cyScr + npc.ry;
        npc.bubbleText.x = tx - npc.bubbleText.width / 2;
        npc.bubbleText.y = ty - 26;
        npc.bubbleText.visible = true;
        npc.bubbleBg!.clear();
        npc.bubbleBg!.roundRect(npc.bubbleText.x - 5, npc.bubbleText.y - 2, npc.bubbleText.width + 10, npc.bubbleText.height + 4, 5);
        npc.bubbleBg!.fill({ color: 0xffffff, alpha: 0.92 });
        npc.bubbleBg!.stroke({ color: 0x404040, width: 0.8 });
        npc.bubbleBg!.visible = true;
        talkerIdx++;
      }
    } else {
      // Hide any talker bubbles created earlier
      for (const npc of SCHOOL_NPCS) {
        if (npc.bubbleText) npc.bubbleText.visible = false;
        if (npc.bubbleBg) npc.bubbleBg.visible = false;
      }
    }

    // Teacher speech bubble
    if (teacherPos) {
      const phrase = TEACHER_PHRASES[Math.floor(Date.now() / 4000) % TEACHER_PHRASES.length];
      teacherBubble.text = phrase;
      teacherBubble.x = teacherPos.x - teacherBubble.width / 2;
      teacherBubble.y = teacherPos.y - 28;
      teacherBubble.visible = true;
      teacherBubbleBg.clear();
      teacherBubbleBg.roundRect(teacherBubble.x - 6, teacherBubble.y - 3, teacherBubble.width + 12, teacherBubble.height + 6, 6);
      teacherBubbleBg.fill({ color: 0xffffff, alpha: 0.95 });
      teacherBubbleBg.stroke({ color: 0x333333, width: 1 });
      // Little tail pointing down to teacher
      teacherBubbleBg.moveTo(teacherPos.x - 3, teacherBubble.y + teacherBubble.height + 3);
      teacherBubbleBg.lineTo(teacherPos.x, teacherBubble.y + teacherBubble.height + 9);
      teacherBubbleBg.lineTo(teacherPos.x + 3, teacherBubble.y + teacherBubble.height + 3);
      teacherBubbleBg.closePath();
      teacherBubbleBg.fill({ color: 0xffffff, alpha: 0.95 });
      teacherBubbleBg.stroke({ color: 0x333333, width: 1 });
      teacherBubbleBg.visible = true;
    } else {
      teacherBubble.visible = false;
      teacherBubbleBg.visible = false;
    }

    // "Gå ut" behaviour:
    //  - School: only usable from the Hall, near the left-wall exit door.
    //  - Other buildings (house/preschool/studenten): single room, always usable.
    let canExit = false;
    if (enteredBuildingType === 'school') {
      if (currentSchoolRoom === 'hall') {
        const doorX = cxScr - ROOM_W / 2;
        const doorY = cyScr;
        const dx = personAbsX - doorX;
        const dy = personAbsY - doorY;
        if (dx * dx + dy * dy < 28 * 28) canExit = true;
      }
    } else {
      canExit = true;
    }
    if (canExit) {
      enterHouseBtn.style.display = 'block';
      enterHouseBtn.textContent = 'Gå ut';
    } else {
      enterHouseBtn.style.display = 'none';
    }
  } else if (gameMode === 'ashore') {
    worldContainer.visible = true;
    boatContainer.visible = true;
    roomGfx.clear();
    indoorPersonGfx.clear();
    indoorPetGfx.clear();
    indoorPet2Gfx.clear();
    indoorNpcGfx.clear();
    teacherBubble.visible = false;
    teacherBubbleBg.visible = false;
    for (const npc of SCHOOL_NPCS) {
      if (npc.bubbleText) npc.bubbleText.visible = false;
      if (npc.bubbleBg) npc.bubbleBg.visible = false;
    }
    stairBtn.style.display = 'none';
    washBtn.style.display = 'none';
    eatBtn.style.display = 'none';
    raiseHandBtn.style.display = 'none';
    if (actionTimer <= 0) peeOverlay.style.display = 'none';
    handEmoji.visible = false;
    handRaisedTimer = 0;

    // Outdoor toilet proximity — pee button when near one
    nearOutdoorToilet = false;
    for (const h of net.houses) {
      if (h.type !== 'toilet') continue;
      const dxh = personPos.x - h.x;
      const dyh = personPos.y - h.y;
      if (dxh * dxh + dyh * dyh < 30 * 30) { nearOutdoorToilet = true; break; }
    }
    peeBtn.style.display = (nearOutdoorToilet && actionTimer <= 0) ? 'block' : 'none';

    // Fishing spot — near the water's edge (outer band of the current
    // island), not deep inland. Reuses currentIsland, no new geometry.
    nearFishingSpot = currentIsland !== null && (() => {
      const dx = personPos.x - currentIsland.cx;
      const dy = personPos.y - currentIsland.cy;
      return Math.sqrt(dx * dx + dy * dy) > currentIsland.radius * 0.62;
    })();
    fishBtn.style.display = (nearFishingSpot && actionTimer <= 0 && !fishing) ? 'block' : 'none';

    // ---- Dropped items on the ground: pick the nearest one in range ----
    nearDroppedItem = null;
    let nearDroppedDist2 = 26 * 26;
    for (const d of net.droppedItems) {
      const dx = personPos.x - d.x, dy = personPos.y - d.y;
      const dist2 = dx * dx + dy * dy;
      if (dist2 < nearDroppedDist2) { nearDroppedDist2 = dist2; nearDroppedItem = d; }
    }
    if (nearDroppedItem && actionTimer <= 0 && myInventory.length < INVENTORY_SLOTS) {
      pickupBtn.style.display = 'block';
      pickupBtn.textContent = `📍 Plocka upp ${nearDroppedItem.emoji} ${nearDroppedItem.label}`;
    } else {
      pickupBtn.style.display = 'none';
    }

    // ---- Ashore: joystick moves person on island ----
    let isWalking = false;
    if (actionTimer <= 0 && steerJoy.active) {
      const d = steerJoy.direction;
      if (Math.abs(d.x) > 0.05 || Math.abs(d.y) > 0.05) {
        const candidate: Vec2 = {
          x: personPos.x + d.x * WALK_SPEED * starvingMul() * dt,
          y: personPos.y + d.y * WALK_SPEED * starvingMul() * dt,
        };
        let gateBlocked = false;
        if (!bypassGate) {
          for (const g of net.gates) {
            if (segmentsIntersect(personPos, candidate, { x: g.x1, y: g.y1 }, { x: g.x2, y: g.y2 })) {
              gateBlocked = true;
              break;
            }
          }
        }
        // Walkable if inside any island (update currentIsland when crossing a bridge) or on a bridge.
        if (!gateBlocked) {
          const isl = isOnAnyIsland(candidate);
          const onBridge = isOnBridge(candidate);
          if (isl) {
            personPos = candidate;
            currentIsland = isl;
            isWalking = true;
          } else if (onBridge) {
            personPos = candidate;
            isWalking = true;
          }
        }
      }
    }

    if (isWalking) {
      walkPhase += 0.25 * dt;
    }

    // Draw person in world space
    ashoreGfx.clear();
    drawPerson(ashoreGfx, personPos.x, personPos.y, shirtColor, hairStyle, isWalking ? walkPhase : 0, 1, { hairColor });

    // Camera follows person
    worldContainer.x = cx - personPos.x;
    worldContainer.y = cy - personPos.y;

    // Show boat at its parked world position relative to camera
    boatContainer.x = cx + (boatPos.x - personPos.x);
    boatContainer.y = cy + (boatPos.y - personPos.y);

    // Show "board boat" only when near the boat
    const dxBoat = personPos.x - boatPos.x;
    const dyBoat = personPos.y - boatPos.y;
    if (dxBoat * dxBoat + dyBoat * dyBoat < 60 * 60) {
      ashoreBtn.style.display = 'block';
      ashoreBtn.textContent = 'Gå ombord';
    } else {
      ashoreBtn.style.display = 'none';
    }

    // Keep sail visual updating
    const boatDir = angleToDir(boatAngle);
    const sailDir = angleToDir(sailAngle);
    boat.update(boatDir, sailDir, 0);

  } else {
    // ---- Sailing mode ----

    // Read joystick input → set boat target
    const t = 1 - Math.pow(1 - TURN_LERP, dt); // frame-rate independent lerp

    if (steerJoy.active) {
      const d = steerJoy.direction;
      if (Math.abs(d.x) > 0.05 || Math.abs(d.y) > 0.05) {
        targetBoatAngle = dirToAngle(normalize(d));
      }
    }
    boatAngle = lerpAngle(boatAngle, targetBoatAngle, t);

    // Derive boat direction, then auto-trim sail toward optimal angle
    const boatDir = angleToDir(boatAngle);
    const targetSailAngle = optimalSailAngle(windDir, boatDir);
    sailAngle = lerpAngle(sailAngle, targetSailAngle, t);
    const sailDir = angleToDir(sailAngle);

    // Physics
    const { thrust } = computeSailing({ windDir, boatDir, sailDir });
    speed = updateSpeed(speed, thrust) * starvingMul();

    // Move
    const candidatePos = moveBoat(boatPos, boatDir, speed * dt);

    // World wrapping
    const wrapped: Vec2 = {
      x: ((candidatePos.x % WORLD_W) + WORLD_W) % WORLD_W,
      y: ((candidatePos.y % WORLD_H) + WORLD_H) % WORLD_H,
    };

    // Island collision — don't move if colliding, kill speed
    const push = checkCollision(wrapped, getAllIslands());
    let gateBlocked = false;
    if (!push && !bypassGate) {
      for (const g of net.gates) {
        if (segmentsIntersect(boatPos, wrapped, { x: g.x1, y: g.y1 }, { x: g.x2, y: g.y2 })) {
          gateBlocked = true;
          break;
        }
      }
    }
    if (push || gateBlocked) {
      // Stay at previous position, zero out speed
      speed = 0;
    } else {
      boatPos = wrapped;
    }

    // Treasure collection
    for (let i = 0; i < treasures.length; i++) {
      if (net.collectedTreasures.has(i)) continue;
      const dx = boatPos.x - treasures[i].x;
      const dy = boatPos.y - treasures[i].y;
      if (dx * dx + dy * dy < 30 * 30) {
        net.collectTreasure(i);
        myChests++;
      }
    }

    // Show/hide ashore button based on proximity to island
    const nearIsland = findNearestIsland(boatPos, getAllIslands(), 200);
    if (nearIsland && speed < 0.5 && !isLandingBlocked(boatPos, nearIsland)) {
      ashoreBtn.style.display = 'block';
      ashoreBtn.textContent = 'Gå i land';
    } else {
      ashoreBtn.style.display = 'none';
    }

    // Camera: translate world so boat world-pos maps to screen center
    worldContainer.x = cx - boatPos.x;
    worldContainer.y = cy - boatPos.y;

    // Boat always at screen center
    boatContainer.x = cx;
    boatContainer.y = cy;

    // Update visuals
    boat.update(boatDir, sailDir, speed);
  }

  if (gameMode !== 'inside') {
    ikeaPanel.style.display = 'none';
    matbutikPanel.style.display = 'none';
    djuraffarPanel.style.display = 'none';
    restaurangPanel.style.display = 'none';
    blomsteraffarPanel.style.display = 'none';
    elektronikbutikPanel.style.display = 'none';
    furnishToggleBtn.style.display = 'none';
    furnishMenuOpen = false;
    furnishMenu.style.display = 'none';
    indoorRemoteGfx.clear();
    furnitureGfx.clear();
    hotelActionBtn.style.display = 'none';
    hotelFloorMenuOpen = false;
    hotelFloorMenu.style.display = 'none';
    sleepBtn.style.display = 'none';
    brushBtn.style.display = 'none';
    cookBtn.style.display = 'none';
    fridgeToggleBtn.style.display = 'none';
    fridgeMenuOpen = false;
    fridgeMenu.style.display = 'none';
    tableToggleBtn.style.display = 'none';
    tableMenuOpen = false;
    tableMenu.style.display = 'none';
    cinemaActionBtn.style.display = 'none';
  }

  // Multiplayer: send own state & render others
  net.sendPosition(
    boatPos.x, boatPos.y, boatAngle, sailAngle, speed,
    gameMode === 'ashore', personPos.x, personPos.y, boatAnimalIdx,
    playerPet ? petOff.x : undefined,
    playerPet ? petOff.y : undefined,
    playerPet2 ? pet2Off.x : undefined,
    playerPet2 ? pet2Off.y : undefined,
    gameMode === 'inside',
    gameMode === 'inside' ? currentHouseId : null,
    indoorPos.x,
    indoorPos.y,
  );
  remoteBoats.update(net.players, boatPos, screenW, screenH, dt);

  // Sync treasure visibility from server
  for (let i = 0; i < treasureGfxList.length; i++) {
    treasureGfxList[i].visible = !net.collectedTreasures.has(i);
  }

  // Static world geometry — rebuilt only when the server world actually changes.
  if (net.worldVersion !== lastWorldVersion) {
    lastWorldVersion = net.worldVersion;
    redrawStaticWorld();
  }

  // Apply server-side animal moves (snap once per unique position)
  for (const [idx, pos] of net.movedAnimals) {
    const sig = `${pos.x.toFixed(1)},${pos.y.toFixed(1)}`;
    if (lastMoveSig.get(idx) !== sig && idx < animals.length) {
      animals[idx].x = pos.x;
      animals[idx].y = pos.y;
      animals[idx].islandIdx = findIslandForPos(pos, getAllIslands());
      lastMoveSig.set(idx, sig);
    }
  }
  // Held animal follows player
  if (heldAnimalIdx !== null && heldAnimalIdx < animals.length) {
    animals[heldAnimalIdx].x = personPos.x;
    animals[heldAnimalIdx].y = personPos.y;
  }
  // Update and draw animals. Animals wander every frame so their geometry must
  // be rebuilt, but only the ones on screen are worth drawing — worldContainer
  // is translated and never scaled, so screen_x = world_x + worldContainer.x.
  updateAnimals(animals, getAllIslands(), dt, net.killedAnimals, heldAnimalIdx);
  animalsGfx.clear();
  const cullMargin = 48; // a little slack so nothing pops in at the edges
  drawAnimals(animalsGfx, animals, net.killedAnimals, {
    x0: -worldContainer.x - cullMargin,
    y0: -worldContainer.y - cullMargin,
    x1: -worldContainer.x + screenW + cullMargin,
    y1: -worldContainer.y + screenH + cullMargin,
  });

  // Villagers wander their home island, same culling as animals.
  updateVillagers(villagers, getAllIslands(), dt);
  villagersGfx.clear();
  drawVillagers(villagersGfx, villagers, {
    x0: -worldContainer.x - cullMargin,
    y0: -worldContainer.y - cullMargin,
    x1: -worldContainer.x + screenW + cullMargin,
    y1: -worldContainer.y + screenH + cullMargin,
  });

  // Kept in ticker scope: also used for the dig-proximity check below.
  const buried = net.buriedTreasures;

  // Build preview: show marker on pending first point
  gatePreviewGfx.clear();
  if (pendingPointA) {
    gatePreviewGfx.circle(pendingPointA.x, pendingPointA.y, 5);
    gatePreviewGfx.fill({ color: 0x000000, alpha: 0.7 });
  }

  // Destroy-gate is now tap-on-gate with confirm dialog — no persistent button.

  // Show/hide buttons
  if (gameMode === 'ashore') {
    // Top: gräv-knappar
    topActions.style.display = 'flex';
    buryBtn.style.display = myChests > 0 ? 'inline-block' : 'none';
    buryBtn.textContent = `Gräv ner kista (${myChests})`;
    let nearBuried = false;
    for (let i = 0; i < buried.length; i++) {
      const dx = personPos.x - buried[i].x;
      const dy = personPos.y - buried[i].y;
      if (dx * dx + dy * dy < 30 * 30) { nearBuried = true; break; }
    }
    digBtn.style.display = nearBuried ? 'inline-block' : 'none';

    // Bottom: bygg / riv
    bottomActions.style.display = 'flex';
    buildHouseBtn.style.opacity = myChests >= 50 ? '1' : '0.4';
    buildParkBtn.style.opacity  = myChests >= 30 ? '1' : '0.4';

    let nearHouse = false;
    for (const h of net.houses) {
      const dx = personPos.x - h.x;
      const dy = personPos.y - h.y;
      if (dx * dx + dy * dy < 40 * 40) { nearHouse = true; break; }
    }
    let nearPark = false;
    for (const p of net.parks) {
      const dx = personPos.x - p.x;
      const dy = personPos.y - p.y;
      if (dx * dx + dy * dy < 70 * 70) { nearPark = true; break; }
    }
    demolishPanel.style.display = (nearHouse || nearPark) ? 'flex' : 'none';
    demolishHouseBtn.style.display = nearHouse ? 'block' : 'none';
    demolishParkBtn.style.display  = nearPark  ? 'block' : 'none';
    enterHouseBtn.style.display = nearHouse ? 'block' : 'none';
    enterHouseBtn.textContent = 'Gå in i huset';
    if (!nearHouse && !nearPark && demolishMenuOpen) {
      demolishMenuOpen = false;
      demolishMenu.style.display = 'none';
    }

    // Djur-knappar
    if (heldAnimalIdx !== null) {
      killAnimalBtn.style.display  = 'none';
      moveAnimalBtn.style.display  = 'none';
      dropAnimalBtn.style.display  = 'inline-block';
    } else {
      const nearAnimal = findAnimalNear(animals, net.killedAnimals, personPos.x, personPos.y, 22) !== -1;
      killAnimalBtn.style.display = nearAnimal ? 'inline-block' : 'none';
      moveAnimalBtn.style.display = nearAnimal ? 'inline-block' : 'none';
      dropAnimalBtn.style.display = 'none';
    }
  } else {
    topActions.style.display    = 'none';
    bottomActions.style.display = 'none';
    buildMenuOpen    = false;
    demolishMenuOpen = false;
    buildMenu.style.display    = 'none';
    demolishMenu.style.display = 'none';
    killAnimalBtn.style.display = 'none';
    moveAnimalBtn.style.display = 'none';
    dropAnimalBtn.style.display = 'none';
    fishBtn.style.display = 'none';
    pickupBtn.style.display = 'none';
    if (gameMode !== 'inside') enterHouseBtn.style.display = 'none';
    if (heldAnimalIdx !== null) {
      net.moveAnimal(heldAnimalIdx, personPos.x, personPos.y);
      heldAnimalIdx = null;
    }
  }

  inventoryToggleBtn.textContent = `🎒 Väska (${myInventory.length}/${INVENTORY_SLOTS})`;

  hud.update(
    screenW,
    screenH,
    myChests,
    net.season,
  );
  minimap.update(
    screenW,
    screenH,
    gameMode === 'ashore' ? personPos : boatPos,
    hullColor,
    net.players,
  );
  drawJoysticks(joystickGraphics, steerJoy);

  // Bladder meter (always visible)
  bladderMeterGfx.clear();
  const fillPct = Math.min(1, bladderFill / PEE_FULL_FRAMES);
  const mx = METER_X + METER_ICON_W, my = METER_Y;
  bladderMeterGfx.rect(mx, my, 120, 14);
  bladderMeterGfx.fill({ color: 0x000000, alpha: 0.45 });
  bladderMeterGfx.rect(mx + 2, my + 2, 116 * fillPct, 10);
  bladderMeterGfx.fill({ color: peeingOnSelf ? 0x40c040 : fillPct < 0.5 ? 0x40b0e0 : fillPct < 0.8 ? 0xe0c040 : 0xe04040 });
  bladderMeterGfx.rect(mx, my, 120, 14);
  bladderMeterGfx.stroke({ color: 0xffffff, width: 1, alpha: 0.7 });

  // Hunger meter, stacked right below the bladder meter. Counts down (empty
  // = starving) instead of up, so the color ramp is inverted.
  hungerMeterGfx.clear();
  const hungerPct = Math.max(0, hungerFill / HUNGER_EMPTY_FRAMES);
  const hmy = my + 18;
  hungerMeterGfx.rect(mx, hmy, 120, 14);
  hungerMeterGfx.fill({ color: 0x000000, alpha: 0.45 });
  hungerMeterGfx.rect(mx + 2, hmy + 2, 116 * hungerPct, 10);
  hungerMeterGfx.fill({ color: starving ? 0xe04040 : hungerPct > 0.5 ? 0x40c040 : hungerPct > 0.2 ? 0xe0c040 : 0xe08030 });
  hungerMeterGfx.rect(mx, hmy, 120, 14);
  hungerMeterGfx.stroke({ color: 0xffffff, width: 1, alpha: 0.7 });

  // Green smoke above head when peeing on yourself
  peeSmokeGfx.clear();
  if (peeingOnSelf) {
    const selfSX = (gameMode === 'inside')
      ? screenW / 2 + indoorPos.x
      : screenW / 2;
    const selfSY = (gameMode === 'inside')
      ? screenH / 2 + indoorPos.y
      : screenH / 2;
    const t = Date.now() / 180;
    for (let i = 0; i < 4; i++) {
      const phase = t + i * 1.4;
      const pct = (Math.sin(phase) + 1) / 2;
      const yOff = -22 - 16 * pct;
      const r = 4 + 4 * pct;
      peeSmokeGfx.circle(selfSX + Math.sin(phase * 1.3 + i) * 5, selfSY + yOff, r);
      peeSmokeGfx.fill({ color: 0x60b060, alpha: 0.6 * (1 - pct * 0.3) });
    }
  }

  // Seasonal weather (snow / leaves / petals) — screen-space, hidden indoors.
  weatherGfx.clear();
  if (net.season && gameMode !== 'inside') {
    ensureWeatherParticles(net.season, screenW, screenH);
    for (const p of weatherParticles) {
      p.phase += 0.025 * dt;
      p.y += p.vy * dt;
      if (p.y > screenH + 10) respawnWeatherParticle(p, net.season, screenW, screenH, false);
      const px = p.x + Math.sin(p.phase) * p.drift * 6;
      if (p.leaf) {
        const a = p.phase * 2;
        const dx = Math.cos(a) * p.size, dy = Math.sin(a) * p.size;
        weatherGfx.moveTo(px - dx, p.y - dy);
        weatherGfx.lineTo(px + dx, p.y + dy);
        weatherGfx.stroke({ color: p.color, width: p.size * 0.8, alpha: 0.8, cap: 'round' });
      } else {
        weatherGfx.circle(px, p.y, p.size);
        weatherGfx.fill({ color: p.color, alpha: 0.85 });
      }
    }
  } else {
    weatherParticleSeason = undefined;
  }

  // Draw animal on local boat (screen-space, always at screen center)
  localBoatAnimalGfx.clear();
  if (gameMode === 'sailing' && boatAnimalIdx !== null && boatAnimalIdx < animals.length) {
    drawAnimalOnBoat(localBoatAnimalGfx, animals[boatAnimalIdx].type, cx + 8, cy + 4);
  }

  // Local pets walk like chickens: pick a random heading, keep it for a
  // while, reverse when they hit the leash or non-walkable ground.
  const anchor = gameMode === 'ashore' ? personPos : boatPos;
  const ownerAshore = gameMode === 'ashore';

  function stepLocalPet(off: { x: number; y: number }, state: { angle: number; timer: number }): void {
    state.timer -= dt;
    if (state.timer <= 0) {
      state.angle = Math.random() * Math.PI * 2;
      state.timer = 80 + Math.random() * 200;
    }
    const SPEED = 0.5;
    const nx = off.x + Math.cos(state.angle) * SPEED * dt;
    const ny = off.y + Math.sin(state.angle) * SPEED * dt;
    let blocked = nx * nx + ny * ny > 50 * 50;
    if (!blocked && ownerAshore && !isWalkable({ x: anchor.x + nx, y: anchor.y + ny })) blocked = true;
    if (blocked) {
      state.angle += Math.PI + (Math.random() - 0.5) * 1.2;
      state.timer = 40 + Math.random() * 80;
    } else {
      off.x = nx;
      off.y = ny;
    }
  }

  // Pets no longer play with each other — they just wander independently.

  localPetGfx.clear();
  if (playerPet) {
    const s = { angle: petAngle, timer: petWalkTimer };
    stepLocalPet(petOff, s);
    petAngle = s.angle; petWalkTimer = s.timer;
    drawPet(localPetGfx, playerPet, anchor.x + petOff.x, anchor.y + petOff.y, playerCatColor);
  }

  localPet2Gfx.clear();
  if (playerPet2) {
    const s = { angle: pet2Angle, timer: pet2WalkTimer };
    stepLocalPet(pet2Off, s);
    pet2Angle = s.angle; pet2WalkTimer = s.timer;
    drawPet(localPet2Gfx, playerPet2, anchor.x + pet2Off.x, anchor.y + pet2Off.y, playerCatColor);
  }
});

})();
